import { sharedConfig, createRenderEffect, untrack, mergeProps, splitProps, createSignal, createEffect, createComponent, batch, getListener, onCleanup, For, getOwner, createMemo, Show } from 'solid-js';
import DOMPurify from 'dompurify';

const booleans = ["allowfullscreen", "async", "alpha",
"autofocus",
"autoplay", "checked", "controls", "default", "disabled", "formnovalidate", "hidden",
"indeterminate", "inert",
"ismap", "loop", "multiple", "muted", "nomodule", "novalidate", "open", "playsinline", "readonly", "required", "reversed", "seamless",
"selected", "adauctionheaders",
"browsingtopics",
"credentialless",
"defaultchecked", "defaultmuted", "defaultselected", "defer", "disablepictureinpicture", "disableremoteplayback", "preservespitch",
"shadowrootclonable", "shadowrootcustomelementregistry",
"shadowrootdelegatesfocus", "shadowrootserializable",
"sharedstoragewritable"
];
const Properties = /*#__PURE__*/new Set([
"className", "value",
"readOnly", "noValidate", "formNoValidate", "isMap", "noModule", "playsInline", "adAuctionHeaders",
"allowFullscreen", "browsingTopics",
"defaultChecked", "defaultMuted", "defaultSelected", "disablePictureInPicture", "disableRemotePlayback", "preservesPitch", "shadowRootClonable", "shadowRootCustomElementRegistry",
"shadowRootDelegatesFocus", "shadowRootSerializable",
"sharedStorageWritable",
...booleans]);
const ChildProperties = /*#__PURE__*/new Set(["innerHTML", "textContent", "innerText", "children"]);
const Aliases = /*#__PURE__*/Object.assign(Object.create(null), {
  className: "class",
  htmlFor: "for"
});
const PropAliases = /*#__PURE__*/Object.assign(Object.create(null), {
  class: "className",
  novalidate: {
    $: "noValidate",
    FORM: 1
  },
  formnovalidate: {
    $: "formNoValidate",
    BUTTON: 1,
    INPUT: 1
  },
  ismap: {
    $: "isMap",
    IMG: 1
  },
  nomodule: {
    $: "noModule",
    SCRIPT: 1
  },
  playsinline: {
    $: "playsInline",
    VIDEO: 1
  },
  readonly: {
    $: "readOnly",
    INPUT: 1,
    TEXTAREA: 1
  },
  adauctionheaders: {
    $: "adAuctionHeaders",
    IFRAME: 1
  },
  allowfullscreen: {
    $: "allowFullscreen",
    IFRAME: 1
  },
  browsingtopics: {
    $: "browsingTopics",
    IMG: 1
  },
  defaultchecked: {
    $: "defaultChecked",
    INPUT: 1
  },
  defaultmuted: {
    $: "defaultMuted",
    AUDIO: 1,
    VIDEO: 1
  },
  defaultselected: {
    $: "defaultSelected",
    OPTION: 1
  },
  disablepictureinpicture: {
    $: "disablePictureInPicture",
    VIDEO: 1
  },
  disableremoteplayback: {
    $: "disableRemotePlayback",
    AUDIO: 1,
    VIDEO: 1
  },
  preservespitch: {
    $: "preservesPitch",
    AUDIO: 1,
    VIDEO: 1
  },
  shadowrootclonable: {
    $: "shadowRootClonable",
    TEMPLATE: 1
  },
  shadowrootdelegatesfocus: {
    $: "shadowRootDelegatesFocus",
    TEMPLATE: 1
  },
  shadowrootserializable: {
    $: "shadowRootSerializable",
    TEMPLATE: 1
  },
  sharedstoragewritable: {
    $: "sharedStorageWritable",
    IFRAME: 1,
    IMG: 1
  }
});
function getPropAlias(prop, tagName) {
  const a = PropAliases[prop];
  return typeof a === "object" ? a[tagName] ? a["$"] : undefined : a;
}
const DelegatedEvents = /*#__PURE__*/new Set(["beforeinput", "click", "dblclick", "contextmenu", "focusin", "focusout", "input", "keydown", "keyup", "mousedown", "mousemove", "mouseout", "mouseover", "mouseup", "pointerdown", "pointermove", "pointerout", "pointerover", "pointerup", "touchend", "touchmove", "touchstart"]);

function reconcileArrays(parentNode, a, b) {
  let bLength = b.length,
    aEnd = a.length,
    bEnd = bLength,
    aStart = 0,
    bStart = 0,
    after = a[aEnd - 1].nextSibling,
    map = null;
  while (aStart < aEnd || bStart < bEnd) {
    if (a[aStart] === b[bStart]) {
      aStart++;
      bStart++;
      continue;
    }
    while (a[aEnd - 1] === b[bEnd - 1]) {
      aEnd--;
      bEnd--;
    }
    if (aEnd === aStart) {
      const node = bEnd < bLength ? bStart ? b[bStart - 1].nextSibling : b[bEnd - bStart] : after;
      while (bStart < bEnd) parentNode.insertBefore(b[bStart++], node);
    } else if (bEnd === bStart) {
      while (aStart < aEnd) {
        if (!map || !map.has(a[aStart])) a[aStart].remove();
        aStart++;
      }
    } else if (a[aStart] === b[bEnd - 1] && b[bStart] === a[aEnd - 1]) {
      const node = a[--aEnd].nextSibling;
      parentNode.insertBefore(b[bStart++], a[aStart++].nextSibling);
      parentNode.insertBefore(b[--bEnd], node);
      a[aEnd] = b[bEnd];
    } else {
      if (!map) {
        map = new Map();
        let i = bStart;
        while (i < bEnd) map.set(b[i], i++);
      }
      const index = map.get(a[aStart]);
      if (index != null) {
        if (bStart < index && index < bEnd) {
          let i = aStart,
            sequence = 1,
            t;
          while (++i < aEnd && i < bEnd) {
            if ((t = map.get(a[i])) == null || t !== index + sequence) break;
            sequence++;
          }
          if (sequence > index - bStart) {
            const node = a[aStart];
            while (bStart < index) parentNode.insertBefore(b[bStart++], node);
          } else parentNode.replaceChild(b[bStart++], a[aStart++]);
        } else aStart++;
      } else a[aStart++].remove();
    }
  }
}

const $$EVENTS = "_$DX_DELEGATE";
function template(html, isImportNode, isSVG, isMathML) {
  let node;
  const create = () => {
    const t = document.createElement("template");
    t.innerHTML = html;
    return t.content.firstChild;
  };
  const fn = () => (node || (node = create())).cloneNode(true);
  fn.cloneNode = fn;
  return fn;
}
function delegateEvents(eventNames, document = window.document) {
  const e = document[$$EVENTS] || (document[$$EVENTS] = new Set());
  for (let i = 0, l = eventNames.length; i < l; i++) {
    const name = eventNames[i];
    if (!e.has(name)) {
      e.add(name);
      document.addEventListener(name, eventHandler);
    }
  }
}
function setAttribute(node, name, value) {
  if (isHydrating(node)) return;
  if (value == null) node.removeAttribute(name);else node.setAttribute(name, value);
}
function setBoolAttribute(node, name, value) {
  if (isHydrating(node)) return;
  value ? node.setAttribute(name, "") : node.removeAttribute(name);
}
function className(node, value) {
  if (isHydrating(node)) return;
  if (value == null) node.removeAttribute("class");else node.className = value;
}
function addEventListener$1(node, name, handler, delegate) {
  if (delegate) {
    if (Array.isArray(handler)) {
      node[`$$${name}`] = handler[0];
      node[`$$${name}Data`] = handler[1];
    } else node[`$$${name}`] = handler;
  } else if (Array.isArray(handler)) {
    const handlerFn = handler[0];
    node.addEventListener(name, handler[0] = e => handlerFn.call(node, handler[1], e));
  } else node.addEventListener(name, handler, typeof handler !== "function" && handler);
}
function classList(node, value, prev = {}) {
  const classKeys = Object.keys(value || {}),
    prevKeys = Object.keys(prev);
  let i, len;
  for (i = 0, len = prevKeys.length; i < len; i++) {
    const key = prevKeys[i];
    if (!key || key === "undefined" || value[key]) continue;
    toggleClassKey(node, key, false);
    delete prev[key];
  }
  for (i = 0, len = classKeys.length; i < len; i++) {
    const key = classKeys[i],
      classValue = !!value[key];
    if (!key || key === "undefined" || prev[key] === classValue || !classValue) continue;
    toggleClassKey(node, key, true);
    prev[key] = classValue;
  }
  return prev;
}
function style(node, value, prev) {
  if (!value) return prev ? setAttribute(node, "style") : value;
  const nodeStyle = node.style;
  if (typeof value === "string") return nodeStyle.cssText = value;
  typeof prev === "string" && (nodeStyle.cssText = prev = undefined);
  prev || (prev = {});
  value || (value = {});
  let v, s;
  for (s in prev) {
    value[s] == null && nodeStyle.removeProperty(s);
    delete prev[s];
  }
  for (s in value) {
    v = value[s];
    if (v !== prev[s]) {
      nodeStyle.setProperty(s, v);
      prev[s] = v;
    }
  }
  return prev;
}
function spread(node, props = {}, isSVG, skipChildren) {
  const prevProps = {};
  if (!skipChildren) {
    createRenderEffect(() => prevProps.children = insertExpression(node, props.children, prevProps.children));
  }
  createRenderEffect(() => typeof props.ref === "function" && use(props.ref, node));
  createRenderEffect(() => assign(node, props, isSVG, true, prevProps, true));
  return prevProps;
}
function use(fn, element, arg) {
  return untrack(() => fn(element, arg));
}
function insert(parent, accessor, marker, initial) {
  if (marker !== undefined && !initial) initial = [];
  if (typeof accessor !== "function") return insertExpression(parent, accessor, initial, marker);
  createRenderEffect(current => insertExpression(parent, accessor(), current, marker), initial);
}
function assign(node, props, isSVG, skipChildren, prevProps = {}, skipRef = false) {
  props || (props = {});
  for (const prop in prevProps) {
    if (!(prop in props)) {
      if (prop === "children") continue;
      prevProps[prop] = assignProp(node, prop, null, prevProps[prop], isSVG, skipRef, props);
    }
  }
  for (const prop in props) {
    if (prop === "children") {
      continue;
    }
    const value = props[prop];
    prevProps[prop] = assignProp(node, prop, value, prevProps[prop], isSVG, skipRef, props);
  }
}
function isHydrating(node) {
  return !!sharedConfig.context && !sharedConfig.done && (!node || node.isConnected);
}
function toPropertyName(name) {
  return name.toLowerCase().replace(/-([a-z])/g, (_, w) => w.toUpperCase());
}
function toggleClassKey(node, key, value) {
  const classNames = key.trim().split(/\s+/);
  for (let i = 0, nameLen = classNames.length; i < nameLen; i++) node.classList.toggle(classNames[i], value);
}
function assignProp(node, prop, value, prev, isSVG, skipRef, props) {
  let isCE, isProp, isChildProp, propAlias, forceProp;
  if (prop === "style") return style(node, value, prev);
  if (prop === "classList") return classList(node, value, prev);
  if (value === prev) return prev;
  if (prop === "ref") {
    if (!skipRef) value(node);
  } else if (prop.slice(0, 3) === "on:") {
    const e = prop.slice(3);
    prev && node.removeEventListener(e, prev, typeof prev !== "function" && prev);
    value && node.addEventListener(e, value, typeof value !== "function" && value);
  } else if (prop.slice(0, 10) === "oncapture:") {
    const e = prop.slice(10);
    prev && node.removeEventListener(e, prev, true);
    value && node.addEventListener(e, value, true);
  } else if (prop.slice(0, 2) === "on") {
    const name = prop.slice(2).toLowerCase();
    const delegate = DelegatedEvents.has(name);
    if (!delegate && prev) {
      const h = Array.isArray(prev) ? prev[0] : prev;
      node.removeEventListener(name, h);
    }
    if (delegate || value) {
      addEventListener$1(node, name, value, delegate);
      delegate && delegateEvents([name]);
    }
  } else if (prop.slice(0, 5) === "attr:") {
    setAttribute(node, prop.slice(5), value);
  } else if (prop.slice(0, 5) === "bool:") {
    setBoolAttribute(node, prop.slice(5), value);
  } else if ((forceProp = prop.slice(0, 5) === "prop:") || (isChildProp = ChildProperties.has(prop)) || ((propAlias = getPropAlias(prop, node.tagName)) || (isProp = Properties.has(prop))) || (isCE = node.nodeName.includes("-") || "is" in props)) {
    if (forceProp) {
      prop = prop.slice(5);
      isProp = true;
    } else if (isHydrating(node)) return value;
    if (prop === "class" || prop === "className") className(node, value);else if (isCE && !isProp && !isChildProp) node[toPropertyName(prop)] = value;else node[propAlias || prop] = value;
  } else {
    setAttribute(node, Aliases[prop] || prop, value);
  }
  return value;
}
function eventHandler(e) {
  if (sharedConfig.registry && sharedConfig.events) {
    if (sharedConfig.events.find(([el, ev]) => ev === e)) return;
  }
  let node = e.target;
  const key = `$$${e.type}`;
  const oriTarget = e.target;
  const oriCurrentTarget = e.currentTarget;
  const retarget = value => Object.defineProperty(e, "target", {
    configurable: true,
    value
  });
  const handleNode = () => {
    const handler = node[key];
    if (handler && !node.disabled) {
      const data = node[`${key}Data`];
      data !== undefined ? handler.call(node, data, e) : handler.call(node, e);
      if (e.cancelBubble) return;
    }
    node.host && typeof node.host !== "string" && !node.host._$host && node.contains(e.target) && retarget(node.host);
    return true;
  };
  const walkUpTree = () => {
    while (handleNode() && (node = node._$host || node.parentNode || node.host));
  };
  Object.defineProperty(e, "currentTarget", {
    configurable: true,
    get() {
      return node || document;
    }
  });
  if (sharedConfig.registry && !sharedConfig.done) sharedConfig.done = _$HY.done = true;
  if (e.composedPath) {
    const path = e.composedPath();
    retarget(path[0]);
    for (let i = 0; i < path.length - 2; i++) {
      node = path[i];
      if (!handleNode()) break;
      if (node._$host) {
        node = node._$host;
        walkUpTree();
        break;
      }
      if (node.parentNode === oriCurrentTarget) {
        break;
      }
    }
  }
  else walkUpTree();
  retarget(oriTarget);
}
function insertExpression(parent, value, current, marker, unwrapArray) {
  const hydrating = isHydrating(parent);
  if (hydrating) {
    !current && (current = [...parent.childNodes]);
    let cleaned = [];
    for (let i = 0; i < current.length; i++) {
      const node = current[i];
      if (node.nodeType === 8 && node.data.slice(0, 2) === "!$") node.remove();else cleaned.push(node);
    }
    current = cleaned;
  }
  while (typeof current === "function") current = current();
  if (value === current) return current;
  const t = typeof value,
    multi = marker !== undefined;
  parent = multi && current[0] && current[0].parentNode || parent;
  if (t === "string" || t === "number") {
    if (hydrating) return current;
    if (t === "number") {
      value = value.toString();
      if (value === current) return current;
    }
    if (multi) {
      let node = current[0];
      if (node && node.nodeType === 3) {
        node.data !== value && (node.data = value);
      } else node = document.createTextNode(value);
      current = cleanChildren(parent, current, marker, node);
    } else {
      if (current !== "" && typeof current === "string") {
        current = parent.firstChild.data = value;
      } else current = parent.textContent = value;
    }
  } else if (value == null || t === "boolean") {
    if (hydrating) return current;
    current = cleanChildren(parent, current, marker);
  } else if (t === "function") {
    createRenderEffect(() => {
      let v = value();
      while (typeof v === "function") v = v();
      current = insertExpression(parent, v, current, marker);
    });
    return () => current;
  } else if (Array.isArray(value)) {
    const array = [];
    const currentArray = current && Array.isArray(current);
    if (normalizeIncomingArray(array, value, current, unwrapArray)) {
      createRenderEffect(() => current = insertExpression(parent, array, current, marker, true));
      return () => current;
    }
    if (hydrating) {
      if (!array.length) return current;
      if (marker === undefined) return current = [...parent.childNodes];
      let node = array[0];
      if (node.parentNode !== parent) return current;
      const nodes = [node];
      while ((node = node.nextSibling) !== marker) nodes.push(node);
      return current = nodes;
    }
    if (array.length === 0) {
      current = cleanChildren(parent, current, marker);
      if (multi) return current;
    } else if (currentArray) {
      if (current.length === 0) {
        appendNodes(parent, array, marker);
      } else reconcileArrays(parent, current, array);
    } else {
      current && cleanChildren(parent);
      appendNodes(parent, array);
    }
    current = array;
  } else if (value.nodeType) {
    if (hydrating && value.parentNode) return current = multi ? [value] : value;
    if (Array.isArray(current)) {
      if (multi) return current = cleanChildren(parent, current, marker, value);
      cleanChildren(parent, current, null, value);
    } else if (current == null || current === "" || !parent.firstChild) {
      parent.appendChild(value);
    } else parent.replaceChild(value, parent.firstChild);
    current = value;
  } else ;
  return current;
}
function normalizeIncomingArray(normalized, array, current, unwrap) {
  let dynamic = false;
  for (let i = 0, len = array.length; i < len; i++) {
    let item = array[i],
      prev = current && current[normalized.length],
      t;
    if (item == null || item === true || item === false) ; else if ((t = typeof item) === "object" && item.nodeType) {
      normalized.push(item);
    } else if (Array.isArray(item)) {
      dynamic = normalizeIncomingArray(normalized, item, prev) || dynamic;
    } else if (t === "function") {
      if (unwrap) {
        while (typeof item === "function") item = item();
        dynamic = normalizeIncomingArray(normalized, Array.isArray(item) ? item : [item], Array.isArray(prev) ? prev : [prev]) || dynamic;
      } else {
        normalized.push(item);
        dynamic = true;
      }
    } else {
      const value = String(item);
      if (prev && prev.nodeType === 3 && prev.data === value) normalized.push(prev);else normalized.push(document.createTextNode(value));
    }
  }
  return dynamic;
}
function appendNodes(parent, array, marker = null) {
  for (let i = 0, len = array.length; i < len; i++) parent.insertBefore(array[i], marker);
}
function cleanChildren(parent, current, marker, replacement) {
  if (marker === undefined) return parent.textContent = "";
  const node = replacement || document.createTextNode("");
  if (current.length) {
    let inserted = false;
    for (let i = current.length - 1; i >= 0; i--) {
      const el = current[i];
      if (node !== el) {
        const isParent = el.parentNode === parent;
        if (!inserted && !i) isParent ? parent.replaceChild(node, el) : parent.insertBefore(node, marker);else isParent && el.remove();
      } else inserted = true;
    }
  } else parent.insertBefore(node, marker);
  return [node];
}

function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

// kebabCase function
const kebabCase = (str) => {
    return str
        .replace(/([a-z])([A-Z])/g, '$1-$2')
        .replace(/\s+/g, '-')
        .toLowerCase();
};

function pascalCase(string) {
    return string
        .split(' ')
        .map((word) => capitalizeFirstLetter(word))
        .join('');
}

function dispatchCustomEvent(element, eventName, detail, { bubbles = true, composed = true } = {}) {
    const event = new CustomEvent(eventName, {
        detail,
        bubbles,
        composed,
    });
    element.dispatchEvent(event);
}

function flatMap(collection, iteratee) {
    const result = [];
    if (Array.isArray(collection)) {
        for (const item of collection) {
            const mapped = iteratee(item);
            result.push(...(Array.isArray(mapped) ? mapped : [mapped]));
        }
    }
    else {
        for (const key in collection) {
            if (Object.prototype.hasOwnProperty.call(collection, key)) {
                const mapped = iteratee(collection[key]);
                result.push(...(Array.isArray(mapped) ? mapped : [mapped]));
            }
        }
    }
    return result;
}

// lodash uniqBy function
function uniqBy(array, iteratee) {
    const seen = new Map();
    return array.filter((item) => {
        const key = item[iteratee];
        if (!seen.has(key)) {
            seen.set(key, true);
            return true;
        }
        return false;
    });
}

function _arrayLikeToArray(r, a) {
  (null == a || a > r.length) && (a = r.length);
  for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e];
  return n;
}
function _arrayWithHoles(r) {
  if (Array.isArray(r)) return r;
}
function _createForOfIteratorHelper(r, e) {
  var t = "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (!t) {
    if (Array.isArray(r) || (t = _unsupportedIterableToArray(r)) || e) {
      t && (r = t);
      var n = 0,
        F = function () {};
      return {
        s: F,
        n: function () {
          return n >= r.length ? {
            done: true
          } : {
            done: false,
            value: r[n++]
          };
        },
        e: function (r) {
          throw r;
        },
        f: F
      };
    }
    throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
  }
  var o,
    a = true,
    u = false;
  return {
    s: function () {
      t = t.call(r);
    },
    n: function () {
      var r = t.next();
      return a = r.done, r;
    },
    e: function (r) {
      u = true, o = r;
    },
    f: function () {
      try {
        a || null == t.return || t.return();
      } finally {
        if (u) throw o;
      }
    }
  };
}
function _defineProperty(e, r, t) {
  return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, {
    value: t,
    enumerable: true,
    configurable: true,
    writable: true
  }) : e[r] = t, e;
}
function _iterableToArrayLimit(r, l) {
  var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"];
  if (null != t) {
    var e,
      n,
      i,
      u,
      a = [],
      f = true,
      o = false;
    try {
      if (i = (t = t.call(r)).next, 0 === l) ; else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0);
    } catch (r) {
      o = true, n = r;
    } finally {
      try {
        if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return;
      } finally {
        if (o) throw n;
      }
    }
    return a;
  }
}
function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), true).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _objectWithoutProperties(e, t) {
  if (null == e) return {};
  var o,
    r,
    i = _objectWithoutPropertiesLoose(e, t);
  if (Object.getOwnPropertySymbols) {
    var n = Object.getOwnPropertySymbols(e);
    for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]);
  }
  return i;
}
function _objectWithoutPropertiesLoose(r, e) {
  if (null == r) return {};
  var t = {};
  for (var n in r) if ({}.hasOwnProperty.call(r, n)) {
    if (-1 !== e.indexOf(n)) continue;
    t[n] = r[n];
  }
  return t;
}
function _slicedToArray(r, e) {
  return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest();
}
function _toPrimitive(t, r) {
  if ("object" != typeof t || !t) return t;
  var e = t[Symbol.toPrimitive];
  if (void 0 !== e) {
    var i = e.call(t, r);
    if ("object" != typeof i) return i;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return ("string" === r ? String : Number)(t);
}
function _toPropertyKey(t) {
  var i = _toPrimitive(t, "string");
  return "symbol" == typeof i ? i : i + "";
}
function _unsupportedIterableToArray(r, a) {
  if (r) {
    if ("string" == typeof r) return _arrayLikeToArray(r, a);
    var t = {}.toString.call(r).slice(8, -1);
    return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0;
  }
}

var countries$1 = {
	AD: "Andorra",
	AE: "United Arab Emirates",
	AF: "Afghanistan",
	AG: "Antigua and Barbuda",
	AI: "Anguilla",
	AL: "Albania",
	AM: "Armenia",
	AO: "Angola",
	AQ: "Antarctica",
	AR: "Argentina",
	AS: "American Samoa",
	AT: "Austria",
	AU: "Australia",
	AW: "Aruba",
	AX: "Åland Islands",
	AZ: "Azerbaijan",
	BA: "Bosnia and Herzegovina",
	BB: "Barbados",
	BD: "Bangladesh",
	BE: "Belgium",
	BF: "Burkina Faso",
	BG: "Bulgaria",
	BH: "Bahrain",
	BI: "Burundi",
	BJ: "Benin",
	BL: "Saint Barthélemy",
	BM: "Bermuda",
	BN: "Brunei",
	BO: "Bolivia",
	BQ: "Caribbean Netherlands",
	BR: "Brazil",
	BS: "Bahamas",
	BT: "Bhutan",
	BW: "Botswana",
	BY: "Belarus",
	BZ: "Belize",
	CA: "Canada",
	CC: "Cocos Islands",
	CD: "Democratic Republic of the Congo",
	CF: "Central African Republic",
	CG: "Republic of the Congo",
	CH: "Switzerland",
	CI: "Ivory Coast",
	CK: "Cook Islands",
	CL: "Chile",
	CM: "Cameroon",
	CN: "China",
	CO: "Colombia",
	CR: "Costa Rica",
	CU: "Cuba",
	CV: "Cabo Verde",
	CW: "Curaçao",
	CX: "Christmas Island",
	CY: "Cyprus",
	CZ: "Czechia",
	DE: "Germany",
	DJ: "Djibouti",
	DK: "Denmark",
	DM: "Dominica",
	DO: "Dominican Republic",
	DZ: "Algeria",
	EC: "Ecuador",
	EE: "Estonia",
	EG: "Egypt",
	EH: "Western Sahara",
	ER: "Eritrea",
	ES: "Spain",
	ET: "Ethiopia",
	FI: "Finland",
	FJ: "Fiji",
	FK: "Falkland Islands",
	FM: "Micronesia",
	FO: "Faroe Islands",
	FR: "France",
	GA: "Gabon",
	GB: "United Kingdom",
	GD: "Grenada",
	GE: "Georgia",
	GF: "French Guiana",
	GG: "Guernsey",
	GH: "Ghana",
	GI: "Gibraltar",
	GL: "Greenland",
	GM: "Gambia",
	GN: "Guinea",
	GP: "Guadeloupe",
	GQ: "Equatorial Guinea",
	GR: "Greece",
	GS: "South Georgia and the South Sandwich Islands",
	GT: "Guatemala",
	GU: "Guam",
	GW: "Guinea-Bissau",
	GY: "Guyana",
	HK: "Hong Kong",
	HN: "Honduras",
	HR: "Croatia",
	HT: "Haiti",
	HU: "Hungary",
	ID: "Indonesia",
	IE: "Ireland",
	IL: "Israel",
	IM: "Isle of Man",
	IN: "India",
	IO: "British Indian Ocean Territory",
	IQ: "Iraq",
	IR: "Iran",
	IS: "Iceland",
	IT: "Italy",
	JE: "Jersey",
	JM: "Jamaica",
	JO: "Jordan",
	JP: "Japan",
	KE: "Kenya",
	KG: "Kyrgyzstan",
	KH: "Cambodia",
	KI: "Kiribati",
	KM: "Comoros",
	KN: "Saint Kitts and Nevis",
	KP: "North Korea",
	KR: "South Korea",
	KW: "Kuwait",
	KY: "Cayman Islands",
	KZ: "Kazakhstan",
	LA: "Laos",
	LB: "Lebanon",
	LC: "Saint Lucia",
	LI: "Liechtenstein",
	LK: "Sri Lanka",
	LR: "Liberia",
	LS: "Lesotho",
	LT: "Lithuania",
	LU: "Luxembourg",
	LV: "Latvia",
	LY: "Libya",
	MA: "Morocco",
	MC: "Monaco",
	MD: "Moldova",
	ME: "Montenegro",
	MF: "Saint Martin",
	MG: "Madagascar",
	MH: "Marshall Islands",
	MK: "North Macedonia",
	ML: "Mali",
	MM: "Myanmar",
	MN: "Mongolia",
	MO: "Macao",
	MP: "Northern Mariana Islands",
	MQ: "Martinique",
	MR: "Mauritania",
	MS: "Montserrat",
	MT: "Malta",
	MU: "Mauritius",
	MV: "Maldives",
	MW: "Malawi",
	MX: "Mexico",
	MY: "Malaysia",
	MZ: "Mozambique",
	NA: "Namibia",
	NC: "New Caledonia",
	NE: "Niger",
	NF: "Norfolk Island",
	NG: "Nigeria",
	NI: "Nicaragua",
	NL: "Netherlands",
	NO: "Norway",
	NP: "Nepal",
	NR: "Nauru",
	NU: "Niue",
	NZ: "New Zealand",
	OM: "Oman",
	PA: "Panama",
	PE: "Peru",
	PF: "French Polynesia",
	PG: "Papua New Guinea",
	PH: "Philippines",
	PK: "Pakistan",
	PL: "Poland",
	PM: "Saint Pierre and Miquelon",
	PN: "Pitcairn",
	PR: "Puerto Rico",
	PS: "Palestine",
	PT: "Portugal",
	PW: "Palau",
	PY: "Paraguay",
	QA: "Qatar",
	RE: "Réunion",
	RO: "Romania",
	RS: "Serbia",
	RU: "Russia",
	RW: "Rwanda",
	SA: "Saudi Arabia",
	SB: "Solomon Islands",
	SC: "Seychelles",
	SD: "Sudan",
	SE: "Sweden",
	SG: "Singapore",
	SH: "Saint Helena, Ascension and Tristan da Cunha",
	SI: "Slovenia",
	SJ: "Svalbard and Jan Mayen",
	SK: "Slovakia",
	SL: "Sierra Leone",
	SM: "San Marino",
	SN: "Senegal",
	SO: "Somalia",
	SR: "Suriname",
	SS: "South Sudan",
	ST: "Sao Tome and Principe",
	SV: "El Salvador",
	SX: "Sint Maarten",
	SY: "Syria",
	SZ: "Eswatini",
	TC: "Turks and Caicos Islands",
	TD: "Chad",
	TF: "French Southern Territories",
	TG: "Togo",
	TH: "Thailand",
	TJ: "Tajikistan",
	TK: "Tokelau",
	TL: "Timor-Leste",
	TM: "Turkmenistan",
	TN: "Tunisia",
	TO: "Tonga",
	TR: "Türkiye",
	TT: "Trinidad and Tobago",
	TV: "Tuvalu",
	TW: "Taiwan",
	TZ: "Tanzania",
	UA: "Ukraine",
	UG: "Uganda",
	UM: "United States Minor Outlying Islands",
	US: "United States of America",
	UY: "Uruguay",
	UZ: "Uzbekistan",
	VA: "Holy See",
	VC: "Saint Vincent and the Grenadines",
	VE: "Venezuela",
	VG: "Virgin Islands (UK)",
	VI: "Virgin Islands (US)",
	VN: "Vietnam",
	VU: "Vanuatu",
	WF: "Wallis and Futuna",
	WS: "Samoa",
	YE: "Yemen",
	YT: "Mayotte",
	ZA: "South Africa",
	ZM: "Zambia",
	ZW: "Zimbabwe"
};
var timezones$1 = {
	"Africa/Abidjan": {
		u: 0,
		c: [
			"CI",
			"BF",
			"GH",
			"GM",
			"GN",
			"IS",
			"ML",
			"MR",
			"SH",
			"SL",
			"SN",
			"TG"
		]
	},
	"Africa/Accra": {
		a: "Africa/Abidjan",
		c: [
			"GH"
		],
		r: 1
	},
	"Africa/Addis_Ababa": {
		a: "Africa/Nairobi",
		c: [
			"ET"
		],
		r: 1
	},
	"Africa/Algiers": {
		u: 60,
		c: [
			"DZ"
		]
	},
	"Africa/Asmara": {
		a: "Africa/Nairobi",
		c: [
			"ER"
		],
		r: 1
	},
	"Africa/Asmera": {
		a: "Africa/Nairobi",
		c: [
			"ER"
		],
		r: 1
	},
	"Africa/Bamako": {
		a: "Africa/Abidjan",
		c: [
			"ML"
		],
		r: 1
	},
	"Africa/Bangui": {
		a: "Africa/Lagos",
		c: [
			"CF"
		],
		r: 1
	},
	"Africa/Banjul": {
		a: "Africa/Abidjan",
		c: [
			"GM"
		],
		r: 1
	},
	"Africa/Bissau": {
		u: 0,
		c: [
			"GW"
		]
	},
	"Africa/Blantyre": {
		a: "Africa/Maputo",
		c: [
			"MW"
		],
		r: 1
	},
	"Africa/Brazzaville": {
		a: "Africa/Lagos",
		c: [
			"CG"
		],
		r: 1
	},
	"Africa/Bujumbura": {
		a: "Africa/Maputo",
		c: [
			"BI"
		],
		r: 1
	},
	"Africa/Cairo": {
		u: 120,
		d: 180,
		c: [
			"EG"
		]
	},
	"Africa/Casablanca": {
		u: 60,
		d: 0,
		c: [
			"MA"
		]
	},
	"Africa/Ceuta": {
		u: 60,
		d: 120,
		c: [
			"ES"
		]
	},
	"Africa/Conakry": {
		a: "Africa/Abidjan",
		c: [
			"GN"
		],
		r: 1
	},
	"Africa/Dakar": {
		a: "Africa/Abidjan",
		c: [
			"SN"
		],
		r: 1
	},
	"Africa/Dar_es_Salaam": {
		a: "Africa/Nairobi",
		c: [
			"TZ"
		],
		r: 1
	},
	"Africa/Djibouti": {
		a: "Africa/Nairobi",
		c: [
			"DJ"
		],
		r: 1
	},
	"Africa/Douala": {
		a: "Africa/Lagos",
		c: [
			"CM"
		],
		r: 1
	},
	"Africa/El_Aaiun": {
		u: 60,
		d: 0,
		c: [
			"EH"
		]
	},
	"Africa/Freetown": {
		a: "Africa/Abidjan",
		c: [
			"SL"
		],
		r: 1
	},
	"Africa/Gaborone": {
		a: "Africa/Maputo",
		c: [
			"BW"
		],
		r: 1
	},
	"Africa/Harare": {
		a: "Africa/Maputo",
		c: [
			"ZW"
		],
		r: 1
	},
	"Africa/Johannesburg": {
		u: 120,
		c: [
			"ZA",
			"LS",
			"SZ"
		]
	},
	"Africa/Juba": {
		u: 120,
		c: [
			"SS"
		]
	},
	"Africa/Kampala": {
		a: "Africa/Nairobi",
		c: [
			"UG"
		],
		r: 1
	},
	"Africa/Khartoum": {
		u: 120,
		c: [
			"SD"
		]
	},
	"Africa/Kigali": {
		a: "Africa/Maputo",
		c: [
			"RW"
		],
		r: 1
	},
	"Africa/Kinshasa": {
		a: "Africa/Lagos",
		c: [
			"CD"
		],
		r: 1
	},
	"Africa/Lagos": {
		u: 60,
		c: [
			"NG",
			"AO",
			"BJ",
			"CD",
			"CF",
			"CG",
			"CM",
			"GA",
			"GQ",
			"NE"
		]
	},
	"Africa/Libreville": {
		a: "Africa/Lagos",
		c: [
			"GA"
		],
		r: 1
	},
	"Africa/Lome": {
		a: "Africa/Abidjan",
		c: [
			"TG"
		],
		r: 1
	},
	"Africa/Luanda": {
		a: "Africa/Lagos",
		c: [
			"AO"
		],
		r: 1
	},
	"Africa/Lubumbashi": {
		a: "Africa/Maputo",
		c: [
			"CD"
		],
		r: 1
	},
	"Africa/Lusaka": {
		a: "Africa/Maputo",
		c: [
			"ZM"
		],
		r: 1
	},
	"Africa/Malabo": {
		a: "Africa/Lagos",
		c: [
			"GQ"
		],
		r: 1
	},
	"Africa/Maputo": {
		u: 120,
		c: [
			"MZ",
			"BI",
			"BW",
			"CD",
			"MW",
			"RW",
			"ZM",
			"ZW"
		]
	},
	"Africa/Maseru": {
		a: "Africa/Johannesburg",
		c: [
			"LS"
		],
		r: 1
	},
	"Africa/Mbabane": {
		a: "Africa/Johannesburg",
		c: [
			"SZ"
		],
		r: 1
	},
	"Africa/Mogadishu": {
		a: "Africa/Nairobi",
		c: [
			"SO"
		],
		r: 1
	},
	"Africa/Monrovia": {
		u: 0,
		c: [
			"LR"
		]
	},
	"Africa/Nairobi": {
		u: 180,
		c: [
			"KE",
			"DJ",
			"ER",
			"ET",
			"KM",
			"MG",
			"SO",
			"TZ",
			"UG",
			"YT"
		]
	},
	"Africa/Ndjamena": {
		u: 60,
		c: [
			"TD"
		]
	},
	"Africa/Niamey": {
		a: "Africa/Lagos",
		c: [
			"NE"
		],
		r: 1
	},
	"Africa/Nouakchott": {
		a: "Africa/Abidjan",
		c: [
			"MR"
		],
		r: 1
	},
	"Africa/Ouagadougou": {
		a: "Africa/Abidjan",
		c: [
			"BF"
		],
		r: 1
	},
	"Africa/Porto-Novo": {
		a: "Africa/Lagos",
		c: [
			"BJ"
		],
		r: 1
	},
	"Africa/Sao_Tome": {
		u: 0,
		c: [
			"ST"
		]
	},
	"Africa/Timbuktu": {
		a: "Africa/Abidjan",
		c: [
			"ML"
		],
		r: 1
	},
	"Africa/Tripoli": {
		u: 120,
		c: [
			"LY"
		]
	},
	"Africa/Tunis": {
		u: 60,
		c: [
			"TN"
		]
	},
	"Africa/Windhoek": {
		u: 120,
		c: [
			"NA"
		]
	},
	"America/Adak": {
		u: -600,
		d: -540,
		c: [
			"US"
		]
	},
	"America/Anchorage": {
		u: -540,
		d: -480,
		c: [
			"US"
		]
	},
	"America/Anguilla": {
		a: "America/Puerto_Rico",
		c: [
			"AI"
		],
		r: 1
	},
	"America/Antigua": {
		a: "America/Puerto_Rico",
		c: [
			"AG"
		],
		r: 1
	},
	"America/Araguaina": {
		u: -180,
		c: [
			"BR"
		]
	},
	"America/Argentina/Buenos_Aires": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Argentina/Catamarca": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Argentina/ComodRivadavia": {
		a: "America/Argentina/Catamarca",
		r: 1
	},
	"America/Argentina/Cordoba": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Argentina/Jujuy": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Argentina/La_Rioja": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Argentina/Mendoza": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Argentina/Rio_Gallegos": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Argentina/Salta": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Argentina/San_Juan": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Argentina/San_Luis": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Argentina/Tucuman": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Argentina/Ushuaia": {
		u: -180,
		c: [
			"AR"
		]
	},
	"America/Aruba": {
		a: "America/Puerto_Rico",
		c: [
			"AW"
		],
		r: 1
	},
	"America/Asuncion": {
		u: -180,
		c: [
			"PY"
		]
	},
	"America/Atikokan": {
		a: "America/Panama",
		c: [
			"CA"
		],
		r: 1
	},
	"America/Atka": {
		a: "America/Adak",
		r: 1
	},
	"America/Bahia": {
		u: -180,
		c: [
			"BR"
		]
	},
	"America/Bahia_Banderas": {
		u: -360,
		c: [
			"MX"
		]
	},
	"America/Barbados": {
		u: -240,
		c: [
			"BB"
		]
	},
	"America/Belem": {
		u: -180,
		c: [
			"BR"
		]
	},
	"America/Belize": {
		u: -360,
		c: [
			"BZ"
		]
	},
	"America/Blanc-Sablon": {
		a: "America/Puerto_Rico",
		c: [
			"CA"
		],
		r: 1
	},
	"America/Boa_Vista": {
		u: -240,
		c: [
			"BR"
		]
	},
	"America/Bogota": {
		u: -300,
		c: [
			"CO"
		]
	},
	"America/Boise": {
		u: -420,
		d: -360,
		c: [
			"US"
		]
	},
	"America/Buenos_Aires": {
		a: "America/Argentina/Buenos_Aires",
		r: 1
	},
	"America/Cambridge_Bay": {
		u: -420,
		d: -360,
		c: [
			"CA"
		]
	},
	"America/Campo_Grande": {
		u: -240,
		c: [
			"BR"
		]
	},
	"America/Cancun": {
		u: -300,
		c: [
			"MX"
		]
	},
	"America/Caracas": {
		u: -240,
		c: [
			"VE"
		]
	},
	"America/Catamarca": {
		a: "America/Argentina/Catamarca",
		r: 1
	},
	"America/Cayenne": {
		u: -180,
		c: [
			"GF"
		]
	},
	"America/Cayman": {
		a: "America/Panama",
		c: [
			"KY"
		],
		r: 1
	},
	"America/Chicago": {
		u: -360,
		d: -300,
		c: [
			"US"
		]
	},
	"America/Chihuahua": {
		u: -360,
		c: [
			"MX"
		]
	},
	"America/Ciudad_Juarez": {
		u: -420,
		d: -360,
		c: [
			"MX"
		]
	},
	"America/Coral_Harbour": {
		a: "America/Panama",
		c: [
			"CA"
		],
		r: 1
	},
	"America/Cordoba": {
		a: "America/Argentina/Cordoba",
		r: 1
	},
	"America/Costa_Rica": {
		u: -360,
		c: [
			"CR"
		]
	},
	"America/Coyhaique": {
		u: -180,
		c: [
			"CL"
		]
	},
	"America/Creston": {
		a: "America/Phoenix",
		c: [
			"CA"
		],
		r: 1
	},
	"America/Cuiaba": {
		u: -240,
		c: [
			"BR"
		]
	},
	"America/Curacao": {
		a: "America/Puerto_Rico",
		c: [
			"CW"
		],
		r: 1
	},
	"America/Danmarkshavn": {
		u: 0,
		c: [
			"GL"
		]
	},
	"America/Dawson": {
		u: -420,
		c: [
			"CA"
		]
	},
	"America/Dawson_Creek": {
		u: -420,
		c: [
			"CA"
		]
	},
	"America/Denver": {
		u: -420,
		d: -360,
		c: [
			"US"
		]
	},
	"America/Detroit": {
		u: -300,
		d: -240,
		c: [
			"US"
		]
	},
	"America/Dominica": {
		a: "America/Puerto_Rico",
		c: [
			"DM"
		],
		r: 1
	},
	"America/Edmonton": {
		u: -420,
		d: -360,
		c: [
			"CA"
		]
	},
	"America/Eirunepe": {
		u: -300,
		c: [
			"BR"
		]
	},
	"America/El_Salvador": {
		u: -360,
		c: [
			"SV"
		]
	},
	"America/Ensenada": {
		a: "America/Tijuana",
		r: 1
	},
	"America/Fort_Nelson": {
		u: -420,
		c: [
			"CA"
		]
	},
	"America/Fort_Wayne": {
		a: "America/Indiana/Indianapolis",
		r: 1
	},
	"America/Fortaleza": {
		u: -180,
		c: [
			"BR"
		]
	},
	"America/Glace_Bay": {
		u: -240,
		d: -180,
		c: [
			"CA"
		]
	},
	"America/Godthab": {
		a: "America/Nuuk",
		r: 1
	},
	"America/Goose_Bay": {
		u: -240,
		d: -180,
		c: [
			"CA"
		]
	},
	"America/Grand_Turk": {
		u: -300,
		d: -240,
		c: [
			"TC"
		]
	},
	"America/Grenada": {
		a: "America/Puerto_Rico",
		c: [
			"GD"
		],
		r: 1
	},
	"America/Guadeloupe": {
		a: "America/Puerto_Rico",
		c: [
			"GP"
		],
		r: 1
	},
	"America/Guatemala": {
		u: -360,
		c: [
			"GT"
		]
	},
	"America/Guayaquil": {
		u: -300,
		c: [
			"EC"
		]
	},
	"America/Guyana": {
		u: -240,
		c: [
			"GY"
		]
	},
	"America/Halifax": {
		u: -240,
		d: -180,
		c: [
			"CA"
		]
	},
	"America/Havana": {
		u: -300,
		d: -240,
		c: [
			"CU"
		]
	},
	"America/Hermosillo": {
		u: -420,
		c: [
			"MX"
		]
	},
	"America/Indiana/Indianapolis": {
		u: -300,
		d: -240,
		c: [
			"US"
		]
	},
	"America/Indiana/Knox": {
		u: -360,
		d: -300,
		c: [
			"US"
		]
	},
	"America/Indiana/Marengo": {
		u: -300,
		d: -240,
		c: [
			"US"
		]
	},
	"America/Indiana/Petersburg": {
		u: -300,
		d: -240,
		c: [
			"US"
		]
	},
	"America/Indiana/Tell_City": {
		u: -360,
		d: -300,
		c: [
			"US"
		]
	},
	"America/Indiana/Vevay": {
		u: -300,
		d: -240,
		c: [
			"US"
		]
	},
	"America/Indiana/Vincennes": {
		u: -300,
		d: -240,
		c: [
			"US"
		]
	},
	"America/Indiana/Winamac": {
		u: -300,
		d: -240,
		c: [
			"US"
		]
	},
	"America/Indianapolis": {
		a: "America/Indiana/Indianapolis",
		r: 1
	},
	"America/Inuvik": {
		u: -420,
		d: -360,
		c: [
			"CA"
		]
	},
	"America/Iqaluit": {
		u: -300,
		d: -240,
		c: [
			"CA"
		]
	},
	"America/Jamaica": {
		u: -300,
		c: [
			"JM"
		]
	},
	"America/Jujuy": {
		a: "America/Argentina/Jujuy",
		r: 1
	},
	"America/Juneau": {
		u: -540,
		d: -480,
		c: [
			"US"
		]
	},
	"America/Kentucky/Louisville": {
		u: -300,
		d: -240,
		c: [
			"US"
		]
	},
	"America/Kentucky/Monticello": {
		u: -300,
		d: -240,
		c: [
			"US"
		]
	},
	"America/Knox_IN": {
		a: "America/Indiana/Knox",
		r: 1
	},
	"America/Kralendijk": {
		a: "America/Puerto_Rico",
		c: [
			"BQ"
		],
		r: 1
	},
	"America/La_Paz": {
		u: -240,
		c: [
			"BO"
		]
	},
	"America/Lima": {
		u: -300,
		c: [
			"PE"
		]
	},
	"America/Los_Angeles": {
		u: -480,
		d: -420,
		c: [
			"US"
		]
	},
	"America/Louisville": {
		a: "America/Kentucky/Louisville",
		r: 1
	},
	"America/Lower_Princes": {
		a: "America/Puerto_Rico",
		c: [
			"SX"
		],
		r: 1
	},
	"America/Maceio": {
		u: -180,
		c: [
			"BR"
		]
	},
	"America/Managua": {
		u: -360,
		c: [
			"NI"
		]
	},
	"America/Manaus": {
		u: -240,
		c: [
			"BR"
		]
	},
	"America/Marigot": {
		a: "America/Puerto_Rico",
		c: [
			"MF"
		],
		r: 1
	},
	"America/Martinique": {
		u: -240,
		c: [
			"MQ"
		]
	},
	"America/Matamoros": {
		u: -360,
		d: -300,
		c: [
			"MX"
		]
	},
	"America/Mazatlan": {
		u: -420,
		c: [
			"MX"
		]
	},
	"America/Mendoza": {
		a: "America/Argentina/Mendoza",
		r: 1
	},
	"America/Menominee": {
		u: -360,
		d: -300,
		c: [
			"US"
		]
	},
	"America/Merida": {
		u: -360,
		c: [
			"MX"
		]
	},
	"America/Metlakatla": {
		u: -540,
		d: -480,
		c: [
			"US"
		]
	},
	"America/Mexico_City": {
		u: -360,
		c: [
			"MX"
		]
	},
	"America/Miquelon": {
		u: -180,
		d: -120,
		c: [
			"PM"
		]
	},
	"America/Moncton": {
		u: -240,
		d: -180,
		c: [
			"CA"
		]
	},
	"America/Monterrey": {
		u: -360,
		c: [
			"MX"
		]
	},
	"America/Montevideo": {
		u: -180,
		c: [
			"UY"
		]
	},
	"America/Montreal": {
		a: "America/Toronto",
		c: [
			"CA"
		],
		r: 1
	},
	"America/Montserrat": {
		a: "America/Puerto_Rico",
		c: [
			"MS"
		],
		r: 1
	},
	"America/Nassau": {
		a: "America/Toronto",
		c: [
			"BS"
		],
		r: 1
	},
	"America/New_York": {
		u: -300,
		d: -240,
		c: [
			"US"
		]
	},
	"America/Nipigon": {
		a: "America/Toronto",
		c: [
			"CA"
		],
		r: 1
	},
	"America/Nome": {
		u: -540,
		d: -480,
		c: [
			"US"
		]
	},
	"America/Noronha": {
		u: -120,
		c: [
			"BR"
		]
	},
	"America/North_Dakota/Beulah": {
		u: -360,
		d: -300,
		c: [
			"US"
		]
	},
	"America/North_Dakota/Center": {
		u: -360,
		d: -300,
		c: [
			"US"
		]
	},
	"America/North_Dakota/New_Salem": {
		u: -360,
		d: -300,
		c: [
			"US"
		]
	},
	"America/Nuuk": {
		u: -120,
		d: -60,
		c: [
			"GL"
		]
	},
	"America/Ojinaga": {
		u: -360,
		d: -300,
		c: [
			"MX"
		]
	},
	"America/Panama": {
		u: -300,
		c: [
			"PA",
			"CA",
			"KY"
		]
	},
	"America/Pangnirtung": {
		a: "America/Iqaluit",
		r: 1
	},
	"America/Paramaribo": {
		u: -180,
		c: [
			"SR"
		]
	},
	"America/Phoenix": {
		u: -420,
		c: [
			"US",
			"CA"
		]
	},
	"America/Port-au-Prince": {
		u: -300,
		d: -240,
		c: [
			"HT"
		]
	},
	"America/Port_of_Spain": {
		a: "America/Puerto_Rico",
		c: [
			"TT"
		],
		r: 1
	},
	"America/Porto_Acre": {
		a: "America/Rio_Branco",
		r: 1
	},
	"America/Porto_Velho": {
		u: -240,
		c: [
			"BR"
		]
	},
	"America/Puerto_Rico": {
		u: -240,
		c: [
			"PR",
			"AG",
			"CA",
			"AI",
			"AW",
			"BL",
			"BQ",
			"CW",
			"DM",
			"GD",
			"GP",
			"KN",
			"LC",
			"MF",
			"MS",
			"SX",
			"TT",
			"VC",
			"VG",
			"VI"
		]
	},
	"America/Punta_Arenas": {
		u: -180,
		c: [
			"CL"
		]
	},
	"America/Rainy_River": {
		a: "America/Winnipeg",
		r: 1
	},
	"America/Rankin_Inlet": {
		u: -360,
		d: -300,
		c: [
			"CA"
		]
	},
	"America/Recife": {
		u: -180,
		c: [
			"BR"
		]
	},
	"America/Regina": {
		u: -360,
		c: [
			"CA"
		]
	},
	"America/Resolute": {
		u: -360,
		d: -300,
		c: [
			"CA"
		]
	},
	"America/Rio_Branco": {
		u: -300,
		c: [
			"BR"
		]
	},
	"America/Rosario": {
		a: "America/Argentina/Cordoba",
		r: 1
	},
	"America/Santa_Isabel": {
		a: "America/Tijuana",
		r: 1
	},
	"America/Santarem": {
		u: -180,
		c: [
			"BR"
		]
	},
	"America/Santiago": {
		u: -240,
		d: -180,
		c: [
			"CL"
		]
	},
	"America/Santo_Domingo": {
		u: -240,
		c: [
			"DO"
		]
	},
	"America/Sao_Paulo": {
		u: -180,
		c: [
			"BR"
		]
	},
	"America/Scoresbysund": {
		u: -120,
		d: -60,
		c: [
			"GL"
		]
	},
	"America/Shiprock": {
		a: "America/Denver",
		r: 1
	},
	"America/Sitka": {
		u: -540,
		d: -480,
		c: [
			"US"
		]
	},
	"America/St_Barthelemy": {
		a: "America/Puerto_Rico",
		c: [
			"BL"
		],
		r: 1
	},
	"America/St_Johns": {
		u: -210,
		d: -150,
		c: [
			"CA"
		]
	},
	"America/St_Kitts": {
		a: "America/Puerto_Rico",
		c: [
			"KN"
		],
		r: 1
	},
	"America/St_Lucia": {
		a: "America/Puerto_Rico",
		c: [
			"LC"
		],
		r: 1
	},
	"America/St_Thomas": {
		a: "America/Puerto_Rico",
		c: [
			"VI"
		],
		r: 1
	},
	"America/St_Vincent": {
		a: "America/Puerto_Rico",
		c: [
			"VC"
		],
		r: 1
	},
	"America/Swift_Current": {
		u: -360,
		c: [
			"CA"
		]
	},
	"America/Tegucigalpa": {
		u: -360,
		c: [
			"HN"
		]
	},
	"America/Thule": {
		u: -240,
		d: -180,
		c: [
			"GL"
		]
	},
	"America/Thunder_Bay": {
		a: "America/Toronto",
		c: [
			"CA"
		],
		r: 1
	},
	"America/Tijuana": {
		u: -480,
		d: -420,
		c: [
			"MX"
		]
	},
	"America/Toronto": {
		u: -300,
		d: -240,
		c: [
			"CA",
			"BS"
		]
	},
	"America/Tortola": {
		a: "America/Puerto_Rico",
		c: [
			"VG"
		],
		r: 1
	},
	"America/Vancouver": {
		u: -480,
		d: -420,
		c: [
			"CA"
		]
	},
	"America/Virgin": {
		a: "America/Puerto_Rico",
		c: [
			"VI"
		],
		r: 1
	},
	"America/Whitehorse": {
		u: -420,
		c: [
			"CA"
		]
	},
	"America/Winnipeg": {
		u: -360,
		d: -300,
		c: [
			"CA"
		]
	},
	"America/Yakutat": {
		u: -540,
		d: -480,
		c: [
			"US"
		]
	},
	"America/Yellowknife": {
		a: "America/Edmonton",
		r: 1
	},
	"Antarctica/Casey": {
		u: 480,
		c: [
			"AQ"
		]
	},
	"Antarctica/Davis": {
		u: 420,
		c: [
			"AQ"
		]
	},
	"Antarctica/DumontDUrville": {
		a: "Pacific/Port_Moresby",
		c: [
			"AQ"
		],
		r: 1
	},
	"Antarctica/Macquarie": {
		u: 600,
		d: 660,
		c: [
			"AU"
		]
	},
	"Antarctica/Mawson": {
		u: 300,
		c: [
			"AQ"
		]
	},
	"Antarctica/McMurdo": {
		a: "Pacific/Auckland",
		c: [
			"AQ"
		],
		r: 1
	},
	"Antarctica/Palmer": {
		u: -180,
		c: [
			"AQ"
		]
	},
	"Antarctica/Rothera": {
		u: -180,
		c: [
			"AQ"
		]
	},
	"Antarctica/South_Pole": {
		a: "Pacific/Auckland",
		c: [
			"AQ"
		],
		r: 1
	},
	"Antarctica/Syowa": {
		a: "Asia/Riyadh",
		c: [
			"AQ"
		],
		r: 1
	},
	"Antarctica/Troll": {
		u: 0,
		d: 120,
		c: [
			"AQ"
		]
	},
	"Antarctica/Vostok": {
		u: 300,
		c: [
			"AQ"
		]
	},
	"Arctic/Longyearbyen": {
		a: "Europe/Berlin",
		c: [
			"SJ"
		],
		r: 1
	},
	"Asia/Aden": {
		a: "Asia/Riyadh",
		c: [
			"YE"
		],
		r: 1
	},
	"Asia/Almaty": {
		u: 300,
		c: [
			"KZ"
		]
	},
	"Asia/Amman": {
		u: 180,
		c: [
			"JO"
		]
	},
	"Asia/Anadyr": {
		u: 720,
		c: [
			"RU"
		]
	},
	"Asia/Aqtau": {
		u: 300,
		c: [
			"KZ"
		]
	},
	"Asia/Aqtobe": {
		u: 300,
		c: [
			"KZ"
		]
	},
	"Asia/Ashgabat": {
		u: 300,
		c: [
			"TM"
		]
	},
	"Asia/Ashkhabad": {
		a: "Asia/Ashgabat",
		r: 1
	},
	"Asia/Atyrau": {
		u: 300,
		c: [
			"KZ"
		]
	},
	"Asia/Baghdad": {
		u: 180,
		c: [
			"IQ"
		]
	},
	"Asia/Bahrain": {
		a: "Asia/Qatar",
		c: [
			"BH"
		],
		r: 1
	},
	"Asia/Baku": {
		u: 240,
		c: [
			"AZ"
		]
	},
	"Asia/Bangkok": {
		u: 420,
		c: [
			"TH",
			"CX",
			"KH",
			"LA",
			"VN"
		]
	},
	"Asia/Barnaul": {
		u: 420,
		c: [
			"RU"
		]
	},
	"Asia/Beirut": {
		u: 120,
		d: 180,
		c: [
			"LB"
		]
	},
	"Asia/Bishkek": {
		u: 360,
		c: [
			"KG"
		]
	},
	"Asia/Brunei": {
		a: "Asia/Kuching",
		c: [
			"BN"
		],
		r: 1
	},
	"Asia/Calcutta": {
		a: "Asia/Kolkata",
		r: 1
	},
	"Asia/Chita": {
		u: 540,
		c: [
			"RU"
		]
	},
	"Asia/Choibalsan": {
		a: "Asia/Ulaanbaatar",
		r: 1
	},
	"Asia/Chongqing": {
		a: "Asia/Shanghai",
		r: 1
	},
	"Asia/Chungking": {
		a: "Asia/Shanghai",
		r: 1
	},
	"Asia/Colombo": {
		u: 330,
		c: [
			"LK"
		]
	},
	"Asia/Dacca": {
		a: "Asia/Dhaka",
		r: 1
	},
	"Asia/Damascus": {
		u: 180,
		c: [
			"SY"
		]
	},
	"Asia/Dhaka": {
		u: 360,
		c: [
			"BD"
		]
	},
	"Asia/Dili": {
		u: 540,
		c: [
			"TL"
		]
	},
	"Asia/Dubai": {
		u: 240,
		c: [
			"AE",
			"OM",
			"RE",
			"SC",
			"TF"
		]
	},
	"Asia/Dushanbe": {
		u: 300,
		c: [
			"TJ"
		]
	},
	"Asia/Famagusta": {
		u: 120,
		d: 180,
		c: [
			"CY"
		]
	},
	"Asia/Gaza": {
		u: 120,
		d: 180,
		c: [
			"PS"
		]
	},
	"Asia/Harbin": {
		a: "Asia/Shanghai",
		r: 1
	},
	"Asia/Hebron": {
		u: 120,
		d: 180,
		c: [
			"PS"
		]
	},
	"Asia/Ho_Chi_Minh": {
		u: 420,
		c: [
			"VN"
		]
	},
	"Asia/Hong_Kong": {
		u: 480,
		c: [
			"HK"
		]
	},
	"Asia/Hovd": {
		u: 420,
		c: [
			"MN"
		]
	},
	"Asia/Irkutsk": {
		u: 480,
		c: [
			"RU"
		]
	},
	"Asia/Istanbul": {
		a: "Europe/Istanbul",
		r: 1
	},
	"Asia/Jakarta": {
		u: 420,
		c: [
			"ID"
		]
	},
	"Asia/Jayapura": {
		u: 540,
		c: [
			"ID"
		]
	},
	"Asia/Jerusalem": {
		u: 120,
		d: 180,
		c: [
			"IL"
		]
	},
	"Asia/Kabul": {
		u: 270,
		c: [
			"AF"
		]
	},
	"Asia/Kamchatka": {
		u: 720,
		c: [
			"RU"
		]
	},
	"Asia/Karachi": {
		u: 300,
		c: [
			"PK"
		]
	},
	"Asia/Kashgar": {
		a: "Asia/Urumqi",
		r: 1
	},
	"Asia/Kathmandu": {
		u: 345,
		c: [
			"NP"
		]
	},
	"Asia/Katmandu": {
		a: "Asia/Kathmandu",
		r: 1
	},
	"Asia/Khandyga": {
		u: 540,
		c: [
			"RU"
		]
	},
	"Asia/Kolkata": {
		u: 330,
		c: [
			"IN"
		]
	},
	"Asia/Krasnoyarsk": {
		u: 420,
		c: [
			"RU"
		]
	},
	"Asia/Kuala_Lumpur": {
		a: "Asia/Singapore",
		c: [
			"MY"
		],
		r: 1
	},
	"Asia/Kuching": {
		u: 480,
		c: [
			"MY",
			"BN"
		]
	},
	"Asia/Kuwait": {
		a: "Asia/Riyadh",
		c: [
			"KW"
		],
		r: 1
	},
	"Asia/Macao": {
		a: "Asia/Macau",
		r: 1
	},
	"Asia/Macau": {
		u: 480,
		c: [
			"MO"
		]
	},
	"Asia/Magadan": {
		u: 660,
		c: [
			"RU"
		]
	},
	"Asia/Makassar": {
		u: 480,
		c: [
			"ID"
		]
	},
	"Asia/Manila": {
		u: 480,
		c: [
			"PH"
		]
	},
	"Asia/Muscat": {
		a: "Asia/Dubai",
		c: [
			"OM"
		],
		r: 1
	},
	"Asia/Nicosia": {
		u: 120,
		d: 180,
		c: [
			"CY"
		]
	},
	"Asia/Novokuznetsk": {
		u: 420,
		c: [
			"RU"
		]
	},
	"Asia/Novosibirsk": {
		u: 420,
		c: [
			"RU"
		]
	},
	"Asia/Omsk": {
		u: 360,
		c: [
			"RU"
		]
	},
	"Asia/Oral": {
		u: 300,
		c: [
			"KZ"
		]
	},
	"Asia/Phnom_Penh": {
		a: "Asia/Bangkok",
		c: [
			"KH"
		],
		r: 1
	},
	"Asia/Pontianak": {
		u: 420,
		c: [
			"ID"
		]
	},
	"Asia/Pyongyang": {
		u: 540,
		c: [
			"KP"
		]
	},
	"Asia/Qatar": {
		u: 180,
		c: [
			"QA",
			"BH"
		]
	},
	"Asia/Qostanay": {
		u: 300,
		c: [
			"KZ"
		]
	},
	"Asia/Qyzylorda": {
		u: 300,
		c: [
			"KZ"
		]
	},
	"Asia/Rangoon": {
		a: "Asia/Yangon",
		c: [
			"MM"
		],
		r: 1
	},
	"Asia/Riyadh": {
		u: 180,
		c: [
			"SA",
			"AQ",
			"KW",
			"YE"
		]
	},
	"Asia/Saigon": {
		a: "Asia/Ho_Chi_Minh",
		r: 1
	},
	"Asia/Sakhalin": {
		u: 660,
		c: [
			"RU"
		]
	},
	"Asia/Samarkand": {
		u: 300,
		c: [
			"UZ"
		]
	},
	"Asia/Seoul": {
		u: 540,
		c: [
			"KR"
		]
	},
	"Asia/Shanghai": {
		u: 480,
		c: [
			"CN"
		]
	},
	"Asia/Singapore": {
		u: 480,
		c: [
			"SG",
			"AQ",
			"MY"
		]
	},
	"Asia/Srednekolymsk": {
		u: 660,
		c: [
			"RU"
		]
	},
	"Asia/Taipei": {
		u: 480,
		c: [
			"TW"
		]
	},
	"Asia/Tashkent": {
		u: 300,
		c: [
			"UZ"
		]
	},
	"Asia/Tbilisi": {
		u: 240,
		c: [
			"GE"
		]
	},
	"Asia/Tehran": {
		u: 210,
		c: [
			"IR"
		]
	},
	"Asia/Tel_Aviv": {
		a: "Asia/Jerusalem",
		r: 1
	},
	"Asia/Thimbu": {
		a: "Asia/Thimphu",
		r: 1
	},
	"Asia/Thimphu": {
		u: 360,
		c: [
			"BT"
		]
	},
	"Asia/Tokyo": {
		u: 540,
		c: [
			"JP",
			"AU"
		]
	},
	"Asia/Tomsk": {
		u: 420,
		c: [
			"RU"
		]
	},
	"Asia/Ujung_Pandang": {
		a: "Asia/Makassar",
		r: 1
	},
	"Asia/Ulaanbaatar": {
		u: 480,
		c: [
			"MN"
		]
	},
	"Asia/Ulan_Bator": {
		a: "Asia/Ulaanbaatar",
		r: 1
	},
	"Asia/Urumqi": {
		u: 360,
		c: [
			"CN"
		]
	},
	"Asia/Ust-Nera": {
		u: 600,
		c: [
			"RU"
		]
	},
	"Asia/Vientiane": {
		a: "Asia/Bangkok",
		c: [
			"LA"
		],
		r: 1
	},
	"Asia/Vladivostok": {
		u: 600,
		c: [
			"RU"
		]
	},
	"Asia/Yakutsk": {
		u: 540,
		c: [
			"RU"
		]
	},
	"Asia/Yangon": {
		u: 390,
		c: [
			"MM",
			"CC"
		]
	},
	"Asia/Yekaterinburg": {
		u: 300,
		c: [
			"RU"
		]
	},
	"Asia/Yerevan": {
		u: 240,
		c: [
			"AM"
		]
	},
	"Atlantic/Azores": {
		u: -60,
		d: 0,
		c: [
			"PT"
		]
	},
	"Atlantic/Bermuda": {
		u: -240,
		d: -180,
		c: [
			"BM"
		]
	},
	"Atlantic/Canary": {
		u: 0,
		d: 60,
		c: [
			"ES"
		]
	},
	"Atlantic/Cape_Verde": {
		u: -60,
		c: [
			"CV"
		]
	},
	"Atlantic/Faeroe": {
		a: "Atlantic/Faroe",
		r: 1
	},
	"Atlantic/Faroe": {
		u: 0,
		d: 60,
		c: [
			"FO"
		]
	},
	"Atlantic/Jan_Mayen": {
		a: "Europe/Berlin",
		c: [
			"SJ"
		],
		r: 1
	},
	"Atlantic/Madeira": {
		u: 0,
		d: 60,
		c: [
			"PT"
		]
	},
	"Atlantic/Reykjavik": {
		a: "Africa/Abidjan",
		c: [
			"IS"
		],
		r: 1
	},
	"Atlantic/South_Georgia": {
		u: -120,
		c: [
			"GS"
		]
	},
	"Atlantic/St_Helena": {
		a: "Africa/Abidjan",
		c: [
			"SH"
		],
		r: 1
	},
	"Atlantic/Stanley": {
		u: -180,
		c: [
			"FK"
		]
	},
	"Australia/ACT": {
		a: "Australia/Sydney",
		r: 1
	},
	"Australia/Adelaide": {
		u: 570,
		d: 630,
		c: [
			"AU"
		]
	},
	"Australia/Brisbane": {
		u: 600,
		c: [
			"AU"
		]
	},
	"Australia/Broken_Hill": {
		u: 570,
		d: 630,
		c: [
			"AU"
		]
	},
	"Australia/Canberra": {
		a: "Australia/Sydney",
		r: 1
	},
	"Australia/Currie": {
		a: "Australia/Hobart",
		r: 1
	},
	"Australia/Darwin": {
		u: 570,
		c: [
			"AU"
		]
	},
	"Australia/Eucla": {
		u: 525,
		c: [
			"AU"
		]
	},
	"Australia/Hobart": {
		u: 600,
		d: 660,
		c: [
			"AU"
		]
	},
	"Australia/LHI": {
		a: "Australia/Lord_Howe",
		r: 1
	},
	"Australia/Lindeman": {
		u: 600,
		c: [
			"AU"
		]
	},
	"Australia/Lord_Howe": {
		u: 630,
		d: 660,
		c: [
			"AU"
		]
	},
	"Australia/Melbourne": {
		u: 600,
		d: 660,
		c: [
			"AU"
		]
	},
	"Australia/NSW": {
		a: "Australia/Sydney",
		r: 1
	},
	"Australia/North": {
		a: "Australia/Darwin",
		r: 1
	},
	"Australia/Perth": {
		u: 480,
		c: [
			"AU"
		]
	},
	"Australia/Queensland": {
		a: "Australia/Brisbane",
		r: 1
	},
	"Australia/South": {
		a: "Australia/Adelaide",
		r: 1
	},
	"Australia/Sydney": {
		u: 600,
		d: 660,
		c: [
			"AU"
		]
	},
	"Australia/Tasmania": {
		a: "Australia/Hobart",
		r: 1
	},
	"Australia/Victoria": {
		a: "Australia/Melbourne",
		r: 1
	},
	"Australia/West": {
		a: "Australia/Perth",
		r: 1
	},
	"Australia/Yancowinna": {
		a: "Australia/Broken_Hill",
		r: 1
	},
	"Brazil/Acre": {
		a: "America/Rio_Branco",
		r: 1
	},
	"Brazil/DeNoronha": {
		a: "America/Noronha",
		r: 1
	},
	"Brazil/East": {
		a: "America/Sao_Paulo",
		r: 1
	},
	"Brazil/West": {
		a: "America/Manaus",
		r: 1
	},
	CET: {
		a: "Europe/Brussels",
		r: 1
	},
	CST6CDT: {
		a: "America/Chicago",
		r: 1
	},
	"Canada/Atlantic": {
		a: "America/Halifax",
		r: 1
	},
	"Canada/Central": {
		a: "America/Winnipeg",
		r: 1
	},
	"Canada/Eastern": {
		a: "America/Toronto",
		c: [
			"CA"
		],
		r: 1
	},
	"Canada/Mountain": {
		a: "America/Edmonton",
		r: 1
	},
	"Canada/Newfoundland": {
		a: "America/St_Johns",
		r: 1
	},
	"Canada/Pacific": {
		a: "America/Vancouver",
		r: 1
	},
	"Canada/Saskatchewan": {
		a: "America/Regina",
		r: 1
	},
	"Canada/Yukon": {
		a: "America/Whitehorse",
		r: 1
	},
	"Chile/Continental": {
		a: "America/Santiago",
		r: 1
	},
	"Chile/EasterIsland": {
		a: "Pacific/Easter",
		r: 1
	},
	Cuba: {
		a: "America/Havana",
		r: 1
	},
	EET: {
		a: "Europe/Athens",
		r: 1
	},
	EST: {
		a: "America/Panama",
		r: 1
	},
	EST5EDT: {
		a: "America/New_York",
		r: 1
	},
	Egypt: {
		a: "Africa/Cairo",
		r: 1
	},
	Eire: {
		a: "Europe/Dublin",
		r: 1
	},
	"Etc/GMT": {
		u: 0
	},
	"Etc/GMT+0": {
		a: "Etc/GMT",
		r: 1
	},
	"Etc/GMT+1": {
		u: -60
	},
	"Etc/GMT+10": {
		u: -600
	},
	"Etc/GMT+11": {
		u: -660
	},
	"Etc/GMT+12": {
		u: -720
	},
	"Etc/GMT+2": {
		u: -120
	},
	"Etc/GMT+3": {
		u: -180
	},
	"Etc/GMT+4": {
		u: -240
	},
	"Etc/GMT+5": {
		u: -300
	},
	"Etc/GMT+6": {
		u: -360
	},
	"Etc/GMT+7": {
		u: -420
	},
	"Etc/GMT+8": {
		u: -480
	},
	"Etc/GMT+9": {
		u: -540
	},
	"Etc/GMT-0": {
		a: "Etc/GMT",
		r: 1
	},
	"Etc/GMT-1": {
		u: 60
	},
	"Etc/GMT-10": {
		u: 600
	},
	"Etc/GMT-11": {
		u: 660
	},
	"Etc/GMT-12": {
		u: 720
	},
	"Etc/GMT-13": {
		u: 780
	},
	"Etc/GMT-14": {
		u: 840
	},
	"Etc/GMT-2": {
		u: 120
	},
	"Etc/GMT-3": {
		u: 180
	},
	"Etc/GMT-4": {
		u: 240
	},
	"Etc/GMT-5": {
		u: 300
	},
	"Etc/GMT-6": {
		u: 360
	},
	"Etc/GMT-7": {
		u: 420
	},
	"Etc/GMT-8": {
		u: 480
	},
	"Etc/GMT-9": {
		u: 540
	},
	"Etc/GMT0": {
		a: "Etc/GMT",
		r: 1
	},
	"Etc/Greenwich": {
		a: "Etc/GMT",
		r: 1
	},
	"Etc/UCT": {
		a: "Etc/UTC",
		r: 1
	},
	"Etc/UTC": {
		u: 0
	},
	"Etc/Universal": {
		a: "Etc/UTC",
		r: 1
	},
	"Etc/Zulu": {
		a: "Etc/UTC",
		r: 1
	},
	"Europe/Amsterdam": {
		a: "Europe/Brussels",
		c: [
			"NL"
		],
		r: 1
	},
	"Europe/Andorra": {
		u: 60,
		d: 120,
		c: [
			"AD"
		]
	},
	"Europe/Astrakhan": {
		u: 240,
		c: [
			"RU"
		]
	},
	"Europe/Athens": {
		u: 120,
		d: 180,
		c: [
			"GR"
		]
	},
	"Europe/Belfast": {
		a: "Europe/London",
		c: [
			"GB"
		],
		r: 1
	},
	"Europe/Belgrade": {
		u: 60,
		d: 120,
		c: [
			"RS",
			"BA",
			"HR",
			"ME",
			"MK",
			"SI"
		]
	},
	"Europe/Berlin": {
		u: 60,
		d: 120,
		c: [
			"DE",
			"DK",
			"NO",
			"SE",
			"SJ"
		]
	},
	"Europe/Bratislava": {
		a: "Europe/Prague",
		c: [
			"SK"
		],
		r: 1
	},
	"Europe/Brussels": {
		u: 60,
		d: 120,
		c: [
			"BE",
			"LU",
			"NL"
		]
	},
	"Europe/Bucharest": {
		u: 120,
		d: 180,
		c: [
			"RO"
		]
	},
	"Europe/Budapest": {
		u: 60,
		d: 120,
		c: [
			"HU"
		]
	},
	"Europe/Busingen": {
		a: "Europe/Zurich",
		c: [
			"DE"
		],
		r: 1
	},
	"Europe/Chisinau": {
		u: 120,
		d: 180,
		c: [
			"MD"
		]
	},
	"Europe/Copenhagen": {
		a: "Europe/Berlin",
		c: [
			"DK"
		],
		r: 1
	},
	"Europe/Dublin": {
		u: 0,
		d: 60,
		c: [
			"IE"
		]
	},
	"Europe/Gibraltar": {
		u: 60,
		d: 120,
		c: [
			"GI"
		]
	},
	"Europe/Guernsey": {
		a: "Europe/London",
		c: [
			"GG"
		],
		r: 1
	},
	"Europe/Helsinki": {
		u: 120,
		d: 180,
		c: [
			"FI",
			"AX"
		]
	},
	"Europe/Isle_of_Man": {
		a: "Europe/London",
		c: [
			"IM"
		],
		r: 1
	},
	"Europe/Istanbul": {
		u: 180,
		c: [
			"TR"
		]
	},
	"Europe/Jersey": {
		a: "Europe/London",
		c: [
			"JE"
		],
		r: 1
	},
	"Europe/Kaliningrad": {
		u: 120,
		c: [
			"RU"
		]
	},
	"Europe/Kiev": {
		a: "Europe/Kyiv",
		r: 1
	},
	"Europe/Kirov": {
		u: 180,
		c: [
			"RU"
		]
	},
	"Europe/Kyiv": {
		u: 120,
		d: 180,
		c: [
			"UA"
		]
	},
	"Europe/Lisbon": {
		u: 0,
		d: 60,
		c: [
			"PT"
		]
	},
	"Europe/Ljubljana": {
		a: "Europe/Belgrade",
		c: [
			"SI"
		],
		r: 1
	},
	"Europe/London": {
		u: 0,
		d: 60,
		c: [
			"GB",
			"GG",
			"IM",
			"JE"
		]
	},
	"Europe/Luxembourg": {
		a: "Europe/Brussels",
		c: [
			"LU"
		],
		r: 1
	},
	"Europe/Madrid": {
		u: 60,
		d: 120,
		c: [
			"ES"
		]
	},
	"Europe/Malta": {
		u: 60,
		d: 120,
		c: [
			"MT"
		]
	},
	"Europe/Mariehamn": {
		a: "Europe/Helsinki",
		c: [
			"AX"
		],
		r: 1
	},
	"Europe/Minsk": {
		u: 180,
		c: [
			"BY"
		]
	},
	"Europe/Monaco": {
		a: "Europe/Paris",
		c: [
			"MC"
		],
		r: 1
	},
	"Europe/Moscow": {
		u: 180,
		c: [
			"RU"
		]
	},
	"Europe/Nicosia": {
		a: "Asia/Nicosia",
		r: 1
	},
	"Europe/Oslo": {
		a: "Europe/Berlin",
		c: [
			"NO"
		],
		r: 1
	},
	"Europe/Paris": {
		u: 60,
		d: 120,
		c: [
			"FR",
			"MC"
		]
	},
	"Europe/Podgorica": {
		a: "Europe/Belgrade",
		c: [
			"ME"
		],
		r: 1
	},
	"Europe/Prague": {
		u: 60,
		d: 120,
		c: [
			"CZ",
			"SK"
		]
	},
	"Europe/Riga": {
		u: 120,
		d: 180,
		c: [
			"LV"
		]
	},
	"Europe/Rome": {
		u: 60,
		d: 120,
		c: [
			"IT",
			"SM",
			"VA"
		]
	},
	"Europe/Samara": {
		u: 240,
		c: [
			"RU"
		]
	},
	"Europe/San_Marino": {
		a: "Europe/Rome",
		c: [
			"SM"
		],
		r: 1
	},
	"Europe/Sarajevo": {
		a: "Europe/Belgrade",
		c: [
			"BA"
		],
		r: 1
	},
	"Europe/Saratov": {
		u: 240,
		c: [
			"RU"
		]
	},
	"Europe/Simferopol": {
		u: 180,
		c: [
			"RU",
			"UA"
		]
	},
	"Europe/Skopje": {
		a: "Europe/Belgrade",
		c: [
			"MK"
		],
		r: 1
	},
	"Europe/Sofia": {
		u: 120,
		d: 180,
		c: [
			"BG"
		]
	},
	"Europe/Stockholm": {
		a: "Europe/Berlin",
		c: [
			"SE"
		],
		r: 1
	},
	"Europe/Tallinn": {
		u: 120,
		d: 180,
		c: [
			"EE"
		]
	},
	"Europe/Tirane": {
		u: 60,
		d: 120,
		c: [
			"AL"
		]
	},
	"Europe/Tiraspol": {
		a: "Europe/Chisinau",
		r: 1
	},
	"Europe/Ulyanovsk": {
		u: 240,
		c: [
			"RU"
		]
	},
	"Europe/Uzhgorod": {
		a: "Europe/Kyiv",
		r: 1
	},
	"Europe/Vaduz": {
		a: "Europe/Zurich",
		c: [
			"LI"
		],
		r: 1
	},
	"Europe/Vatican": {
		a: "Europe/Rome",
		c: [
			"VA"
		],
		r: 1
	},
	"Europe/Vienna": {
		u: 60,
		d: 120,
		c: [
			"AT"
		]
	},
	"Europe/Vilnius": {
		u: 120,
		d: 180,
		c: [
			"LT"
		]
	},
	"Europe/Volgograd": {
		u: 180,
		c: [
			"RU"
		]
	},
	"Europe/Warsaw": {
		u: 60,
		d: 120,
		c: [
			"PL"
		]
	},
	"Europe/Zagreb": {
		a: "Europe/Belgrade",
		c: [
			"HR"
		],
		r: 1
	},
	"Europe/Zaporozhye": {
		a: "Europe/Kyiv",
		r: 1
	},
	"Europe/Zurich": {
		u: 60,
		d: 120,
		c: [
			"CH",
			"DE",
			"LI"
		]
	},
	Factory: {
		u: 0
	},
	GB: {
		a: "Europe/London",
		c: [
			"GB"
		],
		r: 1
	},
	"GB-Eire": {
		a: "Europe/London",
		c: [
			"GB"
		],
		r: 1
	},
	GMT: {
		a: "Etc/GMT",
		r: 1
	},
	"GMT+0": {
		a: "Etc/GMT",
		r: 1
	},
	"GMT-0": {
		a: "Etc/GMT",
		r: 1
	},
	GMT0: {
		a: "Etc/GMT",
		r: 1
	},
	Greenwich: {
		a: "Etc/GMT",
		r: 1
	},
	HST: {
		a: "Pacific/Honolulu",
		r: 1
	},
	Hongkong: {
		a: "Asia/Hong_Kong",
		r: 1
	},
	Iceland: {
		a: "Africa/Abidjan",
		c: [
			"IS"
		],
		r: 1
	},
	"Indian/Antananarivo": {
		a: "Africa/Nairobi",
		c: [
			"MG"
		],
		r: 1
	},
	"Indian/Chagos": {
		u: 360,
		c: [
			"IO"
		]
	},
	"Indian/Christmas": {
		a: "Asia/Bangkok",
		c: [
			"CX"
		],
		r: 1
	},
	"Indian/Cocos": {
		a: "Asia/Yangon",
		c: [
			"CC"
		],
		r: 1
	},
	"Indian/Comoro": {
		a: "Africa/Nairobi",
		c: [
			"KM"
		],
		r: 1
	},
	"Indian/Kerguelen": {
		a: "Indian/Maldives",
		c: [
			"TF"
		],
		r: 1
	},
	"Indian/Mahe": {
		a: "Asia/Dubai",
		c: [
			"SC"
		],
		r: 1
	},
	"Indian/Maldives": {
		u: 300,
		c: [
			"MV",
			"TF"
		]
	},
	"Indian/Mauritius": {
		u: 240,
		c: [
			"MU"
		]
	},
	"Indian/Mayotte": {
		a: "Africa/Nairobi",
		c: [
			"YT"
		],
		r: 1
	},
	"Indian/Reunion": {
		a: "Asia/Dubai",
		c: [
			"RE"
		],
		r: 1
	},
	Iran: {
		a: "Asia/Tehran",
		r: 1
	},
	Israel: {
		a: "Asia/Jerusalem",
		r: 1
	},
	Jamaica: {
		a: "America/Jamaica",
		r: 1
	},
	Japan: {
		a: "Asia/Tokyo",
		c: [
			"JP"
		],
		r: 1
	},
	Kwajalein: {
		a: "Pacific/Kwajalein",
		r: 1
	},
	Libya: {
		a: "Africa/Tripoli",
		r: 1
	},
	MET: {
		a: "Europe/Brussels",
		r: 1
	},
	MST: {
		a: "America/Phoenix",
		r: 1
	},
	MST7MDT: {
		a: "America/Denver",
		r: 1
	},
	"Mexico/BajaNorte": {
		a: "America/Tijuana",
		r: 1
	},
	"Mexico/BajaSur": {
		a: "America/Mazatlan",
		r: 1
	},
	"Mexico/General": {
		a: "America/Mexico_City",
		r: 1
	},
	NZ: {
		a: "Pacific/Auckland",
		c: [
			"NZ"
		],
		r: 1
	},
	"NZ-CHAT": {
		a: "Pacific/Chatham",
		r: 1
	},
	Navajo: {
		a: "America/Denver",
		r: 1
	},
	PRC: {
		a: "Asia/Shanghai",
		r: 1
	},
	PST8PDT: {
		a: "America/Los_Angeles",
		r: 1
	},
	"Pacific/Apia": {
		u: 780,
		c: [
			"WS"
		]
	},
	"Pacific/Auckland": {
		u: 720,
		d: 780,
		c: [
			"NZ",
			"AQ"
		]
	},
	"Pacific/Bougainville": {
		u: 660,
		c: [
			"PG"
		]
	},
	"Pacific/Chatham": {
		u: 765,
		d: 825,
		c: [
			"NZ"
		]
	},
	"Pacific/Chuuk": {
		a: "Pacific/Port_Moresby",
		c: [
			"FM"
		],
		r: 1
	},
	"Pacific/Easter": {
		u: -360,
		d: -300,
		c: [
			"CL"
		]
	},
	"Pacific/Efate": {
		u: 660,
		c: [
			"VU"
		]
	},
	"Pacific/Enderbury": {
		a: "Pacific/Kanton",
		r: 1
	},
	"Pacific/Fakaofo": {
		u: 780,
		c: [
			"TK"
		]
	},
	"Pacific/Fiji": {
		u: 720,
		c: [
			"FJ"
		]
	},
	"Pacific/Funafuti": {
		a: "Pacific/Tarawa",
		c: [
			"TV"
		],
		r: 1
	},
	"Pacific/Galapagos": {
		u: -360,
		c: [
			"EC"
		]
	},
	"Pacific/Gambier": {
		u: -540,
		c: [
			"PF"
		]
	},
	"Pacific/Guadalcanal": {
		u: 660,
		c: [
			"SB",
			"FM"
		]
	},
	"Pacific/Guam": {
		u: 600,
		c: [
			"GU",
			"MP"
		]
	},
	"Pacific/Honolulu": {
		u: -600,
		c: [
			"US"
		]
	},
	"Pacific/Johnston": {
		a: "Pacific/Honolulu",
		c: [
			"UM"
		],
		r: 1
	},
	"Pacific/Kanton": {
		u: 780,
		c: [
			"KI"
		]
	},
	"Pacific/Kiritimati": {
		u: 840,
		c: [
			"KI"
		]
	},
	"Pacific/Kosrae": {
		u: 660,
		c: [
			"FM"
		]
	},
	"Pacific/Kwajalein": {
		u: 720,
		c: [
			"MH"
		]
	},
	"Pacific/Majuro": {
		a: "Pacific/Tarawa",
		c: [
			"MH"
		],
		r: 1
	},
	"Pacific/Marquesas": {
		u: -570,
		c: [
			"PF"
		]
	},
	"Pacific/Midway": {
		a: "Pacific/Pago_Pago",
		c: [
			"UM"
		],
		r: 1
	},
	"Pacific/Nauru": {
		u: 720,
		c: [
			"NR"
		]
	},
	"Pacific/Niue": {
		u: -660,
		c: [
			"NU"
		]
	},
	"Pacific/Norfolk": {
		u: 660,
		d: 720,
		c: [
			"NF"
		]
	},
	"Pacific/Noumea": {
		u: 660,
		c: [
			"NC"
		]
	},
	"Pacific/Pago_Pago": {
		u: -660,
		c: [
			"AS",
			"UM"
		]
	},
	"Pacific/Palau": {
		u: 540,
		c: [
			"PW"
		]
	},
	"Pacific/Pitcairn": {
		u: -480,
		c: [
			"PN"
		]
	},
	"Pacific/Pohnpei": {
		a: "Pacific/Guadalcanal",
		c: [
			"FM"
		],
		r: 1
	},
	"Pacific/Ponape": {
		a: "Pacific/Guadalcanal",
		c: [
			"FM"
		],
		r: 1
	},
	"Pacific/Port_Moresby": {
		u: 600,
		c: [
			"PG",
			"AQ",
			"FM"
		]
	},
	"Pacific/Rarotonga": {
		u: -600,
		c: [
			"CK"
		]
	},
	"Pacific/Saipan": {
		a: "Pacific/Guam",
		c: [
			"MP"
		],
		r: 1
	},
	"Pacific/Samoa": {
		a: "Pacific/Pago_Pago",
		c: [
			"AS"
		],
		r: 1
	},
	"Pacific/Tahiti": {
		u: -600,
		c: [
			"PF"
		]
	},
	"Pacific/Tarawa": {
		u: 720,
		c: [
			"KI",
			"MH",
			"TV",
			"UM",
			"WF"
		]
	},
	"Pacific/Tongatapu": {
		u: 780,
		c: [
			"TO"
		]
	},
	"Pacific/Truk": {
		a: "Pacific/Port_Moresby",
		c: [
			"FM"
		],
		r: 1
	},
	"Pacific/Wake": {
		a: "Pacific/Tarawa",
		c: [
			"UM"
		],
		r: 1
	},
	"Pacific/Wallis": {
		a: "Pacific/Tarawa",
		c: [
			"WF"
		],
		r: 1
	},
	"Pacific/Yap": {
		a: "Pacific/Port_Moresby",
		c: [
			"FM"
		],
		r: 1
	},
	Poland: {
		a: "Europe/Warsaw",
		r: 1
	},
	Portugal: {
		a: "Europe/Lisbon",
		r: 1
	},
	ROC: {
		a: "Asia/Taipei",
		r: 1
	},
	ROK: {
		a: "Asia/Seoul",
		r: 1
	},
	Singapore: {
		a: "Asia/Singapore",
		c: [
			"SG"
		],
		r: 1
	},
	Turkey: {
		a: "Europe/Istanbul",
		r: 1
	},
	UCT: {
		a: "Etc/UTC",
		r: 1
	},
	"US/Alaska": {
		a: "America/Anchorage",
		r: 1
	},
	"US/Aleutian": {
		a: "America/Adak",
		r: 1
	},
	"US/Arizona": {
		a: "America/Phoenix",
		c: [
			"US"
		],
		r: 1
	},
	"US/Central": {
		a: "America/Chicago",
		r: 1
	},
	"US/East-Indiana": {
		a: "America/Indiana/Indianapolis",
		r: 1
	},
	"US/Eastern": {
		a: "America/New_York",
		r: 1
	},
	"US/Hawaii": {
		a: "Pacific/Honolulu",
		r: 1
	},
	"US/Indiana-Starke": {
		a: "America/Indiana/Knox",
		r: 1
	},
	"US/Michigan": {
		a: "America/Detroit",
		r: 1
	},
	"US/Mountain": {
		a: "America/Denver",
		r: 1
	},
	"US/Pacific": {
		a: "America/Los_Angeles",
		r: 1
	},
	"US/Samoa": {
		a: "Pacific/Pago_Pago",
		c: [
			"AS"
		],
		r: 1
	},
	UTC: {
		a: "Etc/UTC",
		r: 1
	},
	Universal: {
		a: "Etc/UTC",
		r: 1
	},
	"W-SU": {
		a: "Europe/Moscow",
		r: 1
	},
	WET: {
		a: "Europe/Lisbon",
		r: 1
	},
	Zulu: {
		a: "Etc/UTC",
		r: 1
	}
};
var data = {
	countries: countries$1,
	timezones: timezones$1
};

var timezonesMap;
function buildCountry(data, id) {
  var name = data.countries[id];
  if (!name) return null;
  var tzMap = getTimezonesMap(data)[id] || {};
  return {
    id: id,
    name: name,
    timezones: tzMap.current || [],
    allTimezones: tzMap.all || []
  };
}
function getTimezonesMap(data) {
  timezonesMap || (timezonesMap = buildTimezonesMap(data));
  return timezonesMap;
}
function buildTimezonesMap(data) {
  return Object.keys(data.timezones).reduce(function (result, id) {
    var tz = data.timezones[id];
    var c = tz.c,
      a = tz.a;
    var aliasTz = data.timezones[a] || {};
    var countries = c || aliasTz.c;
    if (!countries) return result;
    var _iterator = _createForOfIteratorHelper(countries),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var country = _step.value;
        if (!result[country]) Object.assign(result, _defineProperty({}, country, {
          current: [],
          all: []
        }));
        if (tz.r === undefined) result[country].current.push(id);
        result[country].all.push(id);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
    return result;
  }, {});
}

function buildTimezone(data, name) {
  var timezone = data.timezones[name];
  if (!timezone) return null;
  var _timezone$a = timezone.a,
    aliasOf = _timezone$a === void 0 ? null : _timezone$a;
  var aliasTz = aliasOf ? data.timezones[aliasOf] : {};
  var tz = _objectSpread2(_objectSpread2({}, aliasTz), data.timezones[name]);
  var countries = tz.c || [];
  var utcOffset = tz.u;
  var dstOffset = Number.isInteger(tz.d) ? tz.d : utcOffset;
  var result = {
    name: name,
    countries: countries,
    utcOffset: utcOffset,
    utcOffsetStr: getOffsetString(utcOffset),
    dstOffset: dstOffset,
    dstOffsetStr: getOffsetString(dstOffset),
    aliasOf: aliasOf
  };
  if (timezone.r) result.deprecated = true;
  return result;
}
function getOffsetString(offset) {
  var hours = Math.floor(Math.abs(offset) / 60);
  var min = offset % 60;
  var sign = offset < 0 ? '-' : '+';
  return "".concat(sign).concat(getNumberString(hours), ":").concat(getNumberString(min));
}
function getNumberString(input) {
  var number_ = Math.abs(input);
  var prefix = number_ < 10 ? '0' : '';
  return "".concat(prefix).concat(number_);
}

var _excluded = ["allTimezones"];
Object.keys(data.timezones).length;
var countries = {};
var timezones = {};
function getAllCountries() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  return Object.keys(data.countries).reduce(function (previous, id) {
    return Object.assign(previous, _defineProperty({}, id, getCountry(id, options)));
  }, {});
}
function getCountry(id) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  if (!countries[id]) memoizeCountry(buildCountry(data, id));
  return deliverCountry(countries[id], options);
}
function memoizeCountry(country) {
  if (!country) return;
  countries[country.id] = country;
}
function getTimezone(name) {
  if (!timezones[name]) memoizeTimezone(buildTimezone(data, name));
  return timezones[name] ? _objectSpread2({}, timezones[name]) : null;
}
function memoizeTimezone(timezone) {
  if (!timezone) return;
  timezones[timezone.name] = timezone;
  Object.keys(timezone).length;
}
function getCountriesForTimezone(tzName) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var timezone = getTimezone(tzName) || {};
  var values = timezone.countries || [];
  return values.map(function (c) {
    return getCountry(c, options);
  });
}
function getCountryForTimezone(tzName) {
  var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  var _getCountriesForTimez = getCountriesForTimezone(tzName, options),
    _getCountriesForTimez2 = _slicedToArray(_getCountriesForTimez, 1),
    main = _getCountriesForTimez2[0];
  return main || null;
}
function deliverCountry(country, options) {
  if (!country) return null;
  var _ref2 = options || {},
    deprecated = _ref2.deprecated;
  country.allTimezones;
    var other = _objectWithoutProperties(country, _excluded);
  var tz = deprecated ? country.allTimezones : country.timezones;
  return _objectSpread2(_objectSpread2({}, other), {}, {
    timezones: tz
  });
}

function getCountries() {
    const countries = Object.values(getAllCountries()).map((country) => ({
        code: country.id,
        country: country.name,
    }));
    countries.push({ code: 'EU', country: 'Europe' });
    return countries;
}
const getCountryFromIp = async () => {
    try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        return data.country;
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        return null;
    }
};
const getCountryFromBrowser = async () => {
    try {
        // check if the country is provided in query params
        const urlParams = new URLSearchParams(window.location.search);
        const countryFromQuery = urlParams.get('region');
        if (countryFromQuery) {
            // check if the country is valid
            const validCountry = getCountries().find((country) => country.code === countryFromQuery);
            if (validCountry) {
                return countryFromQuery;
            }
        }
        // check if the country is already in the local storage
        const countryFromLocalStorage = localStorage.getItem('brainfish-user-country');
        if (countryFromLocalStorage) {
            return countryFromLocalStorage;
        }
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        // get country from timezone if supported by the browser
        if (!timezone) {
            // if country is not found, get country from IP
            const countryFromIp = await getCountryFromIp();
            return countryFromIp;
        }
        const countryFromBrowser = getCountryForTimezone(timezone);
        const country = countryFromBrowser?.id;
        return country || null;
    }
    catch (error) {
        // eslint-disable-next-line no-console
        console.log(error);
        return null;
    }
};

function _extends() {
  return _extends = Object.assign ? Object.assign.bind() : function (n) {
    for (var e = 1; e < arguments.length; e++) {
      var t = arguments[e];
      for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]);
    }
    return n;
  }, _extends.apply(null, arguments);
}

function _assertThisInitialized(e) {
  if (void 0 === e) throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  return e;
}

function _setPrototypeOf(t, e) {
  return _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function (t, e) {
    return t.__proto__ = e, t;
  }, _setPrototypeOf(t, e);
}

function _inheritsLoose(t, o) {
  t.prototype = Object.create(o.prototype), t.prototype.constructor = t, _setPrototypeOf(t, o);
}

function _getPrototypeOf(t) {
  return _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function (t) {
    return t.__proto__ || Object.getPrototypeOf(t);
  }, _getPrototypeOf(t);
}

function _isNativeFunction(t) {
  try {
    return -1 !== Function.toString.call(t).indexOf("[native code]");
  } catch (n) {
    return "function" == typeof t;
  }
}

function _isNativeReflectConstruct() {
  try {
    var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
  } catch (t) {}
  return (_isNativeReflectConstruct = function _isNativeReflectConstruct() {
    return !!t;
  })();
}

function _construct(t, e, r) {
  if (_isNativeReflectConstruct()) return Reflect.construct.apply(null, arguments);
  var o = [null];
  o.push.apply(o, e);
  var p = new (t.bind.apply(t, o))();
  return r && _setPrototypeOf(p, r.prototype), p;
}

function _wrapNativeSuper(t) {
  var r = "function" == typeof Map ? new Map() : void 0;
  return _wrapNativeSuper = function _wrapNativeSuper(t) {
    if (null === t || !_isNativeFunction(t)) return t;
    if ("function" != typeof t) throw new TypeError("Super expression must either be null or a function");
    if (void 0 !== r) {
      if (r.has(t)) return r.get(t);
      r.set(t, Wrapper);
    }
    function Wrapper() {
      return _construct(t, arguments, _getPrototypeOf(this).constructor);
    }
    return Wrapper.prototype = Object.create(t.prototype, {
      constructor: {
        value: Wrapper,
        enumerable: false,
        writable: true,
        configurable: true
      }
    }), _setPrototypeOf(Wrapper, t);
  }, _wrapNativeSuper(t);
}

// based on https://github.com/styled-components/styled-components/blob/fcf6f3804c57a14dd7984dfab7bc06ee2edca044/src/utils/error.js
/**
 * Parse errors.md and turn it into a simple hash of code: message
 * @private
 */
var ERRORS = {
  "1": "Passed invalid arguments to hsl, please pass multiple numbers e.g. hsl(360, 0.75, 0.4) or an object e.g. rgb({ hue: 255, saturation: 0.4, lightness: 0.75 }).\n\n",
  "2": "Passed invalid arguments to hsla, please pass multiple numbers e.g. hsla(360, 0.75, 0.4, 0.7) or an object e.g. rgb({ hue: 255, saturation: 0.4, lightness: 0.75, alpha: 0.7 }).\n\n",
  "3": "Passed an incorrect argument to a color function, please pass a string representation of a color.\n\n",
  "4": "Couldn't generate valid rgb string from %s, it returned %s.\n\n",
  "5": "Couldn't parse the color string. Please provide the color as a string in hex, rgb, rgba, hsl or hsla notation.\n\n",
  "6": "Passed invalid arguments to rgb, please pass multiple numbers e.g. rgb(255, 205, 100) or an object e.g. rgb({ red: 255, green: 205, blue: 100 }).\n\n",
  "7": "Passed invalid arguments to rgba, please pass multiple numbers e.g. rgb(255, 205, 100, 0.75) or an object e.g. rgb({ red: 255, green: 205, blue: 100, alpha: 0.75 }).\n\n",
  "8": "Passed invalid argument to toColorString, please pass a RgbColor, RgbaColor, HslColor or HslaColor object.\n\n",
  "9": "Please provide a number of steps to the modularScale helper.\n\n",
  "10": "Please pass a number or one of the predefined scales to the modularScale helper as the ratio.\n\n",
  "11": "Invalid value passed as base to modularScale, expected number or em string but got \"%s\"\n\n",
  "12": "Expected a string ending in \"px\" or a number passed as the first argument to %s(), got \"%s\" instead.\n\n",
  "13": "Expected a string ending in \"px\" or a number passed as the second argument to %s(), got \"%s\" instead.\n\n",
  "14": "Passed invalid pixel value (\"%s\") to %s(), please pass a value like \"12px\" or 12.\n\n",
  "15": "Passed invalid base value (\"%s\") to %s(), please pass a value like \"12px\" or 12.\n\n",
  "16": "You must provide a template to this method.\n\n",
  "17": "You passed an unsupported selector state to this method.\n\n",
  "18": "minScreen and maxScreen must be provided as stringified numbers with the same units.\n\n",
  "19": "fromSize and toSize must be provided as stringified numbers with the same units.\n\n",
  "20": "expects either an array of objects or a single object with the properties prop, fromSize, and toSize.\n\n",
  "21": "expects the objects in the first argument array to have the properties `prop`, `fromSize`, and `toSize`.\n\n",
  "22": "expects the first argument object to have the properties `prop`, `fromSize`, and `toSize`.\n\n",
  "23": "fontFace expects a name of a font-family.\n\n",
  "24": "fontFace expects either the path to the font file(s) or a name of a local copy.\n\n",
  "25": "fontFace expects localFonts to be an array.\n\n",
  "26": "fontFace expects fileFormats to be an array.\n\n",
  "27": "radialGradient requries at least 2 color-stops to properly render.\n\n",
  "28": "Please supply a filename to retinaImage() as the first argument.\n\n",
  "29": "Passed invalid argument to triangle, please pass correct pointingDirection e.g. 'right'.\n\n",
  "30": "Passed an invalid value to `height` or `width`. Please provide a pixel based unit.\n\n",
  "31": "The animation shorthand only takes 8 arguments. See the specification for more information: http://mdn.io/animation\n\n",
  "32": "To pass multiple animations please supply them in arrays, e.g. animation(['rotate', '2s'], ['move', '1s'])\nTo pass a single animation please supply them in simple values, e.g. animation('rotate', '2s')\n\n",
  "33": "The animation shorthand arrays can only have 8 elements. See the specification for more information: http://mdn.io/animation\n\n",
  "34": "borderRadius expects a radius value as a string or number as the second argument.\n\n",
  "35": "borderRadius expects one of \"top\", \"bottom\", \"left\" or \"right\" as the first argument.\n\n",
  "36": "Property must be a string value.\n\n",
  "37": "Syntax Error at %s.\n\n",
  "38": "Formula contains a function that needs parentheses at %s.\n\n",
  "39": "Formula is missing closing parenthesis at %s.\n\n",
  "40": "Formula has too many closing parentheses at %s.\n\n",
  "41": "All values in a formula must have the same unit or be unitless.\n\n",
  "42": "Please provide a number of steps to the modularScale helper.\n\n",
  "43": "Please pass a number or one of the predefined scales to the modularScale helper as the ratio.\n\n",
  "44": "Invalid value passed as base to modularScale, expected number or em/rem string but got %s.\n\n",
  "45": "Passed invalid argument to hslToColorString, please pass a HslColor or HslaColor object.\n\n",
  "46": "Passed invalid argument to rgbToColorString, please pass a RgbColor or RgbaColor object.\n\n",
  "47": "minScreen and maxScreen must be provided as stringified numbers with the same units.\n\n",
  "48": "fromSize and toSize must be provided as stringified numbers with the same units.\n\n",
  "49": "Expects either an array of objects or a single object with the properties prop, fromSize, and toSize.\n\n",
  "50": "Expects the objects in the first argument array to have the properties prop, fromSize, and toSize.\n\n",
  "51": "Expects the first argument object to have the properties prop, fromSize, and toSize.\n\n",
  "52": "fontFace expects either the path to the font file(s) or a name of a local copy.\n\n",
  "53": "fontFace expects localFonts to be an array.\n\n",
  "54": "fontFace expects fileFormats to be an array.\n\n",
  "55": "fontFace expects a name of a font-family.\n\n",
  "56": "linearGradient requries at least 2 color-stops to properly render.\n\n",
  "57": "radialGradient requries at least 2 color-stops to properly render.\n\n",
  "58": "Please supply a filename to retinaImage() as the first argument.\n\n",
  "59": "Passed invalid argument to triangle, please pass correct pointingDirection e.g. 'right'.\n\n",
  "60": "Passed an invalid value to `height` or `width`. Please provide a pixel based unit.\n\n",
  "61": "Property must be a string value.\n\n",
  "62": "borderRadius expects a radius value as a string or number as the second argument.\n\n",
  "63": "borderRadius expects one of \"top\", \"bottom\", \"left\" or \"right\" as the first argument.\n\n",
  "64": "The animation shorthand only takes 8 arguments. See the specification for more information: http://mdn.io/animation.\n\n",
  "65": "To pass multiple animations please supply them in arrays, e.g. animation(['rotate', '2s'], ['move', '1s'])\\nTo pass a single animation please supply them in simple values, e.g. animation('rotate', '2s').\n\n",
  "66": "The animation shorthand arrays can only have 8 elements. See the specification for more information: http://mdn.io/animation.\n\n",
  "67": "You must provide a template to this method.\n\n",
  "68": "You passed an unsupported selector state to this method.\n\n",
  "69": "Expected a string ending in \"px\" or a number passed as the first argument to %s(), got %s instead.\n\n",
  "70": "Expected a string ending in \"px\" or a number passed as the second argument to %s(), got %s instead.\n\n",
  "71": "Passed invalid pixel value %s to %s(), please pass a value like \"12px\" or 12.\n\n",
  "72": "Passed invalid base value %s to %s(), please pass a value like \"12px\" or 12.\n\n",
  "73": "Please provide a valid CSS variable.\n\n",
  "74": "CSS variable not found and no default was provided.\n\n",
  "75": "important requires a valid style object, got a %s instead.\n\n",
  "76": "fromSize and toSize must be provided as stringified numbers with the same units as minScreen and maxScreen.\n\n",
  "77": "remToPx expects a value in \"rem\" but you provided it in \"%s\".\n\n",
  "78": "base must be set in \"px\" or \"%\" but you set it in \"%s\".\n"
};

/**
 * super basic version of sprintf
 * @private
 */
function format() {
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }
  var a = args[0];
  var b = [];
  var c;
  for (c = 1; c < args.length; c += 1) {
    b.push(args[c]);
  }
  b.forEach(function (d) {
    a = a.replace(/%[a-z]/, d);
  });
  return a;
}

/**
 * Create an error file out of errors.md for development and a simple web link to the full errors
 * in production mode.
 * @private
 */
var PolishedError = /*#__PURE__*/function (_Error) {
  _inheritsLoose(PolishedError, _Error);
  function PolishedError(code) {
    var _this;
    if (process.env.NODE_ENV === 'production') {
      _this = _Error.call(this, "An error occurred. See https://github.com/styled-components/polished/blob/main/src/internalHelpers/errors.md#" + code + " for more information.") || this;
    } else {
      for (var _len2 = arguments.length, args = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        args[_key2 - 1] = arguments[_key2];
      }
      _this = _Error.call(this, format.apply(void 0, [ERRORS[code]].concat(args))) || this;
    }
    return _assertThisInitialized(_this);
  }
  return PolishedError;
}( /*#__PURE__*/_wrapNativeSuper(Error));

function colorToInt(color) {
  return Math.round(color * 255);
}
function convertToInt(red, green, blue) {
  return colorToInt(red) + "," + colorToInt(green) + "," + colorToInt(blue);
}
function hslToRgb(hue, saturation, lightness, convert) {
  if (convert === void 0) {
    convert = convertToInt;
  }
  if (saturation === 0) {
    // achromatic
    return convert(lightness, lightness, lightness);
  }

  // formulae from https://en.wikipedia.org/wiki/HSL_and_HSV
  var huePrime = (hue % 360 + 360) % 360 / 60;
  var chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  var secondComponent = chroma * (1 - Math.abs(huePrime % 2 - 1));
  var red = 0;
  var green = 0;
  var blue = 0;
  if (huePrime >= 0 && huePrime < 1) {
    red = chroma;
    green = secondComponent;
  } else if (huePrime >= 1 && huePrime < 2) {
    red = secondComponent;
    green = chroma;
  } else if (huePrime >= 2 && huePrime < 3) {
    green = chroma;
    blue = secondComponent;
  } else if (huePrime >= 3 && huePrime < 4) {
    green = secondComponent;
    blue = chroma;
  } else if (huePrime >= 4 && huePrime < 5) {
    red = secondComponent;
    blue = chroma;
  } else if (huePrime >= 5 && huePrime < 6) {
    red = chroma;
    blue = secondComponent;
  }
  var lightnessModification = lightness - chroma / 2;
  var finalRed = red + lightnessModification;
  var finalGreen = green + lightnessModification;
  var finalBlue = blue + lightnessModification;
  return convert(finalRed, finalGreen, finalBlue);
}

var namedColorMap = {
  aliceblue: 'f0f8ff',
  antiquewhite: 'faebd7',
  aqua: '00ffff',
  aquamarine: '7fffd4',
  azure: 'f0ffff',
  beige: 'f5f5dc',
  bisque: 'ffe4c4',
  black: '000',
  blanchedalmond: 'ffebcd',
  blue: '0000ff',
  blueviolet: '8a2be2',
  brown: 'a52a2a',
  burlywood: 'deb887',
  cadetblue: '5f9ea0',
  chartreuse: '7fff00',
  chocolate: 'd2691e',
  coral: 'ff7f50',
  cornflowerblue: '6495ed',
  cornsilk: 'fff8dc',
  crimson: 'dc143c',
  cyan: '00ffff',
  darkblue: '00008b',
  darkcyan: '008b8b',
  darkgoldenrod: 'b8860b',
  darkgray: 'a9a9a9',
  darkgreen: '006400',
  darkgrey: 'a9a9a9',
  darkkhaki: 'bdb76b',
  darkmagenta: '8b008b',
  darkolivegreen: '556b2f',
  darkorange: 'ff8c00',
  darkorchid: '9932cc',
  darkred: '8b0000',
  darksalmon: 'e9967a',
  darkseagreen: '8fbc8f',
  darkslateblue: '483d8b',
  darkslategray: '2f4f4f',
  darkslategrey: '2f4f4f',
  darkturquoise: '00ced1',
  darkviolet: '9400d3',
  deeppink: 'ff1493',
  deepskyblue: '00bfff',
  dimgray: '696969',
  dimgrey: '696969',
  dodgerblue: '1e90ff',
  firebrick: 'b22222',
  floralwhite: 'fffaf0',
  forestgreen: '228b22',
  fuchsia: 'ff00ff',
  gainsboro: 'dcdcdc',
  ghostwhite: 'f8f8ff',
  gold: 'ffd700',
  goldenrod: 'daa520',
  gray: '808080',
  green: '008000',
  greenyellow: 'adff2f',
  grey: '808080',
  honeydew: 'f0fff0',
  hotpink: 'ff69b4',
  indianred: 'cd5c5c',
  indigo: '4b0082',
  ivory: 'fffff0',
  khaki: 'f0e68c',
  lavender: 'e6e6fa',
  lavenderblush: 'fff0f5',
  lawngreen: '7cfc00',
  lemonchiffon: 'fffacd',
  lightblue: 'add8e6',
  lightcoral: 'f08080',
  lightcyan: 'e0ffff',
  lightgoldenrodyellow: 'fafad2',
  lightgray: 'd3d3d3',
  lightgreen: '90ee90',
  lightgrey: 'd3d3d3',
  lightpink: 'ffb6c1',
  lightsalmon: 'ffa07a',
  lightseagreen: '20b2aa',
  lightskyblue: '87cefa',
  lightslategray: '789',
  lightslategrey: '789',
  lightsteelblue: 'b0c4de',
  lightyellow: 'ffffe0',
  lime: '0f0',
  limegreen: '32cd32',
  linen: 'faf0e6',
  magenta: 'f0f',
  maroon: '800000',
  mediumaquamarine: '66cdaa',
  mediumblue: '0000cd',
  mediumorchid: 'ba55d3',
  mediumpurple: '9370db',
  mediumseagreen: '3cb371',
  mediumslateblue: '7b68ee',
  mediumspringgreen: '00fa9a',
  mediumturquoise: '48d1cc',
  mediumvioletred: 'c71585',
  midnightblue: '191970',
  mintcream: 'f5fffa',
  mistyrose: 'ffe4e1',
  moccasin: 'ffe4b5',
  navajowhite: 'ffdead',
  navy: '000080',
  oldlace: 'fdf5e6',
  olive: '808000',
  olivedrab: '6b8e23',
  orange: 'ffa500',
  orangered: 'ff4500',
  orchid: 'da70d6',
  palegoldenrod: 'eee8aa',
  palegreen: '98fb98',
  paleturquoise: 'afeeee',
  palevioletred: 'db7093',
  papayawhip: 'ffefd5',
  peachpuff: 'ffdab9',
  peru: 'cd853f',
  pink: 'ffc0cb',
  plum: 'dda0dd',
  powderblue: 'b0e0e6',
  purple: '800080',
  rebeccapurple: '639',
  red: 'f00',
  rosybrown: 'bc8f8f',
  royalblue: '4169e1',
  saddlebrown: '8b4513',
  salmon: 'fa8072',
  sandybrown: 'f4a460',
  seagreen: '2e8b57',
  seashell: 'fff5ee',
  sienna: 'a0522d',
  silver: 'c0c0c0',
  skyblue: '87ceeb',
  slateblue: '6a5acd',
  slategray: '708090',
  slategrey: '708090',
  snow: 'fffafa',
  springgreen: '00ff7f',
  steelblue: '4682b4',
  tan: 'd2b48c',
  teal: '008080',
  thistle: 'd8bfd8',
  tomato: 'ff6347',
  turquoise: '40e0d0',
  violet: 'ee82ee',
  wheat: 'f5deb3',
  white: 'fff',
  whitesmoke: 'f5f5f5',
  yellow: 'ff0',
  yellowgreen: '9acd32'
};

/**
 * Checks if a string is a CSS named color and returns its equivalent hex value, otherwise returns the original color.
 * @private
 */
function nameToHex(color) {
  if (typeof color !== 'string') return color;
  var normalizedColorName = color.toLowerCase();
  return namedColorMap[normalizedColorName] ? "#" + namedColorMap[normalizedColorName] : color;
}

var hexRegex = /^#[a-fA-F0-9]{6}$/;
var hexRgbaRegex = /^#[a-fA-F0-9]{8}$/;
var reducedHexRegex = /^#[a-fA-F0-9]{3}$/;
var reducedRgbaHexRegex = /^#[a-fA-F0-9]{4}$/;
var rgbRegex = /^rgb\(\s*(\d{1,3})\s*(?:,)?\s*(\d{1,3})\s*(?:,)?\s*(\d{1,3})\s*\)$/i;
var rgbaRegex = /^rgb(?:a)?\(\s*(\d{1,3})\s*(?:,)?\s*(\d{1,3})\s*(?:,)?\s*(\d{1,3})\s*(?:,|\/)\s*([-+]?\d*[.]?\d+[%]?)\s*\)$/i;
var hslRegex = /^hsl\(\s*(\d{0,3}[.]?[0-9]+(?:deg)?)\s*(?:,)?\s*(\d{1,3}[.]?[0-9]?)%\s*(?:,)?\s*(\d{1,3}[.]?[0-9]?)%\s*\)$/i;
var hslaRegex = /^hsl(?:a)?\(\s*(\d{0,3}[.]?[0-9]+(?:deg)?)\s*(?:,)?\s*(\d{1,3}[.]?[0-9]?)%\s*(?:,)?\s*(\d{1,3}[.]?[0-9]?)%\s*(?:,|\/)\s*([-+]?\d*[.]?\d+[%]?)\s*\)$/i;

/**
 * Returns an RgbColor or RgbaColor object. This utility function is only useful
 * if want to extract a color component. With the color util `toColorString` you
 * can convert a RgbColor or RgbaColor object back to a string.
 *
 * @example
 * // Assigns `{ red: 255, green: 0, blue: 0 }` to color1
 * const color1 = parseToRgb('rgb(255, 0, 0)');
 * // Assigns `{ red: 92, green: 102, blue: 112, alpha: 0.75 }` to color2
 * const color2 = parseToRgb('hsla(210, 10%, 40%, 0.75)');
 */
function parseToRgb(color) {
  if (typeof color !== 'string') {
    throw new PolishedError(3);
  }
  var normalizedColor = nameToHex(color);
  if (normalizedColor.match(hexRegex)) {
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[2], 16),
      green: parseInt("" + normalizedColor[3] + normalizedColor[4], 16),
      blue: parseInt("" + normalizedColor[5] + normalizedColor[6], 16)
    };
  }
  if (normalizedColor.match(hexRgbaRegex)) {
    var alpha = parseFloat((parseInt("" + normalizedColor[7] + normalizedColor[8], 16) / 255).toFixed(2));
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[2], 16),
      green: parseInt("" + normalizedColor[3] + normalizedColor[4], 16),
      blue: parseInt("" + normalizedColor[5] + normalizedColor[6], 16),
      alpha: alpha
    };
  }
  if (normalizedColor.match(reducedHexRegex)) {
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[1], 16),
      green: parseInt("" + normalizedColor[2] + normalizedColor[2], 16),
      blue: parseInt("" + normalizedColor[3] + normalizedColor[3], 16)
    };
  }
  if (normalizedColor.match(reducedRgbaHexRegex)) {
    var _alpha = parseFloat((parseInt("" + normalizedColor[4] + normalizedColor[4], 16) / 255).toFixed(2));
    return {
      red: parseInt("" + normalizedColor[1] + normalizedColor[1], 16),
      green: parseInt("" + normalizedColor[2] + normalizedColor[2], 16),
      blue: parseInt("" + normalizedColor[3] + normalizedColor[3], 16),
      alpha: _alpha
    };
  }
  var rgbMatched = rgbRegex.exec(normalizedColor);
  if (rgbMatched) {
    return {
      red: parseInt("" + rgbMatched[1], 10),
      green: parseInt("" + rgbMatched[2], 10),
      blue: parseInt("" + rgbMatched[3], 10)
    };
  }
  var rgbaMatched = rgbaRegex.exec(normalizedColor.substring(0, 50));
  if (rgbaMatched) {
    return {
      red: parseInt("" + rgbaMatched[1], 10),
      green: parseInt("" + rgbaMatched[2], 10),
      blue: parseInt("" + rgbaMatched[3], 10),
      alpha: parseFloat("" + rgbaMatched[4]) > 1 ? parseFloat("" + rgbaMatched[4]) / 100 : parseFloat("" + rgbaMatched[4])
    };
  }
  var hslMatched = hslRegex.exec(normalizedColor);
  if (hslMatched) {
    var hue = parseInt("" + hslMatched[1], 10);
    var saturation = parseInt("" + hslMatched[2], 10) / 100;
    var lightness = parseInt("" + hslMatched[3], 10) / 100;
    var rgbColorString = "rgb(" + hslToRgb(hue, saturation, lightness) + ")";
    var hslRgbMatched = rgbRegex.exec(rgbColorString);
    if (!hslRgbMatched) {
      throw new PolishedError(4, normalizedColor, rgbColorString);
    }
    return {
      red: parseInt("" + hslRgbMatched[1], 10),
      green: parseInt("" + hslRgbMatched[2], 10),
      blue: parseInt("" + hslRgbMatched[3], 10)
    };
  }
  var hslaMatched = hslaRegex.exec(normalizedColor.substring(0, 50));
  if (hslaMatched) {
    var _hue = parseInt("" + hslaMatched[1], 10);
    var _saturation = parseInt("" + hslaMatched[2], 10) / 100;
    var _lightness = parseInt("" + hslaMatched[3], 10) / 100;
    var _rgbColorString = "rgb(" + hslToRgb(_hue, _saturation, _lightness) + ")";
    var _hslRgbMatched = rgbRegex.exec(_rgbColorString);
    if (!_hslRgbMatched) {
      throw new PolishedError(4, normalizedColor, _rgbColorString);
    }
    return {
      red: parseInt("" + _hslRgbMatched[1], 10),
      green: parseInt("" + _hslRgbMatched[2], 10),
      blue: parseInt("" + _hslRgbMatched[3], 10),
      alpha: parseFloat("" + hslaMatched[4]) > 1 ? parseFloat("" + hslaMatched[4]) / 100 : parseFloat("" + hslaMatched[4])
    };
  }
  throw new PolishedError(5);
}

function rgbToHsl(color) {
  // make sure rgb are contained in a set of [0, 255]
  var red = color.red / 255;
  var green = color.green / 255;
  var blue = color.blue / 255;
  var max = Math.max(red, green, blue);
  var min = Math.min(red, green, blue);
  var lightness = (max + min) / 2;
  if (max === min) {
    // achromatic
    if (color.alpha !== undefined) {
      return {
        hue: 0,
        saturation: 0,
        lightness: lightness,
        alpha: color.alpha
      };
    } else {
      return {
        hue: 0,
        saturation: 0,
        lightness: lightness
      };
    }
  }
  var hue;
  var delta = max - min;
  var saturation = lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  switch (max) {
    case red:
      hue = (green - blue) / delta + (green < blue ? 6 : 0);
      break;
    case green:
      hue = (blue - red) / delta + 2;
      break;
    default:
      // blue case
      hue = (red - green) / delta + 4;
      break;
  }
  hue *= 60;
  if (color.alpha !== undefined) {
    return {
      hue: hue,
      saturation: saturation,
      lightness: lightness,
      alpha: color.alpha
    };
  }
  return {
    hue: hue,
    saturation: saturation,
    lightness: lightness
  };
}

/**
 * Returns an HslColor or HslaColor object. This utility function is only useful
 * if want to extract a color component. With the color util `toColorString` you
 * can convert a HslColor or HslaColor object back to a string.
 *
 * @example
 * // Assigns `{ hue: 0, saturation: 1, lightness: 0.5 }` to color1
 * const color1 = parseToHsl('rgb(255, 0, 0)');
 * // Assigns `{ hue: 128, saturation: 1, lightness: 0.5, alpha: 0.75 }` to color2
 * const color2 = parseToHsl('hsla(128, 100%, 50%, 0.75)');
 */
function parseToHsl(color) {
  // Note: At a later stage we can optimize this function as right now a hsl
  // color would be parsed converted to rgb values and converted back to hsl.
  return rgbToHsl(parseToRgb(color));
}

/**
 * Reduces hex values if possible e.g. #ff8866 to #f86
 * @private
 */
var reduceHexValue = function reduceHexValue(value) {
  if (value.length === 7 && value[1] === value[2] && value[3] === value[4] && value[5] === value[6]) {
    return "#" + value[1] + value[3] + value[5];
  }
  return value;
};
var reduceHexValue$1 = reduceHexValue;

function numberToHex(value) {
  var hex = value.toString(16);
  return hex.length === 1 ? "0" + hex : hex;
}

function colorToHex(color) {
  return numberToHex(Math.round(color * 255));
}
function convertToHex(red, green, blue) {
  return reduceHexValue$1("#" + colorToHex(red) + colorToHex(green) + colorToHex(blue));
}
function hslToHex(hue, saturation, lightness) {
  return hslToRgb(hue, saturation, lightness, convertToHex);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible hex notation.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: hsl(359, 0.75, 0.4),
 *   background: hsl({ hue: 360, saturation: 0.75, lightness: 0.4 }),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${hsl(359, 0.75, 0.4)};
 *   background: ${hsl({ hue: 360, saturation: 0.75, lightness: 0.4 })};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#b3191c";
 *   background: "#b3191c";
 * }
 */
function hsl(value, saturation, lightness) {
  if (typeof value === 'object' && saturation === undefined && lightness === undefined) {
    return hslToHex(value.hue, value.saturation, value.lightness);
  }
  throw new PolishedError(1);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible rgba or hex notation.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: hsla(359, 0.75, 0.4, 0.7),
 *   background: hsla({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0,7 }),
 *   background: hsla(359, 0.75, 0.4, 1),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${hsla(359, 0.75, 0.4, 0.7)};
 *   background: ${hsla({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0,7 })};
 *   background: ${hsla(359, 0.75, 0.4, 1)};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "rgba(179,25,28,0.7)";
 *   background: "rgba(179,25,28,0.7)";
 *   background: "#b3191c";
 * }
 */
function hsla(value, saturation, lightness, alpha) {
  if (typeof value === 'object' && saturation === undefined && lightness === undefined && alpha === undefined) {
    return value.alpha >= 1 ? hslToHex(value.hue, value.saturation, value.lightness) : "rgba(" + hslToRgb(value.hue, value.saturation, value.lightness) + "," + value.alpha + ")";
  }
  throw new PolishedError(2);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible hex notation.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: rgb(255, 205, 100),
 *   background: rgb({ red: 255, green: 205, blue: 100 }),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${rgb(255, 205, 100)};
 *   background: ${rgb({ red: 255, green: 205, blue: 100 })};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#ffcd64";
 *   background: "#ffcd64";
 * }
 */
function rgb(value, green, blue) {
  if (typeof value === 'number' && typeof green === 'number' && typeof blue === 'number') {
    return reduceHexValue$1("#" + numberToHex(value) + numberToHex(green) + numberToHex(blue));
  } else if (typeof value === 'object' && green === undefined && blue === undefined) {
    return reduceHexValue$1("#" + numberToHex(value.red) + numberToHex(value.green) + numberToHex(value.blue));
  }
  throw new PolishedError(6);
}

/**
 * Returns a string value for the color. The returned result is the smallest possible rgba or hex notation.
 *
 * Can also be used to fade a color by passing a hex value or named CSS color along with an alpha value.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: rgba(255, 205, 100, 0.7),
 *   background: rgba({ red: 255, green: 205, blue: 100, alpha: 0.7 }),
 *   background: rgba(255, 205, 100, 1),
 *   background: rgba('#ffffff', 0.4),
 *   background: rgba('black', 0.7),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${rgba(255, 205, 100, 0.7)};
 *   background: ${rgba({ red: 255, green: 205, blue: 100, alpha: 0.7 })};
 *   background: ${rgba(255, 205, 100, 1)};
 *   background: ${rgba('#ffffff', 0.4)};
 *   background: ${rgba('black', 0.7)};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "rgba(255,205,100,0.7)";
 *   background: "rgba(255,205,100,0.7)";
 *   background: "#ffcd64";
 *   background: "rgba(255,255,255,0.4)";
 *   background: "rgba(0,0,0,0.7)";
 * }
 */
function rgba(firstValue, secondValue, thirdValue, fourthValue) {
  if (typeof firstValue === 'object' && secondValue === undefined && thirdValue === undefined && fourthValue === undefined) {
    return firstValue.alpha >= 1 ? rgb(firstValue.red, firstValue.green, firstValue.blue) : "rgba(" + firstValue.red + "," + firstValue.green + "," + firstValue.blue + "," + firstValue.alpha + ")";
  }
  throw new PolishedError(7);
}

var isRgb = function isRgb(color) {
  return typeof color.red === 'number' && typeof color.green === 'number' && typeof color.blue === 'number' && (typeof color.alpha !== 'number' || typeof color.alpha === 'undefined');
};
var isRgba = function isRgba(color) {
  return typeof color.red === 'number' && typeof color.green === 'number' && typeof color.blue === 'number' && typeof color.alpha === 'number';
};
var isHsl = function isHsl(color) {
  return typeof color.hue === 'number' && typeof color.saturation === 'number' && typeof color.lightness === 'number' && (typeof color.alpha !== 'number' || typeof color.alpha === 'undefined');
};
var isHsla = function isHsla(color) {
  return typeof color.hue === 'number' && typeof color.saturation === 'number' && typeof color.lightness === 'number' && typeof color.alpha === 'number';
};

/**
 * Converts a RgbColor, RgbaColor, HslColor or HslaColor object to a color string.
 * This util is useful in case you only know on runtime which color object is
 * used. Otherwise we recommend to rely on `rgb`, `rgba`, `hsl` or `hsla`.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: toColorString({ red: 255, green: 205, blue: 100 }),
 *   background: toColorString({ red: 255, green: 205, blue: 100, alpha: 0.72 }),
 *   background: toColorString({ hue: 240, saturation: 1, lightness: 0.5 }),
 *   background: toColorString({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0.72 }),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${toColorString({ red: 255, green: 205, blue: 100 })};
 *   background: ${toColorString({ red: 255, green: 205, blue: 100, alpha: 0.72 })};
 *   background: ${toColorString({ hue: 240, saturation: 1, lightness: 0.5 })};
 *   background: ${toColorString({ hue: 360, saturation: 0.75, lightness: 0.4, alpha: 0.72 })};
 * `
 *
 * // CSS in JS Output
 * element {
 *   background: "#ffcd64";
 *   background: "rgba(255,205,100,0.72)";
 *   background: "#00f";
 *   background: "rgba(179,25,25,0.72)";
 * }
 */

function toColorString(color) {
  if (typeof color !== 'object') throw new PolishedError(8);
  if (isRgba(color)) return rgba(color);
  if (isRgb(color)) return rgb(color);
  if (isHsla(color)) return hsla(color);
  if (isHsl(color)) return hsl(color);
  throw new PolishedError(8);
}

// Type definitions taken from https://github.com/gcanti/flow-static-land/blob/master/src/Fun.js
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-unused-vars
// eslint-disable-next-line no-redeclare
function curried(f, length, acc) {
  return function fn() {
    // eslint-disable-next-line prefer-rest-params
    var combined = acc.concat(Array.prototype.slice.call(arguments));
    return combined.length >= length ? f.apply(this, combined) : curried(f, length, combined);
  };
}

// eslint-disable-next-line no-redeclare
function curry(f) {
  // eslint-disable-line no-redeclare
  return curried(f, f.length, []);
}

/**
 * Changes the hue of the color. Hue is a number between 0 to 360. The first
 * argument for adjustHue is the amount of degrees the color is rotated around
 * the color wheel, always producing a positive hue value.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: adjustHue(180, '#448'),
 *   background: adjustHue('180', 'rgba(101,100,205,0.7)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${adjustHue(180, '#448')};
 *   background: ${adjustHue('180', 'rgba(101,100,205,0.7)')};
 * `
 *
 * // CSS in JS Output
 * element {
 *   background: "#888844";
 *   background: "rgba(136,136,68,0.7)";
 * }
 */
function adjustHue(degree, color) {
  if (color === 'transparent') return color;
  var hslColor = parseToHsl(color);
  return toColorString(_extends({}, hslColor, {
    hue: hslColor.hue + parseFloat(degree)
  }));
}

// prettier-ignore
curry /* ::<number | string, string, string> */(adjustHue);

function guard(lowerBoundary, upperBoundary, value) {
  return Math.max(lowerBoundary, Math.min(upperBoundary, value));
}

/**
 * Returns a string value for the darkened color.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: darken(0.2, '#FFCD64'),
 *   background: darken('0.2', 'rgba(255,205,100,0.7)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${darken(0.2, '#FFCD64')};
 *   background: ${darken('0.2', 'rgba(255,205,100,0.7)')};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#ffbd31";
 *   background: "rgba(255,189,49,0.7)";
 * }
 */
function darken(amount, color) {
  if (color === 'transparent') return color;
  var hslColor = parseToHsl(color);
  return toColorString(_extends({}, hslColor, {
    lightness: guard(0, 1, hslColor.lightness - parseFloat(amount))
  }));
}

// prettier-ignore
curry /* ::<number | string, string, string> */(darken);

/**
 * Decreases the intensity of a color. Its range is between 0 to 1. The first
 * argument of the desaturate function is the amount by how much the color
 * intensity should be decreased.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: desaturate(0.2, '#CCCD64'),
 *   background: desaturate('0.2', 'rgba(204,205,100,0.7)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${desaturate(0.2, '#CCCD64')};
 *   background: ${desaturate('0.2', 'rgba(204,205,100,0.7)')};
 * `
 *
 * // CSS in JS Output
 * element {
 *   background: "#b8b979";
 *   background: "rgba(184,185,121,0.7)";
 * }
 */
function desaturate(amount, color) {
  if (color === 'transparent') return color;
  var hslColor = parseToHsl(color);
  return toColorString(_extends({}, hslColor, {
    saturation: guard(0, 1, hslColor.saturation - parseFloat(amount))
  }));
}

// prettier-ignore
curry /* ::<number | string, string, string> */(desaturate);

/**
 * Returns a number (float) representing the luminance of a color.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: getLuminance('#CCCD64') >= getLuminance('#0000ff') ? '#CCCD64' : '#0000ff',
 *   background: getLuminance('rgba(58, 133, 255, 1)') >= getLuminance('rgba(255, 57, 149, 1)') ?
 *                             'rgba(58, 133, 255, 1)' :
 *                             'rgba(255, 57, 149, 1)',
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${getLuminance('#CCCD64') >= getLuminance('#0000ff') ? '#CCCD64' : '#0000ff'};
 *   background: ${getLuminance('rgba(58, 133, 255, 1)') >= getLuminance('rgba(255, 57, 149, 1)') ?
 *                             'rgba(58, 133, 255, 1)' :
 *                             'rgba(255, 57, 149, 1)'};
 *
 * // CSS in JS Output
 *
 * div {
 *   background: "#CCCD64";
 *   background: "rgba(58, 133, 255, 1)";
 * }
 */
function getLuminance(color) {
  if (color === 'transparent') return 0;
  var rgbColor = parseToRgb(color);
  var _Object$keys$map = Object.keys(rgbColor).map(function (key) {
      var channel = rgbColor[key] / 255;
      return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
    }),
    r = _Object$keys$map[0],
    g = _Object$keys$map[1],
    b = _Object$keys$map[2];
  return parseFloat((0.2126 * r + 0.7152 * g + 0.0722 * b).toFixed(3));
}

/**
 * Returns the contrast ratio between two colors based on
 * [W3's recommended equation for calculating contrast](http://www.w3.org/TR/WCAG20/#contrast-ratiodef).
 *
 * @example
 * const contrastRatio = getContrast('#444', '#fff');
 */
function getContrast(color1, color2) {
  var luminance1 = getLuminance(color1);
  var luminance2 = getLuminance(color2);
  return parseFloat((luminance1 > luminance2 ? (luminance1 + 0.05) / (luminance2 + 0.05) : (luminance2 + 0.05) / (luminance1 + 0.05)).toFixed(2));
}

/**
 * Returns a string value for the lightened color.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: lighten(0.2, '#CCCD64'),
 *   background: lighten('0.2', 'rgba(204,205,100,0.7)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${lighten(0.2, '#FFCD64')};
 *   background: ${lighten('0.2', 'rgba(204,205,100,0.7)')};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#e5e6b1";
 *   background: "rgba(229,230,177,0.7)";
 * }
 */
function lighten(amount, color) {
  if (color === 'transparent') return color;
  var hslColor = parseToHsl(color);
  return toColorString(_extends({}, hslColor, {
    lightness: guard(0, 1, hslColor.lightness + parseFloat(amount))
  }));
}

// prettier-ignore
curry /* ::<number | string, string, string> */(lighten);

/**
 * Determines which contrast guidelines have been met for two colors.
 * Based on the [contrast calculations recommended by W3](https://www.w3.org/WAI/WCAG21/Understanding/contrast-enhanced.html).
 *
 * @example
 * const scores = meetsContrastGuidelines('#444', '#fff');
 */
function meetsContrastGuidelines(color1, color2) {
  var contrastRatio = getContrast(color1, color2);
  return {
    AA: contrastRatio >= 4.5,
    AALarge: contrastRatio >= 3,
    AAA: contrastRatio >= 7,
    AAALarge: contrastRatio >= 4.5
  };
}

/**
 * Mixes the two provided colors together by calculating the average of each of the RGB components weighted to the first color by the provided weight.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: mix(0.5, '#f00', '#00f')
 *   background: mix(0.25, '#f00', '#00f')
 *   background: mix('0.5', 'rgba(255, 0, 0, 0.5)', '#00f')
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${mix(0.5, '#f00', '#00f')};
 *   background: ${mix(0.25, '#f00', '#00f')};
 *   background: ${mix('0.5', 'rgba(255, 0, 0, 0.5)', '#00f')};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#7f007f";
 *   background: "#3f00bf";
 *   background: "rgba(63, 0, 191, 0.75)";
 * }
 */
function mix(weight, color, otherColor) {
  if (color === 'transparent') return otherColor;
  if (otherColor === 'transparent') return color;
  if (weight === 0) return otherColor;
  var parsedColor1 = parseToRgb(color);
  var color1 = _extends({}, parsedColor1, {
    alpha: typeof parsedColor1.alpha === 'number' ? parsedColor1.alpha : 1
  });
  var parsedColor2 = parseToRgb(otherColor);
  var color2 = _extends({}, parsedColor2, {
    alpha: typeof parsedColor2.alpha === 'number' ? parsedColor2.alpha : 1
  });

  // The formula is copied from the original Sass implementation:
  // http://sass-lang.com/documentation/Sass/Script/Functions.html#mix-instance_method
  var alphaDelta = color1.alpha - color2.alpha;
  var x = parseFloat(weight) * 2 - 1;
  var y = x * alphaDelta === -1 ? x : x + alphaDelta;
  var z = 1 + x * alphaDelta;
  var weight1 = (y / z + 1) / 2.0;
  var weight2 = 1 - weight1;
  var mixedColor = {
    red: Math.floor(color1.red * weight1 + color2.red * weight2),
    green: Math.floor(color1.green * weight1 + color2.green * weight2),
    blue: Math.floor(color1.blue * weight1 + color2.blue * weight2),
    alpha: color1.alpha * parseFloat(weight) + color2.alpha * (1 - parseFloat(weight))
  };
  return rgba(mixedColor);
}

// prettier-ignore
var curriedMix = curry /* ::<number | string, string, string, string> */(mix);
var mix$1 = curriedMix;

/**
 * Increases the opacity of a color. Its range for the amount is between 0 to 1.
 *
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: opacify(0.1, 'rgba(255, 255, 255, 0.9)');
 *   background: opacify(0.2, 'hsla(0, 0%, 100%, 0.5)'),
 *   background: opacify('0.5', 'rgba(255, 0, 0, 0.2)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${opacify(0.1, 'rgba(255, 255, 255, 0.9)')};
 *   background: ${opacify(0.2, 'hsla(0, 0%, 100%, 0.5)')},
 *   background: ${opacify('0.5', 'rgba(255, 0, 0, 0.2)')},
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#fff";
 *   background: "rgba(255,255,255,0.7)";
 *   background: "rgba(255,0,0,0.7)";
 * }
 */
function opacify(amount, color) {
  if (color === 'transparent') return color;
  var parsedColor = parseToRgb(color);
  var alpha = typeof parsedColor.alpha === 'number' ? parsedColor.alpha : 1;
  var colorWithAlpha = _extends({}, parsedColor, {
    alpha: guard(0, 1, (alpha * 100 + parseFloat(amount) * 100) / 100)
  });
  return rgba(colorWithAlpha);
}

// prettier-ignore
curry /* ::<number | string, string, string> */(opacify);

var defaultReturnIfLightColor = '#000';
var defaultReturnIfDarkColor = '#fff';

/**
 * Returns black or white (or optional passed colors) for best
 * contrast depending on the luminosity of the given color.
 * When passing custom return colors, strict mode ensures that the
 * return color always meets or exceeds WCAG level AA or greater. If this test
 * fails, the default return color (black or white) is returned in place of the
 * custom return color. You can optionally turn off strict mode.
 *
 * Follows [W3C specs for readability](https://www.w3.org/TR/WCAG20-TECHS/G18.html).
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   color: readableColor('#000'),
 *   color: readableColor('black', '#001', '#ff8'),
 *   color: readableColor('white', '#001', '#ff8'),
 *   color: readableColor('red', '#333', '#ddd', true)
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   color: ${readableColor('#000')};
 *   color: ${readableColor('black', '#001', '#ff8')};
 *   color: ${readableColor('white', '#001', '#ff8')};
 *   color: ${readableColor('red', '#333', '#ddd', true)};
 * `
 *
 * // CSS in JS Output
 * element {
 *   color: "#fff";
 *   color: "#ff8";
 *   color: "#001";
 *   color: "#000";
 * }
 */
function readableColor(color, returnIfLightColor, returnIfDarkColor, strict) {
  if (returnIfLightColor === void 0) {
    returnIfLightColor = defaultReturnIfLightColor;
  }
  if (returnIfDarkColor === void 0) {
    returnIfDarkColor = defaultReturnIfDarkColor;
  }
  var isColorLight = getLuminance(color) > 0.179;
  var preferredReturnColor = isColorLight ? returnIfLightColor : returnIfDarkColor;
  {
    return preferredReturnColor;
  }
}

/**
 * Increases the intensity of a color. Its range is between 0 to 1. The first
 * argument of the saturate function is the amount by how much the color
 * intensity should be increased.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: saturate(0.2, '#CCCD64'),
 *   background: saturate('0.2', 'rgba(204,205,100,0.7)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${saturate(0.2, '#FFCD64')};
 *   background: ${saturate('0.2', 'rgba(204,205,100,0.7)')};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#e0e250";
 *   background: "rgba(224,226,80,0.7)";
 * }
 */
function saturate(amount, color) {
  if (color === 'transparent') return color;
  var hslColor = parseToHsl(color);
  return toColorString(_extends({}, hslColor, {
    saturation: guard(0, 1, hslColor.saturation + parseFloat(amount))
  }));
}

// prettier-ignore
curry /* ::<number | string, string, string> */(saturate);

/**
 * Sets the hue of a color to the provided value. The hue range can be
 * from 0 and 359.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: setHue(42, '#CCCD64'),
 *   background: setHue('244', 'rgba(204,205,100,0.7)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${setHue(42, '#CCCD64')};
 *   background: ${setHue('244', 'rgba(204,205,100,0.7)')};
 * `
 *
 * // CSS in JS Output
 * element {
 *   background: "#cdae64";
 *   background: "rgba(107,100,205,0.7)";
 * }
 */
function setHue(hue, color) {
  if (color === 'transparent') return color;
  return toColorString(_extends({}, parseToHsl(color), {
    hue: parseFloat(hue)
  }));
}

// prettier-ignore
curry /* ::<number | string, string, string> */(setHue);

/**
 * Sets the lightness of a color to the provided value. The lightness range can be
 * from 0 and 1.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: setLightness(0.2, '#CCCD64'),
 *   background: setLightness('0.75', 'rgba(204,205,100,0.7)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${setLightness(0.2, '#CCCD64')};
 *   background: ${setLightness('0.75', 'rgba(204,205,100,0.7)')};
 * `
 *
 * // CSS in JS Output
 * element {
 *   background: "#4d4d19";
 *   background: "rgba(223,224,159,0.7)";
 * }
 */
function setLightness(lightness, color) {
  if (color === 'transparent') return color;
  return toColorString(_extends({}, parseToHsl(color), {
    lightness: parseFloat(lightness)
  }));
}

// prettier-ignore
curry /* ::<number | string, string, string> */(setLightness);

/**
 * Sets the saturation of a color to the provided value. The saturation range can be
 * from 0 and 1.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: setSaturation(0.2, '#CCCD64'),
 *   background: setSaturation('0.75', 'rgba(204,205,100,0.7)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${setSaturation(0.2, '#CCCD64')};
 *   background: ${setSaturation('0.75', 'rgba(204,205,100,0.7)')};
 * `
 *
 * // CSS in JS Output
 * element {
 *   background: "#adad84";
 *   background: "rgba(228,229,76,0.7)";
 * }
 */
function setSaturation(saturation, color) {
  if (color === 'transparent') return color;
  return toColorString(_extends({}, parseToHsl(color), {
    saturation: parseFloat(saturation)
  }));
}

// prettier-ignore
curry /* ::<number | string, string, string> */(setSaturation);

/**
 * Shades a color by mixing it with black. `shade` can produce
 * hue shifts, where as `darken` manipulates the luminance channel and therefore
 * doesn't produce hue shifts.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: shade(0.25, '#00f')
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${shade(0.25, '#00f')};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#00003f";
 * }
 */

function shade(percentage, color) {
  if (color === 'transparent') return color;
  return mix$1(parseFloat(percentage), 'rgb(0, 0, 0)', color);
}

// prettier-ignore
curry /* ::<number | string, string, string> */(shade);

/**
 * Tints a color by mixing it with white. `tint` can produce
 * hue shifts, where as `lighten` manipulates the luminance channel and therefore
 * doesn't produce hue shifts.
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: tint(0.25, '#00f')
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${tint(0.25, '#00f')};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "#bfbfff";
 * }
 */

function tint(percentage, color) {
  if (color === 'transparent') return color;
  return mix$1(parseFloat(percentage), 'rgb(255, 255, 255)', color);
}

// prettier-ignore
curry /* ::<number | string, string, string> */(tint);

/**
 * Decreases the opacity of a color. Its range for the amount is between 0 to 1.
 *
 *
 * @example
 * // Styles as object usage
 * const styles = {
 *   background: transparentize(0.1, '#fff'),
 *   background: transparentize(0.2, 'hsl(0, 0%, 100%)'),
 *   background: transparentize('0.5', 'rgba(255, 0, 0, 0.8)'),
 * }
 *
 * // styled-components usage
 * const div = styled.div`
 *   background: ${transparentize(0.1, '#fff')};
 *   background: ${transparentize(0.2, 'hsl(0, 0%, 100%)')};
 *   background: ${transparentize('0.5', 'rgba(255, 0, 0, 0.8)')};
 * `
 *
 * // CSS in JS Output
 *
 * element {
 *   background: "rgba(255,255,255,0.9)";
 *   background: "rgba(255,255,255,0.8)";
 *   background: "rgba(255,0,0,0.3)";
 * }
 */
function transparentize(amount, color) {
  if (color === 'transparent') return color;
  var parsedColor = parseToRgb(color);
  var alpha = typeof parsedColor.alpha === 'number' ? parsedColor.alpha : 1;
  var colorWithAlpha = _extends({}, parsedColor, {
    alpha: guard(0, 1, +(alpha * 100 - parseFloat(amount) * 100).toFixed(2) / 100)
  });
  return rgba(colorWithAlpha);
}

// prettier-ignore
curry /* ::<number | string, string, string> */(transparentize);

const getReadableColor = (bgColor = '#000', fgColor = '#FFF') => {
    if (meetsContrastGuidelines(bgColor, fgColor).AALarge)
        return fgColor;
    const isFGLight = getLuminance(fgColor) > 0.5;
    const defaultLightBgForeground = isFGLight ? '#000' : fgColor;
    const defaultDarkBgForeground = isFGLight ? fgColor : '#FFF';
    return readableColor(bgColor, defaultLightBgForeground, defaultDarkBgForeground);
};

const button$1 = "_button_rnlvq_1";
const buttonPrimary = "_buttonPrimary_rnlvq_11";
const buttonSecondary = "_buttonSecondary_rnlvq_15";
const buttonSmall = "_buttonSmall_rnlvq_20";
const buttonMedium = "_buttonMedium_rnlvq_24";
const buttonLarge = "_buttonLarge_rnlvq_28";
const styles$3 = {
	button: button$1,
	buttonPrimary: buttonPrimary,
	buttonSecondary: buttonSecondary,
	buttonSmall: buttonSmall,
	buttonMedium: buttonMedium,
	buttonLarge: buttonLarge
};

var _tmpl$$a = /* @__PURE__ */ template(`<button>`);
const Button = (props) => {
  props = mergeProps({
    size: "small"
  }, props);
  const [local, rest] = splitProps(props, ["primary", "size", "backgroundColor", "label"]);
  const [buttonClass, setButtonClass] = createSignal("");
  createEffect(() => {
    const variant = pascalCase(local.primary ? "primary" : "secondary");
    const size = pascalCase(local.size || "medium");
    setButtonClass(`${styles$3.button} ${styles$3[`button${variant}`]} ${styles$3[`button${size}`]}`);
  });
  return (() => {
    var _el$ = _tmpl$$a();
    spread(_el$, mergeProps(rest, {
      "type": "button",
      get ["class"]() {
        return buttonClass();
      },
      get style() {
        return {
          "background-color": local.backgroundColor
        };
      }
    }), false, true);
    insert(_el$, () => local.label);
    return _el$;
  })();
};

const button = "_button_1gg66_1";
const buttons = "_buttons_1gg66_9";
const wide = "_wide_1gg66_13";
const narrow = "_narrow_1gg66_18";
const colSpan2 = "_colSpan2_1gg66_23";
const styles$2 = {
	button: button,
	buttons: buttons,
	wide: wide,
	narrow: narrow,
	colSpan2: colSpan2
};

var ActionType = /* @__PURE__ */ ((ActionType2) => {
  ActionType2["CALL_PHONE"] = "call_phone";
  ActionType2["OPEN_URL"] = "open_url";
  ActionType2["SEND_EMAIL"] = "send_email";
  ActionType2["RUN_WORKFLOW"] = "run_workflow";
  ActionType2["RUN_CALLBACK"] = "run_callback";
  ActionType2["LAUNCH_INTERCOM_CHAT"] = "launch_intercom_chat";
  ActionType2["LAUNCH_ZENDESK_CHAT"] = "launch_zendesk_chat";
  ActionType2["LAUNCH_ZENDESK_MESSENGER"] = "launch_zendesk_messenger";
  ActionType2["LAUNCH_FRESHDESK"] = "launch_freshdesk";
  ActionType2["LAUNCH_SUPPORT_TICKET_FORM"] = "launch_support_ticket_form";
  return ActionType2;
})(ActionType || {});

const hasFreshdesk = () => typeof window !== "undefined" && window.fcWidget;

function loadScript({
  src,
  initFreshdeskWindow,
  initWindow,
  scriptType = "async",
  callBack
}, updateProps) {
  const freshdeskLoaded = document.querySelector(`script[src="${src}"]`);
  if (!freshdeskLoaded) {
    const freshdeskScript = document.createElement("script");
    freshdeskScript.type = "text/javascript";
    if (typeof scriptType !== "undefined") {
      freshdeskScript[scriptType] = true;
    }
    freshdeskScript.src = src;
    const scripts = document.getElementsByTagName("script");
    const initialScript = scripts?.length ? scripts[0] : null;
    freshdeskScript.onload = () => {
      if (initWindow && updateProps) {
        initFreshdeskWindow({
          ...updateProps
        });
      }
      typeof callBack === "function" && callBack();
    };
    if (freshdeskScript) {
      initialScript?.parentNode ? initialScript.parentNode.insertBefore(freshdeskScript, initialScript) : document.body.appendChild(freshdeskScript);
    }
  } else {
    console.error("freshdesk script already inserted");
  }
}

function initFreshdeskWindow(props) {
  const freshdeskProps = {
    ...props
  };
  if (hasFreshdesk()) {
    window.fcWidget.init(freshdeskProps);
  }
}
function loadFreshdesk({
  // eslint-disable-next-line unused-imports/no-unused-vars
  delay = 0,
  initWindow = true,
  callback,
  ...extra
}) {
  if (typeof window !== "undefined") {
    if (typeof window.fcWidget === "function") {
      initFreshdeskWindow(extra);
    } else {
      loadScript(
        {
          src: `${extra.host}/js/widget.js`,
          initFreshdeskWindow,
          initWindow,
          scriptType: "async",
          callBack: callback
        },
        {
          token: extra.token,
          host: extra.host
        }
      );
    }
  }
}
function showFreshdesk() {
  if (hasFreshdesk()) {
    window.fcWidget.open();
    window.fcWidget.show();
  }
}

const callPhoneHandler = (element) => (action) => {
  window.location.href = `tel:${action.action.options.phoneNumber}`;
  dispatchCustomEvent(element, "BrainfishActionClick", { action });
};
const sendEmailHandler = (element) => (action) => {
  window.location.href = `mailto:${action.action.options.to}`;
  dispatchCustomEvent(element, "BrainfishActionClick", { action });
};
const openUrlHandler = (element) => (action) => {
  if (action.action.options.url.startsWith("http")) {
    window.open(action.action.options.url, "_blank");
  }
  dispatchCustomEvent(element, "BrainfishActionClick", { action });
};
const checkZendeskLoaded = () => {
  if (!window.zE) {
    console.error(
      "Zendesk Chat is not loaded. Make sure you've included the Zendesk Chat script in your website."
    );
  }
};
const checkFreshdeskLoaded = (action) => {
  const callback = () => {
    window.Brainfish.HelpWidget.close();
    setTimeout(() => {
      showFreshdesk();
    }, 300);
  };
  if (!window.fcWidget) {
    console.error(
      "Freshdesk is not loaded. Make sure you've included the Freshdesk script in your website."
    );
    loadFreshdesk({
      token: action.action.options.token,
      host: action.action.options.url,
      ssr: false,
      delay: 0,
      initWindow: true,
      callback: () => {
        callback();
      }
    });
  } else {
    callback();
  }
};
const checkIntercomLoaded = () => {
  if (!window.Intercom) {
    console.error(
      "Intercom is not loaded. Make sure you've included the Intercom script in your website."
    );
  }
};
const zendeskChatHandler = (element) => (action) => {
  checkZendeskLoaded();
  dispatchCustomEvent(element, "BrainfishActionClick", { action });
};
const zendeskMessengerHandler = (element) => (action) => {
  checkZendeskLoaded();
  dispatchCustomEvent(element, "BrainfishActionClick", { action });
};
const intercomChatHandler = (element) => (action) => {
  checkIntercomLoaded();
  dispatchCustomEvent(element, "BrainfishActionClick", { action });
};
const supportTicketFormHandler = (element) => (action) => {
  if (action.action.options.formUrl.startsWith("http")) {
    window.open(action.action.options.formUrl, "_blank");
  }
  dispatchCustomEvent(element, "BrainfishActionClick", { action });
};
const freshdeskHandler = (element) => (action) => {
  checkFreshdeskLoaded(action);
  dispatchCustomEvent(element, "BrainfishActionClick", { action });
};
const actionHandlers = {
  [ActionType.CALL_PHONE]: callPhoneHandler,
  [ActionType.SEND_EMAIL]: sendEmailHandler,
  [ActionType.OPEN_URL]: openUrlHandler,
  [ActionType.LAUNCH_ZENDESK_CHAT]: zendeskChatHandler,
  [ActionType.LAUNCH_ZENDESK_MESSENGER]: zendeskMessengerHandler,
  [ActionType.LAUNCH_INTERCOM_CHAT]: intercomChatHandler,
  [ActionType.LAUNCH_SUPPORT_TICKET_FORM]: supportTicketFormHandler,
  [ActionType.LAUNCH_FRESHDESK]: freshdeskHandler
};

var _tmpl$$9 = /* @__PURE__ */ template(`<div>`);
const isUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};
const Icon = (props) => {
  props = mergeProps({
    name: "",
    classes: "",
    fill: "currentColor",
    weight: "regular",
    size: 24
  }, props);
  const [local, rest] = splitProps(props, ["name", "classes", "fill", "weight", "size"]);
  const [svgContent, setSvgContent] = createSignal(null);
  const suffix = local.weight === "regular" ? "" : `-${local.weight}`;
  createEffect(() => {
    const fetchSvg = async (url) => {
      try {
        const response = await fetch(url);
        const data = await response.text();
        setSvgContent(data);
      } catch (error) {
        console.error(error);
      }
    };
    if (isUrl(local.name)) {
      fetchSvg(local.name);
    } else {
      const iconName = `${kebabCase(local.name)}`;
      const iconSvgSrc = `https://unpkg.com/@phosphor-icons/core@2.0.2/assets/${local.weight}/${iconName}${suffix}.svg`;
      fetchSvg(iconSvgSrc);
    }
  });
  return (() => {
    var _el$ = _tmpl$$9();
    spread(_el$, mergeProps({
      get ["class"]() {
        return local.classes;
      },
      get innerHTML() {
        return svgContent() || "";
      },
      get style() {
        return {
          color: local.fill,
          width: `${local.size}px`,
          height: `${local.size}px`
        };
      }
    }, rest), false, false);
    return _el$;
  })();
};

var _tmpl$$8 = /* @__PURE__ */ template(`<svg viewBox="-0.5 -0.5 19 19"fill=none xmlns=http://www.w3.org/2000/svg><path d="M8.96911 8.43875e-05H15.7293C16.0282 -0.00250361 16.3246 0.0544589 16.6012 0.167649C16.8779 0.28084 17.1292 0.447991 17.3406 0.659351C17.5519 0.870711 17.7191 1.12205 17.8323 1.39869C17.9455 1.67534 18.0024 1.97176 17.9998 2.27066V9.03081C17.9993 11.4094 17.0541 13.6904 15.3722 15.3723C13.6903 17.0543 11.4093 17.9994 9.03066 18H8.97965C7.80101 18.0013 6.63368 17.7704 5.54433 17.3204C4.45499 16.8703 3.46499 16.2101 2.63092 15.3773C1.7969 14.5445 1.13529 13.5553 0.68393 12.4665C0.232573 11.3777 0.000330039 10.2106 0.000488362 9.03192C0.000488362 4.06161 4.00934 0.0527591 8.96967 0.00174743L8.96911 8.43875e-05Z"fill=white stroke-width=0.75 stroke=currentColor></path><path d="M8.96907 4.00879C7.876 4.00879 6.8277 4.44301 6.05479 5.21592C5.28187 5.98884 4.84766 7.03713 4.84766 8.1302V10.9325C4.85584 11.2935 5.00288 11.6374 5.25817 11.8927C5.51347 12.148 5.85737 12.295 6.21832 12.3032H7.38438V9.08722H5.80967V8.18731C5.90559 6.49228 7.30786 5.16709 9.00566 5.16709C10.7035 5.16709 12.1107 6.49782 12.2017 8.18731V9.08722H10.6064V12.3032H11.6599V12.3542C11.6546 12.6889 11.5193 13.0083 11.2827 13.245C11.046 13.4817 10.7265 13.617 10.3918 13.6223H9.13319C9.03117 13.6223 8.91861 13.6733 8.91861 13.7759C8.92415 13.8868 9.01675 13.9854 9.13319 13.9904H10.4013C10.8344 13.9877 11.249 13.8144 11.5552 13.5081C11.8615 13.2019 12.0348 12.7873 12.0375 12.3542V12.2522C12.3382 12.1835 12.6065 12.0143 12.7981 11.7726C12.9896 11.5308 13.093 11.231 13.091 10.9225V8.18176C13.1481 5.85962 11.3073 4.00879 8.96907 4.00879Z"fill=#171717>`);
const FreshdeskIcon = (props) => {
  return (() => {
    var _el$ = _tmpl$$8();
    createRenderEffect((_p$) => {
      var _v$ = props.size, _v$2 = props.size;
      _v$ !== _p$.e && setAttribute(_el$, "width", _p$.e = _v$);
      _v$2 !== _p$.t && setAttribute(_el$, "height", _p$.t = _v$2);
      return _p$;
    }, {
      e: void 0,
      t: void 0
    });
    return _el$;
  })();
};

var _tmpl$$7 = /* @__PURE__ */ template(`<svg viewBox="-0.5 -0.5 33 33"fill=currentColor xmlns=http://www.w3.org/2000/svg><path d="M27.7076 17.6089C27.7076 17.8942 27.5955 18.1677 27.3959 18.3694C27.1962 18.5711 26.9255 18.6845 26.6432 18.6845C26.3609 18.6845 26.0902 18.5711 25.8906 18.3694C25.691 18.1677 25.5788 17.8942 25.5788 17.6089V8.00006C25.5788 7.71481 25.691 7.44123 25.8906 7.23953C26.0902 7.03782 26.3609 6.9245 26.6432 6.9245C26.9255 6.9245 27.1962 7.03782 27.3959 7.23953C27.5955 7.44123 27.7076 7.71481 27.7076 8.00006V17.6089ZM27.338 24.2667C27.1974 24.4089 23.2391 27.7422 15.9824 27.7422C8.72568 27.7422 4.79384 24.4267 4.62672 24.2844C4.5214 24.1947 4.43465 24.0848 4.37143 23.9612C4.30822 23.8374 4.26978 23.7024 4.25835 23.5635C4.24691 23.4249 4.26267 23.2852 4.30476 23.1526C4.34684 23.0201 4.41441 22.8972 4.50357 22.7911C4.68727 22.5763 4.94756 22.4437 5.22768 22.4219C5.5078 22.4003 5.78502 22.4915 5.9989 22.6755C6.06048 22.72 9.59648 25.6177 15.9736 25.6177C22.3507 25.6177 25.9131 22.7022 25.9484 22.6755C26.1646 22.4931 26.4428 22.4031 26.7236 22.4247C27.0045 22.4464 27.266 22.5777 27.4524 22.7911C27.6319 23.0027 27.7214 23.2774 27.7017 23.5553C27.682 23.8332 27.5545 24.0921 27.3469 24.2755L27.338 24.2667ZM4.24849 8.00006C4.26439 7.71391 4.39176 7.44574 4.60273 7.2542C4.8137 7.06265 5.09113 6.9633 5.37439 6.97784C5.63697 6.99291 5.88474 7.1057 6.0699 7.29445C6.25507 7.48321 6.36452 7.73458 6.37713 8.00006V17.5912C6.37713 17.8764 6.26499 18.1499 6.0654 18.3516C5.8658 18.5534 5.59508 18.6667 5.31281 18.6667C5.03054 18.6667 4.75982 18.5534 4.56022 18.3516C4.36063 18.1499 4.24849 17.8764 4.24849 17.5912V8.00006ZM9.58769 5.86673C9.60359 5.58057 9.73095 5.31242 9.94193 5.12088C10.1529 4.92933 10.4303 4.82997 10.7136 4.84451C10.9762 4.85959 11.2239 4.97237 11.4091 5.16113C11.5943 5.34989 11.7037 5.60124 11.7163 5.86673V20.0889C11.7163 20.3742 11.6042 20.6478 11.4046 20.8494C11.205 21.0511 10.9343 21.1644 10.652 21.1644C10.3697 21.1644 10.099 21.0511 9.89942 20.8494C9.69982 20.6478 9.58769 20.3742 9.58769 20.0889V5.86673ZM14.9533 5.3334C14.9533 5.04815 15.0654 4.77458 15.265 4.57288C15.4646 4.37116 15.7353 4.25785 16.0175 4.25785C16.2999 4.25785 16.5706 4.37116 16.7702 4.57288C16.9698 4.77458 17.0819 5.04815 17.0819 5.3334V20.8C17.0819 21.0852 16.9698 21.3588 16.7702 21.5606C16.5706 21.7623 16.2999 21.8755 16.0175 21.8755C15.7353 21.8755 15.4646 21.7623 15.265 21.5606C15.0654 21.3588 14.9533 21.0852 14.9533 20.8V5.3334ZM20.2309 5.86673C20.2309 5.58148 20.3431 5.30791 20.5427 5.1062C20.7423 4.9045 21.013 4.79118 21.2952 4.79118C21.5775 4.79118 21.8482 4.9045 22.0478 5.1062C22.2474 5.30791 22.3595 5.58148 22.3595 5.86673V20.0889C22.3595 20.3742 22.2474 20.6478 22.0478 20.8494C21.8482 21.0511 21.5775 21.1644 21.2952 21.1644C21.013 21.1644 20.7423 21.0511 20.5427 20.8494C20.3431 20.6478 20.2309 20.3742 20.2309 20.0889V5.86673ZM27.9978 8.87626e-05H4.00219C3.47934 -0.0034233 2.96094 0.0973183 2.47668 0.296545C1.99241 0.495772 1.55178 0.789573 1.18002 1.16112C0.808269 1.53268 0.512685 1.97467 0.3102 2.46183C0.107714 2.94897 0.00230199 3.4717 0 4.00008V28C0.00230199 28.5283 0.107714 29.0511 0.3102 29.5383C0.512685 30.0253 0.808269 30.4674 1.18002 30.8389C1.55178 31.2104 1.99241 31.5043 2.47668 31.7035C2.96094 31.9028 3.47934 32.0034 4.00219 31.9999H27.9978C28.5199 32.0034 29.0376 31.9031 29.5212 31.7043C30.005 31.5057 30.4452 31.2127 30.8169 30.842C31.1885 30.4715 31.4843 30.0305 31.6872 29.5443C31.8903 29.0583 31.9966 28.5365 31.9999 28.0089V4.00008C31.9977 3.47245 31.8926 2.95046 31.6906 2.46389C31.4887 1.97732 31.194 1.53572 30.823 1.16428C30.4522 0.792848 30.0127 0.498861 29.5294 0.299109C29.0462 0.0993562 28.5287 -0.00225101 28.0066 8.87626e-05"fill=currentColor>`);
const IntercomIcon$1 = (props) => {
  return (() => {
    var _el$ = _tmpl$$7();
    createRenderEffect((_p$) => {
      var _v$ = props.size, _v$2 = props.size;
      _v$ !== _p$.e && setAttribute(_el$, "width", _p$.e = _v$);
      _v$2 !== _p$.t && setAttribute(_el$, "height", _p$.t = _v$2);
      return _p$;
    }, {
      e: void 0,
      t: void 0
    });
    return _el$;
  })();
};

var _tmpl$$6 = /* @__PURE__ */ template(`<svg viewBox="0 0 2500 1895"fill=none xmlns=http://www.w3.org/2000/svg><path d="M1154.9 500.363V1894.83H0L1154.9 500.363ZM1154.9 0.111328C1154.9 318.794 896.752 576.942 578.069 576.942C259.386 576.942 0 318.794 0 0.111328H1154.9ZM1345.14 1894.89C1345.14 1576.21 1603.28 1318.06 1921.97 1318.06C2240.65 1318.06 2498.8 1576.21 2498.8 1894.89H1345.14ZM1345.14 1394.64V0.111328H2500L1345.14 1394.64Z"fill=currentColor>`);
const IntercomIcon = (props) => {
  return (() => {
    var _el$ = _tmpl$$6();
    createRenderEffect((_p$) => {
      var _v$ = props.size, _v$2 = props.size * 1895 / 2500;
      _v$ !== _p$.e && setAttribute(_el$, "width", _p$.e = _v$);
      _v$2 !== _p$.t && setAttribute(_el$, "height", _p$.t = _v$2);
      return _p$;
    }, {
      e: void 0,
      t: void 0
    });
    return _el$;
  })();
};

const ActionIcon = (props) => {
  const {
    action,
    className
  } = props;
  const commonProps = {
    className,
    "aria-hidden": true,
    alt: "",
    size: 24
  };
  switch (action) {
    case ActionType.CALL_PHONE:
      return createComponent(Icon, {
        name: "PhoneCall",
        "aria-hidden": true,
        classes: className
      });
    case ActionType.SEND_EMAIL:
      return createComponent(Icon, {
        name: "Envelope",
        "aria-hidden": true,
        classes: className
      });
    case ActionType.OPEN_URL:
      return createComponent(Icon, {
        name: "LinkSimple",
        "aria-hidden": true,
        classes: className
      });
    case ActionType.RUN_CALLBACK:
      return createComponent(Icon, {
        name: "Sparkle",
        "aria-hidden": true,
        classes: className
      });
    case ActionType.LAUNCH_ZENDESK_CHAT:
      return createComponent(IntercomIcon, commonProps);
    case ActionType.LAUNCH_ZENDESK_MESSENGER:
      return createComponent(Icon, {
        name: "Chats",
        "aria-hidden": true,
        classes: className
      });
    case ActionType.LAUNCH_INTERCOM_CHAT:
      return createComponent(IntercomIcon$1, commonProps);
    case ActionType.LAUNCH_FRESHDESK:
      return createComponent(FreshdeskIcon, commonProps);
    default:
      return null;
  }
};

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var classnames = {exports: {}};

/*!
	Copyright (c) 2018 Jed Watson.
	Licensed under the MIT License (MIT), see
	http://jedwatson.github.io/classnames
*/

var hasRequiredClassnames;

function requireClassnames () {
	if (hasRequiredClassnames) return classnames.exports;
	hasRequiredClassnames = 1;
	(function (module) {
		/* global define */

		(function () {

			var hasOwn = {}.hasOwnProperty;

			function classNames () {
				var classes = '';

				for (var i = 0; i < arguments.length; i++) {
					var arg = arguments[i];
					if (arg) {
						classes = appendClass(classes, parseValue(arg));
					}
				}

				return classes;
			}

			function parseValue (arg) {
				if (typeof arg === 'string' || typeof arg === 'number') {
					return arg;
				}

				if (typeof arg !== 'object') {
					return '';
				}

				if (Array.isArray(arg)) {
					return classNames.apply(null, arg);
				}

				if (arg.toString !== Object.prototype.toString && !arg.toString.toString().includes('[native code]')) {
					return arg.toString();
				}

				var classes = '';

				for (var key in arg) {
					if (hasOwn.call(arg, key) && arg[key]) {
						classes = appendClass(classes, key);
					}
				}

				return classes;
			}

			function appendClass (value, newClass) {
				if (!newClass) {
					return value;
				}
			
				if (value) {
					return value + ' ' + newClass;
				}
			
				return value + newClass;
			}

			if (module.exports) {
				classNames.default = classNames;
				module.exports = classNames;
			} else {
				window.classNames = classNames;
			}
		}()); 
	} (classnames));
	return classnames.exports;
}

var classnamesExports = requireClassnames();
const classNames = /*@__PURE__*/getDefaultExportFromCjs(classnamesExports);

var _tmpl$$5 = /* @__PURE__ */ template(`<a part=action-button><span> `), _tmpl$2$1 = /* @__PURE__ */ template(`<button part=action-button type=button><span>`);
const ActionButton = (props) => {
  props = mergeProps({
    action: {},
    buttonClasses: ""
  }, props);
  const [local, rest] = splitProps(props, ["action", "buttonClasses"]);
  const {
    action,
    buttonClasses
  } = local;
  const buttonLabel = action.action.options.title;
  let buttonRef;
  const handler = actionHandlers[action.type];
  if (action.type === ActionType.SEND_EMAIL) {
    return (() => {
      var _el$ = _tmpl$$5(), _el$2 = _el$.firstChild; _el$2.firstChild;
      var _ref$ = buttonRef;
      typeof _ref$ === "function" ? use(_ref$, _el$) : buttonRef = _el$;
      _el$.$$click = () => handler(buttonRef)(action);
      insert(_el$, createComponent(ActionIcon, {
        get action() {
          return action.type;
        },
        className: "shrink-0"
      }), _el$2);
      insert(_el$2, buttonLabel, null);
      createRenderEffect((_p$) => {
        var _v$ = classNames(styles$2.button, buttonClasses), _v$2 = `mailto:${action.action.options.to}`;
        _v$ !== _p$.e && className(_el$, _p$.e = _v$);
        _v$2 !== _p$.t && setAttribute(_el$, "href", _p$.t = _v$2);
        return _p$;
      }, {
        e: void 0,
        t: void 0
      });
      return _el$;
    })();
  }
  return (() => {
    var _el$4 = _tmpl$2$1(), _el$5 = _el$4.firstChild;
    _el$4.$$click = () => handler(buttonRef)(action);
    var _ref$2 = buttonRef;
    typeof _ref$2 === "function" ? use(_ref$2, _el$4) : buttonRef = _el$4;
    spread(_el$4, mergeProps({
      get ["class"]() {
        return classNames(styles$2.button, buttonClasses);
      }
    }, rest), false, true);
    insert(_el$4, createComponent(ActionIcon, {
      get action() {
        return action.type;
      },
      className: "shrink-0"
    }), _el$5);
    insert(_el$5, buttonLabel);
    return _el$4;
  })();
};
delegateEvents(["click"]);

/** Check if value is typeof "object" or "function" */
function isObject$1(value) {
    return value !== null && (typeof value === "object" || typeof value === "function");
}
/** If value is a function – call it with a given arguments – otherwise get the value as is */
function accessWith(valueOrFn, ...args) {
    return typeof valueOrFn === "function" ? valueOrFn(...args) : valueOrFn;
}

/**
 * A shallowly wrapped reactive store object. It behaves similarly to the createStore, but with limited features to keep it simple. Designed to be used for reactive objects with static keys, but dynamic values, like reactive Event State, location, etc.
 * @param init initial value of the store
 * @returns tuple with the store object and a setter function
 * ```ts
 * [access: T, write: StaticStoreSetter<T>]
 * ```
 * @see https://github.com/solidjs-community/solid-primitives/tree/main/packages/static-store#createStaticStore
 * @example
 * ```ts
 * const [size, setSize] = createStaticStore({ width: 0, height: 0 });
 *
 * el.addEventListener("resize", () => {
 *  setSize({ width: el.offsetWidth, height: el.offsetHeight });
 * });
 *
 * createEffect(() => {
 *   console.log(size.width, size.height);
 * })
 * ```
 */
function createStaticStore(init) {
    const copy = { ...init }, store = { ...init }, cache = {};
    const getValue = (key) => {
        let signal = cache[key];
        if (!signal) {
            if (!getListener())
                return copy[key];
            cache[key] = signal = createSignal(copy[key], { internal: true });
            delete copy[key];
        }
        return signal[0]();
    };
    for (const key in init) {
        Object.defineProperty(store, key, { get: () => getValue(key), enumerable: true });
    }
    const setValue = (key, value) => {
        const signal = cache[key];
        if (signal)
            return signal[1](value);
        if (key in copy)
            copy[key] = accessWith(value, copy[key]);
    };
    return [
        store,
        (a, b) => {
            if (isObject$1(a)) {
                const entries = untrack(() => Object.entries(accessWith(a, store)));
                batch(() => {
                    for (const [key, value] of entries)
                        setValue(key, () => value);
                });
            }
            else
                setValue(a, b);
            return store;
        },
    ];
}

const ELEMENT_SIZE_FALLBACK = { width: null, height: null };
/**
 * @param target html element
 * @returns object with width and height dimensions of provided {@link target} element.
 */
function getElementSize(target) {
    if (!target) {
        return { ...ELEMENT_SIZE_FALLBACK };
    }
    const { width, height } = target.getBoundingClientRect();
    return { width, height };
}
function createElementSize(target) {
    const isFn = typeof target === "function";
    const [size, setSize] = createStaticStore(sharedConfig.context || isFn ? ELEMENT_SIZE_FALLBACK : getElementSize(target));
    const ro = new ResizeObserver(([e]) => setSize(getElementSize(e.target)));
    onCleanup(() => ro.disconnect());
    if (isFn) {
        createEffect(() => {
            const el = target();
            if (el) {
                setSize(getElementSize(el));
                ro.observe(el);
                onCleanup(() => ro.unobserve(el));
            }
        });
    }
    else {
        ro.observe(target);
        onCleanup(() => ro.unobserve(target));
    }
    return size;
}

var _tmpl$$4 = /* @__PURE__ */ template(`<div><button part=action-button type=button><span>Ask a new question`);
const ActionButtons = (props) => {
  props = mergeProps({
    actions: [],
    className: "",
    buttonClasses: "",
    onAskNewQuestionClick: () => {
    }
  }, props);
  const [local, _rest] = splitProps(props, ["actions", "className", "buttonClasses", "onAskNewQuestionClick"]);
  const [containerRef, setContainerRef] = createSignal();
  let ref;
  const actions = local.actions.slice(0, 2);
  const es = createElementSize(containerRef);
  createEffect(() => {
    es.width;
    es.height;
  });
  return (() => {
    var _el$ = _tmpl$$4(), _el$2 = _el$.firstChild, _el$3 = _el$2.firstChild;
    use(setContainerRef, _el$);
    _el$2.$$click = () => {
      local.onAskNewQuestionClick?.();
      dispatchCustomEvent(ref, "AskNewQuestionButtonClick", {});
    };
    use((el) => {
      ref = el;
    }, _el$2);
    insert(_el$2, createComponent(Icon, {
      name: "Lightning",
      classes: "shrink-0",
      "aria-hidden": true
    }), _el$3);
    insert(_el$, createComponent(For, {
      each: actions,
      children: (action) => createComponent(ActionButton, {
        action,
        get buttonClasses() {
          return local.buttonClasses;
        }
      })
    }), null);
    createRenderEffect((_p$) => {
      var _v$ = classNames(styles$2.buttons, local.className, {
        [styles$2.wide]: es.width && es.width > 600,
        [styles$2.narrow]: es.width && es.width <= 600
      }), _v$2 = classNames(styles$2.button, local.buttonClasses, {
        [styles$2.colSpan2]: actions.length === 2 && es.width && es.width > 600
      });
      _v$ !== _p$.e && className(_el$, _p$.e = _v$);
      _v$2 !== _p$.t && className(_el$2, _p$.t = _v$2);
      return _p$;
    }, {
      e: void 0,
      t: void 0
    });
    return _el$;
  })();
};
delegateEvents(["click"]);

var awesomplete$1 = {exports: {}};

/**
 * Simple, lightweight, usable local autocomplete library for modern browsers
 * Because there weren’t enough autocomplete scripts in the world? Because I’m completely insane and have NIH syndrome? Probably both. :P
 * @author Lea Verou http://leaverou.github.io/awesomplete
 * MIT license
 */

var hasRequiredAwesomplete;

function requireAwesomplete () {
	if (hasRequiredAwesomplete) return awesomplete$1.exports;
	hasRequiredAwesomplete = 1;
	(function (module) {
		(function () {

		var _ = function (input, o) {
			var me = this;

			// Keep track of number of instances for unique IDs
			_.count = (_.count || 0) + 1;
			this.count = _.count;

			// Setup

			this.isOpened = false;

			this.input = $(input);
			this.input.setAttribute("autocomplete", "off");
			this.input.setAttribute("aria-autocomplete", "list");
			this.input.setAttribute("aria-expanded", "false");
			this.input.setAttribute("aria-controls", "awesomplete_list_" + this.count);
			this.input.setAttribute("aria-owns", "awesomplete_list_" + this.count);
			this.input.setAttribute("role", "combobox");

			// store constructor options in case we need to distinguish
			// between default and customized behavior later on
			this.options = o = o || {};

			configure(this, {
				minChars: 2,
				maxItems: 10,
				autoFirst: false,
				data: _.DATA,
				filter: _.FILTER_CONTAINS,
				sort: o.sort === false ? false : _.SORT_BYLENGTH,
				container: _.CONTAINER,
				item: _.ITEM,
				replace: _.REPLACE,
				tabSelect: false,
				listLabel: "Results List",
				statusNoResults: "No results found",
				statusXResults: "{0} results found", // uses index placeholder {0}
				statusTypeXChar: "Type {0} or more characters for results"
			}, o);

			this.index = -1;

			// Create necessary elements

			this.container = this.container(input);

			this.ul = $.create("ul", {
				hidden: "hidden",
				role: "listbox",
				id: "awesomplete_list_" + this.count,
				inside: this.container,
				"aria-label": this.listLabel
			});

			this.status = $.create("span", {
				className: "visually-hidden",
				role: "status",
				"aria-live": "assertive",
				"aria-atomic": true,
				inside: this.container,
				textContent: ""  // live region should start empty. Only when the text is changed it will be read by the screen reader.
			});

			// Bind events

			this._events = {
				input: {
					"input": this.evaluate.bind(this),
					"blur": this.close.bind(this, { reason: "blur" }),
					"keydown": function(evt) {
						var c = evt.keyCode;

						// If the dropdown `ul` is in view, then act on keydown for the following keys:
						// Enter / Esc / Up / Down
						if(me.opened) {
							if (c === 13 && me.selected) { // Enter
								evt.preventDefault();
								me.select(undefined, undefined, evt);
							}
							else if (c === 9 && me.selected && me.tabSelect) {
								evt.preventDefault();
								me.select(undefined, undefined, evt);
							}
							else if (c === 27) { // Esc
								me.close({ reason: "esc" });
							}
							else if (c === 38 || c === 40) { // Down/Up arrow
								evt.preventDefault();
								me[c === 38? "previous" : "next"]();
							}
						}
					}
				},
				form: {
					"submit": this.close.bind(this, { reason: "submit" })
				},
				ul: {
					// Prevent the default mousedowm, which ensures the input is not blurred.
					// The actual selection will happen on click. This also ensures dragging the
					// cursor away from the list item will cancel the selection
					"mousedown": function(evt) {
						evt.preventDefault();
					},
					// The click event is fired even if the corresponding mousedown event has called preventDefault
					"click": function(evt) {
						var li = evt.target;

						if (li !== this) {

							while (li && !/li/i.test(li.nodeName)) {
								li = li.parentNode;
							}

							if (li && evt.button === 0) {  // Only select on left click
								evt.preventDefault();
								me.select(li, evt.target, evt);
							}
						}
					}
				}
			};

			$.bind(this.input, this._events.input);
			$.bind(this.input.form, this._events.form);
			$.bind(this.ul, this._events.ul);

			if (this.input.hasAttribute("list")) {
				this.list = "#" + this.input.getAttribute("list");
				this.input.removeAttribute("list");
			}
			else {
				this.list = this.input.getAttribute("data-list") || o.list || [];
			}

			_.all.push(this);
		};

		_.prototype = {
			set list(list) {
				if (Array.isArray(list)) {
					this._list = list;
				}
				else if (typeof list === "string" && list.indexOf(",") > -1) {
						this._list = list.split(/\s*,\s*/);
				}
				else { // Element or CSS selector
					list = $(list);

					if (list && list.children) {
						var items = [];
						slice.apply(list.children).forEach(function (el) {
							if (!el.disabled) {
								var text = el.textContent.trim();
								var value = el.value || text;
								var label = el.label || text;
								if (value !== "") {
									items.push({ label: label, value: value });
								}
							}
						});
						this._list = items;
					}
				}

				if (document.activeElement === this.input) {
					this.evaluate();
				}
			},

			get selected() {
				return this.index > -1;
			},

			get opened() {
				return this.isOpened;
			},

			close: function (o) {
				if (!this.opened) {
					return;
				}

				this.input.setAttribute("aria-expanded", "false");
				this.ul.setAttribute("hidden", "");
				this.isOpened = false;
				this.index = -1;

				this.status.setAttribute("hidden", "");
				this.input.setAttribute("aria-activedescendant", "");

				$.fire(this.input, "awesomplete-close", o || {});
			},

			open: function () {
				this.input.setAttribute("aria-expanded", "true");
				this.ul.removeAttribute("hidden");
				this.isOpened = true;

				this.status.removeAttribute("hidden");

				if (this.autoFirst && this.index === -1) {
					this.goto(0);
				}

				$.fire(this.input, "awesomplete-open");
			},

			destroy: function() {
				//remove events from the input and its form
				$.unbind(this.input, this._events.input);
				$.unbind(this.input.form, this._events.form);

				// cleanup container if it was created by Awesomplete but leave it alone otherwise
				if (!this.options.container) {
					//move the input out of the awesomplete container and remove the container and its children
					var parentNode = this.container.parentNode;

					parentNode.insertBefore(this.input, this.container);
					parentNode.removeChild(this.container);
				}

				// remove autocomplete and aria attributes
				this.input.removeAttribute("autocomplete");
				this.input.removeAttribute("aria-autocomplete");
				this.input.removeAttribute("aria-expanded");
				this.input.removeAttribute("aria-controls");
				this.input.removeAttribute("aria-owns");		
				this.input.removeAttribute("role");

				//remove this awesomeplete instance from the global array of instances
				var indexOfAwesomplete = _.all.indexOf(this);

				if (indexOfAwesomplete !== -1) {
					_.all.splice(indexOfAwesomplete, 1);
				}
			},

			next: function () {
				var count = this.ul.children.length;
				this.goto(this.index < count - 1 ? this.index + 1 : (count ? 0 : -1) );
			},

			previous: function () {
				var count = this.ul.children.length;
				var pos = this.index - 1;

				this.goto(this.selected && pos !== -1 ? pos : count - 1);
			},

			// Should not be used, highlights specific item without any checks!
			goto: function (i) {
				var lis = this.ul.children;

				if (this.selected) {
					lis[this.index].setAttribute("aria-selected", "false");
				}

				this.index = i;

				if (i > -1 && lis.length > 0) {
					lis[i].setAttribute("aria-selected", "true");

					// fix: Turned off this status update.
					//		Screen readers Voiceover and Talkback won't read this status change.
					//		Narrator and NVDA do, but they already tell: 'X of Y (selected)'
					// this.status.textContent = lis[i].textContent + ", list item " + (i + 1) + " of " + lis.length;

					this.input.setAttribute("aria-activedescendant", this.ul.id + "_item_" + this.index);

					// scroll to highlighted element in case parent's height is fixed
					this.ul.scrollTop = lis[i].offsetTop - this.ul.clientHeight + lis[i].clientHeight;

					$.fire(this.input, "awesomplete-highlight", {
						text: this.suggestions[this.index]
					});
				}
			},

			select: function (selected, origin, originalEvent) {
				if (selected) {
					this.index = $.siblingIndex(selected);
				} else {
					selected = this.ul.children[this.index];
				}

				if (selected) {
					var suggestion = this.suggestions[this.index];

					var allowed = $.fire(this.input, "awesomplete-select", {
						text: suggestion,
						origin: origin || selected,
						originalEvent: originalEvent
					});

					if (allowed) {
						this.replace(suggestion);
						this.close({ reason: "select" });
						$.fire(this.input, "awesomplete-selectcomplete", {
							text: suggestion,
							originalEvent: originalEvent
						});
					}
				}
			},

			evaluate: function() {
				var me = this;
				var value = this.input.value;

				if (value.length >= this.minChars && this._list && this._list.length > 0) {
					this.index = -1;
					// Populate list with options that match
					this.ul.innerHTML = "";

					this.suggestions = this._list
						.map(function(item) {
							return new Suggestion(me.data(item, value));
						})
						.filter(function(item) {
							return me.filter(item, value);
						});

					if (this.sort !== false) {
						this.suggestions = this.suggestions.sort(this.sort);
					}

					this.suggestions = this.suggestions.slice(0, this.maxItems);

					this.suggestions.forEach(function(text, index) {
							me.ul.appendChild(me.item(text, value, index));
						});

					if (this.ul.children.length === 0) {

						this.status.textContent = this.statusNoResults;

						this.close({ reason: "nomatches" });

					} else {
						this.input.setAttribute("aria-activedescendant", ""); // none of the list items currently has aria-selected="true"

						this.open();

						this.status.textContent = this.statusXResults.replaceAll('{0}', this.ul.children.length); // N results found;
					}
				}
				else {

					this.close({ reason: "nomatches" });

					if (this.minChar <= 1 || value.length >= this.minChars) {
					   this.status.textContent = this.statusNoResults;
					} else {
					   this.status.textContent = this.statusTypeXChar.replaceAll('{0}', this.minChars); // Type N or more characters for results
					}

				}
			}
		};

		// Static methods/properties

		_.all = [];

		_.FILTER_CONTAINS = function (text, input) {
			return RegExp($.regExpEscape(input.trim()), "i").test(text);
		};

		_.FILTER_STARTSWITH = function (text, input) {
			return RegExp("^" + $.regExpEscape(input.trim()), "i").test(text);
		};

		_.SORT_BYLENGTH = function (a, b) {
			if (a.length !== b.length) {
				return a.length - b.length;
			}

			return a < b? -1 : 1;
		};

		_.CONTAINER = function (input) {
			return $.create("div", {
				className: "awesomplete",
				around: input
			});
		};

		_.ITEM = function (text, input, item_id) {
			var html = input.trim() === "" ? text : text.replace(RegExp($.regExpEscape(input.trim()), "gi"), "<mark>$&</mark>");
			return $.create("li", {
				innerHTML: html,
				"role": "option",
				"aria-selected": "false",
				"tabindex": "-1", // for the Talkback screen reader
				"id": "awesomplete_list_" + this.count + "_item_" + item_id
			});
		};

		_.REPLACE = function (text) {
			this.input.value = text.value;
		};

		_.DATA = function (item/*, input*/) { return item; };

		// Private functions

		function Suggestion(data) {
			var o = Array.isArray(data)
			  ? { label: data[0], value: data[1] }
			  : typeof data === "object" && "label" in data && "value" in data ? data : { label: data, value: data };

			this.label = o.label || o.value;
			this.value = o.value;
		}
		Object.defineProperty(Suggestion.prototype = Object.create(String.prototype), "length", {
			get: function() { return this.label.length; }
		});
		Suggestion.prototype.toString = Suggestion.prototype.valueOf = function () {
			return "" + this.label;
		};

		function configure(instance, properties, o) {
			for (var i in properties) {
				var initial = properties[i],
					attrValue = instance.input.getAttribute("data-" + i.toLowerCase());

				if (typeof initial === "number") {
					instance[i] = parseInt(attrValue);
				}
				else if (initial === false) { // Boolean options must be false by default anyway
					instance[i] = attrValue !== null;
				}
				else if (initial instanceof Function) {
					instance[i] = null;
				}
				else {
					instance[i] = attrValue;
				}

				if (!instance[i] && instance[i] !== 0) {
					instance[i] = (i in o)? o[i] : initial;
				}
			}
		}

		// Helpers

		var slice = Array.prototype.slice;

		function $(expr, con) {
			return typeof expr === "string"? (con || document).querySelector(expr) : expr || null;
		}

		function $$(expr, con) {
			return slice.call((con || document).querySelectorAll(expr));
		}

		$.create = function(tag, o) {
			var element = document.createElement(tag);

			for (var i in o) {
				var val = o[i];

				if (i === "inside") {
					$(val).appendChild(element);
				}
				else if (i === "around") {
					var ref = $(val);
					ref.parentNode.insertBefore(element, ref);
					element.appendChild(ref);

					if (ref.getAttribute("autofocus") != null) {
						ref.focus();
					}
				}
				else if (i in element) {
					element[i] = val;
				}
				else {
					element.setAttribute(i, val);
				}
			}

			return element;
		};

		$.bind = function(element, o) {
			if (element) {
				for (var event in o) {
					var callback = o[event];

					event.split(/\s+/).forEach(function (event) {
						element.addEventListener(event, callback);
					});
				}
			}
		};

		$.unbind = function(element, o) {
			if (element) {
				for (var event in o) {
					var callback = o[event];

					event.split(/\s+/).forEach(function(event) {
						element.removeEventListener(event, callback);
					});
				}
			}
		};

		$.fire = function(target, type, properties) {
			var evt = document.createEvent("HTMLEvents");

			evt.initEvent(type, true, true );

			for (var j in properties) {
				evt[j] = properties[j];
			}

			return target.dispatchEvent(evt);
		};

		$.regExpEscape = function (s) {
			return s.replace(/[-\\^$*+?.()|[\]{}]/g, "\\$&");
		};

		$.siblingIndex = function (el) {
			/* eslint-disable no-cond-assign */
			for (var i = 0; el = el.previousElementSibling; i++);
			return i;
		};

		// Initialization

		function init() {
			$$("input.awesomplete").forEach(function (input) {
				new _(input);
			});
		}

		// Make sure to export Awesomplete on self when in a browser
		if (typeof self !== "undefined") {
			self.Awesomplete = _;
		}

		// Are we in a browser? Check for Document constructor
		if (typeof Document !== "undefined") {
			// DOM already loaded?
			if (document.readyState !== "loading") {
				init();
			}
			else {
				// Wait for it
				document.addEventListener("DOMContentLoaded", init);
			}
		}

		_.$ = $;
		_.$$ = $$;

		// Expose Awesomplete as a CJS module
		if (module.exports) {
			module.exports = _;
		}

		return _;

		}()); 
	} (awesomplete$1));
	return awesomplete$1.exports;
}

var awesompleteExports = requireAwesomplete();
const Awesomplete = /*@__PURE__*/getDefaultExportFromCjs(awesompleteExports);

const form = "_form_642yp_1";
const awesomplete = "_awesomplete_642yp_12";
const searchIcon = "_searchIcon_642yp_16";
const clearButton = "_clearButton_642yp_22";
const hideClearButton = "_hideClearButton_642yp_28";
const input = "_input_642yp_32";
const styles$1 = {
	form: form,
	awesomplete: awesomplete,
	searchIcon: searchIcon,
	clearButton: clearButton,
	hideClearButton: hideClearButton,
	input: input};

const getSuggestions = async ({
  query,
  endpoint,
  country,
  requestOptions,
  collections,
  regionEnabled,
  limit = 10
}) => {
  let response;
  if (requestOptions) {
    response = await fetch(endpoint, {
      ...requestOptions,
      body: JSON.stringify({
        query: encodeURIComponent(query),
        limit,
        ...regionEnabled && {
          filter: {
            allowedRegions: [country]
          }
        }
      })
    });
  } else {
    response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        query,
        limit
      })
    });
  }
  const json = await response.json();
  const results = json?.response?.results || {};
  const suggestions = flatMap(results, (obj) => {
    let currentCollection = "";
    return Object.values(obj).reduce((acc, value) => {
      const { question, documentId: id, collectionId } = value;
      if (collections && collections?.length > 0) {
        if (currentCollection !== collectionId) {
          currentCollection = collectionId;
          const collectionName = collections.find(
            (c) => c.id === collectionId
          )?.name;
          acc.push({
            question: `${collectionName}`,
            id: collectionId,
            collectionId,
            disabled: true
          });
        }
      }
      acc.push({
        question,
        id,
        collectionId
      });
      return acc;
    }, []);
  });
  if (!collections) {
    return uniqBy(suggestions, "question");
  }
  return suggestions;
};

/**
 * Creates a callback that is debounced and cancellable. The debounced callback is called on **trailing** edge.
 *
 * The timeout will be automatically cleared on root dispose.
 *
 * @param callback The callback to debounce
 * @param wait The duration to debounce in milliseconds
 * @returns The debounced function
 *
 * @see https://github.com/solidjs-community/solid-primitives/tree/main/packages/scheduled#debounce
 *
 * @example
 * ```ts
 * const fn = debounce((message: string) => console.log(message), 250);
 * fn('Hello!');
 * fn.clear() // clears a timeout in progress
 * ```
 */
const debounce = (callback, wait) => {
    let timeoutId;
    const clear = () => clearTimeout(timeoutId);
    if (getOwner())
        onCleanup(clear);
    const debounced = (...args) => {
        if (timeoutId !== undefined)
            clear();
        timeoutId = setTimeout(() => callback(...args), wait);
    };
    return Object.assign(debounced, { clear });
};

var _tmpl$$3 = /* @__PURE__ */ template(`<svg xmlns=http://www.w3.org/2000/svg viewBox="0 0 256 256"><rect width=256 height=256 fill=none></rect><circle cx=112 cy=112 r=80 fill=none stroke=currentColor stroke-linecap=round stroke-linejoin=round stroke-width=16></circle><line x1=168.57 y1=168.57 x2=224 y2=224 fill=none stroke=currentColor stroke-linecap=round stroke-linejoin=round stroke-width=16>`);
const MagnifyingGlass = (props) => {
  return (() => {
    var _el$ = _tmpl$$3();
    createRenderEffect((_p$) => {
      var _v$ = props.size, _v$2 = props.size;
      _v$ !== _p$.e && setAttribute(_el$, "width", _p$.e = _v$);
      _v$2 !== _p$.t && setAttribute(_el$, "height", _p$.t = _v$2);
      return _p$;
    }, {
      e: void 0,
      t: void 0
    });
    return _el$;
  })();
};

var _tmpl$$2 = /* @__PURE__ */ template(`<svg xmlns=http://www.w3.org/2000/svg viewBox="0 0 256 256"><rect width=256 height=256 fill=none></rect><line x1=200 y1=56 x2=56 y2=200 stroke=currentColor stroke-linecap=round stroke-linejoin=round stroke-width=16></line><line x1=200 y1=200 x2=56 y2=56 stroke=currentColor stroke-linecap=round stroke-linejoin=round stroke-width=16>`);
const X = (props) => {
  return (() => {
    var _el$ = _tmpl$$2();
    createRenderEffect((_p$) => {
      var _v$ = props.size, _v$2 = props.size;
      _v$ !== _p$.e && setAttribute(_el$, "height", _p$.e = _v$);
      _v$2 !== _p$.t && setAttribute(_el$, "width", _p$.t = _v$2);
      return _p$;
    }, {
      e: void 0,
      t: void 0
    });
    return _el$;
  })();
};

var _tmpl$$1 = /* @__PURE__ */ template(`<form part=form><div></div><input part=input type=search autocorrect=on><button part=clear-button type=reset tabindex=-1 aria-label="Clear search"aria-hidden>`);
function escapeHTML(str) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}
function highlightAndEscape(label, input) {
  if (!input) return escapeHTML(label);
  const re = new RegExp(Awesomplete.$.regExpEscape(input), "gi");
  let marked = label.replace(re, (match) => `@@@${match}@@@`);
  marked = escapeHTML(marked);
  return marked.replace(/@@@(.*?)@@@/g, "<mark>$1</mark>");
}
const DEBOUNCE_TIME = 300;
const AutocompleteSearchInput = (props) => {
  props = mergeProps({
    id: "",
    getSuggestionsOptions: {
      endpoint: "http://localhost:3000/api/searches.site.helpmeta"
    },
    placeholder: "Ask a question or type a search term...",
    question: "",
    autoFocus: false,
    onInput: () => {
    },
    onSearch: () => {
    },
    onClearSearch: () => {
    }
  }, props);
  const [local] = splitProps(props, ["id", "getSuggestionsOptions", "placeholder", "question", "onInput", "onSearch", "onClearSearch", "autoFocus"]);
  const [searchTerm, setSearchTerm] = createSignal(local.question || "");
  const [country, setCountry] = createSignal(null);
  const clearable = createMemo(() => searchTerm().length > 0);
  let inputRef;
  let awesomplete;
  const suggestionsToList = (suggestions) => {
    const list = suggestions.map((item) => ({
      label: item.question,
      value: item.question
    }));
    list.unshift({
      label: `Search for "${searchTerm()}"`,
      value: searchTerm()
    });
    return list;
  };
  const fetchSuggestions = async (id, query, country2) => {
    if (awesomplete && query.length > 1) {
      const cacheKey = `${id}_${query}_${country2}`;
      const cachedData = sessionStorage.getItem(cacheKey);
      if (cachedData) {
        const cachedSuggestions = JSON.parse(cachedData);
        if (cachedSuggestions.length > 0) {
          awesomplete.list = suggestionsToList(cachedSuggestions);
          awesomplete.evaluate();
          return;
        }
      }
      let suggestions = [];
      try {
        suggestions = await getSuggestions({
          ...local.getSuggestionsOptions,
          query,
          country: country2
        });
        if (suggestions.length > 0) {
          sessionStorage.setItem(cacheKey, JSON.stringify(suggestions));
        }
      } catch (error) {
        console.error("Error fetching suggestions:", error);
      } finally {
        awesomplete.list = suggestionsToList(suggestions);
        awesomplete.evaluate();
      }
    }
  };
  const debouncedFetchSuggestions = debounce(fetchSuggestions, DEBOUNCE_TIME);
  const handleInput = (e) => {
    const target = e.target;
    if (awesomplete) {
      const currentList = awesomplete._list;
      const searchForItem = {
        label: `Search for "${target.value}"`,
        value: target.value
      };
      const updatedList = currentList.splice(1);
      updatedList.unshift(searchForItem);
      awesomplete.list = updatedList;
      requestAnimationFrame(() => {
        awesomplete?.open();
        awesomplete?.evaluate();
      });
    }
    debouncedFetchSuggestions(local.id, target.value, country());
    setSearchTerm(target.value);
    local.onInput?.(target.value);
  };
  const handleSearch = (e) => {
    e.preventDefault();
    const query = searchTerm();
    if (inputRef) {
      dispatchCustomEvent(inputRef, "search", {
        query
      });
      setTimeout(() => {
        awesomplete?.close();
      }, DEBOUNCE_TIME + 250);
    }
    local.onSearch?.(query);
  };
  const clearSearch = () => {
    setSearchTerm("");
    if (inputRef) {
      inputRef.focus();
      dispatchCustomEvent(inputRef, "clearSearch", {});
    }
    local.onClearSearch?.();
  };
  const controller = new AbortController();
  const initAwesomplete = async () => {
    if (!inputRef) return;
    const fetchedCountry = await getCountryFromBrowser();
    setCountry(fetchedCountry);
    awesomplete = new Awesomplete(inputRef, {
      minChars: 2,
      container: (input) => {
        const container = document.createElement("div");
        container.classList.add(styles$1.awesomplete, "awesomplete");
        input.parentNode?.insertBefore(container, input);
        container.appendChild(input);
        if (local.autoFocus) {
          input.focus();
        }
        return container;
      },
      // Array.prototype.sort() function to sort 'Search for "query"' to the top
      sort: (a, b) => {
        if (a.label.startsWith("Search for")) {
          return -1;
        }
        if (b.label.startsWith("Search for")) {
          return 1;
        }
        return 0;
      },
      // Override 'item' to ensure XSS safety and correct highlighting:
      // - Escapes all HTML in the label to prevent injection
      // - Highlights only the matching part by inserting <mark> after escaping
      // - Necessary because the default Awesomplete implementation does not escape, and escaping in 'data' breaks highlighting
      item(text, input, item_id) {
        const label = typeof text === "object" && text !== null && "label" in text ? text.label : String(text);
        const html = highlightAndEscape(label, input);
        const count = awesomplete?.count ?? "custom";
        return Awesomplete.$.create("li", {
          innerHTML: html,
          role: "option",
          "aria-selected": "false",
          tabindex: "-1",
          id: "awesomplete_list_" + count + "_item_" + item_id
        });
      }
    });
    const signal = controller.signal;
    inputRef.addEventListener("awesomplete-highlight", (e) => {
      const highlightedText = e.text.value;
      setTimeout(() => {
        setSearchTerm(highlightedText);
      }, 100);
    }, {
      signal
    });
    inputRef.addEventListener(
      "awesomplete-select",
      // Event emitted by Awesomplete when a suggestion is selected with mouse or enter key
      (e) => {
        const selectedText = e.text.value;
        setSearchTerm(selectedText);
        handleSearch(e);
      },
      {
        signal
      }
    );
  };
  onCleanup(() => {
    controller.abort();
    awesomplete?.destroy();
  });
  createEffect(() => {
    setSearchTerm(local.question ?? "");
    if (local.question) {
      inputRef?.focus();
    }
  });
  return (() => {
    var _el$ = _tmpl$$1(), _el$2 = _el$.firstChild, _el$3 = _el$2.nextSibling, _el$4 = _el$3.nextSibling;
    _el$.addEventListener("submit", handleSearch);
    insert(_el$2, createComponent(MagnifyingGlass, {
      size: 20
    }));
    _el$3.$$keydown = (e) => {
      e.stopPropagation();
      if (e.key === "Escape") {
        if (clearable()) {
          e.stopImmediatePropagation();
          clearSearch();
        }
      }
    };
    _el$3.$$input = handleInput;
    use((el) => {
      inputRef = el;
      void initAwesomplete();
    }, _el$3);
    _el$4.$$click = clearSearch;
    insert(_el$4, createComponent(X, {
      size: 20
    }));
    createRenderEffect((_p$) => {
      var _v$ = styles$1.form, _v$2 = styles$1.searchIcon, _v$3 = local.placeholder, _v$4 = styles$1.input, _v$5 = classNames(
        styles$1.clearButton,
        clearable() ? "" : styles$1.hideClearButton
        // Hide the clear button when not clearable
      );
      _v$ !== _p$.e && className(_el$, _p$.e = _v$);
      _v$2 !== _p$.t && className(_el$2, _p$.t = _v$2);
      _v$3 !== _p$.a && setAttribute(_el$3, "placeholder", _p$.a = _v$3);
      _v$4 !== _p$.o && className(_el$3, _p$.o = _v$4);
      _v$5 !== _p$.i && className(_el$4, _p$.i = _v$5);
      return _p$;
    }, {
      e: void 0,
      t: void 0,
      a: void 0,
      o: void 0,
      i: void 0
    });
    createRenderEffect(() => _el$3.value = searchTerm());
    return _el$;
  })();
};
delegateEvents(["input", "keydown", "click"]);

const toast = "_toast_im2s2_1";
const closeButton = "_closeButton_im2s2_10";
const container = "_container_im2s2_16";
const headerIcon = "_headerIcon_im2s2_20";
const content = "_content_im2s2_25";
const title = "_title_im2s2_30";
const ctaBlocks = "_ctaBlocks_im2s2_35";
const ctaBlock = "_ctaBlock_im2s2_35";
const styles = {
	toast: toast,
	closeButton: closeButton,
	container: container,
	headerIcon: headerIcon,
	content: content,
	title: title,
	ctaBlocks: ctaBlocks,
	ctaBlock: ctaBlock
};

const PACKET_TYPES = Object.create(null); // no Map = no polyfill
PACKET_TYPES["open"] = "0";
PACKET_TYPES["close"] = "1";
PACKET_TYPES["ping"] = "2";
PACKET_TYPES["pong"] = "3";
PACKET_TYPES["message"] = "4";
PACKET_TYPES["upgrade"] = "5";
PACKET_TYPES["noop"] = "6";
const PACKET_TYPES_REVERSE = Object.create(null);
Object.keys(PACKET_TYPES).forEach((key) => {
    PACKET_TYPES_REVERSE[PACKET_TYPES[key]] = key;
});
const ERROR_PACKET = { type: "error", data: "parser error" };

const withNativeBlob$1 = typeof Blob === "function" ||
    (typeof Blob !== "undefined" &&
        Object.prototype.toString.call(Blob) === "[object BlobConstructor]");
const withNativeArrayBuffer$2 = typeof ArrayBuffer === "function";
// ArrayBuffer.isView method is not defined in IE10
const isView$1 = (obj) => {
    return typeof ArrayBuffer.isView === "function"
        ? ArrayBuffer.isView(obj)
        : obj && obj.buffer instanceof ArrayBuffer;
};
const encodePacket = ({ type, data }, supportsBinary, callback) => {
    if (withNativeBlob$1 && data instanceof Blob) {
        if (supportsBinary) {
            return callback(data);
        }
        else {
            return encodeBlobAsBase64(data, callback);
        }
    }
    else if (withNativeArrayBuffer$2 &&
        (data instanceof ArrayBuffer || isView$1(data))) {
        if (supportsBinary) {
            return callback(data);
        }
        else {
            return encodeBlobAsBase64(new Blob([data]), callback);
        }
    }
    // plain string
    return callback(PACKET_TYPES[type] + (data || ""));
};
const encodeBlobAsBase64 = (data, callback) => {
    const fileReader = new FileReader();
    fileReader.onload = function () {
        const content = fileReader.result.split(",")[1];
        callback("b" + (content || ""));
    };
    return fileReader.readAsDataURL(data);
};
function toArray(data) {
    if (data instanceof Uint8Array) {
        return data;
    }
    else if (data instanceof ArrayBuffer) {
        return new Uint8Array(data);
    }
    else {
        return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
    }
}
let TEXT_ENCODER;
function encodePacketToBinary(packet, callback) {
    if (withNativeBlob$1 && packet.data instanceof Blob) {
        return packet.data.arrayBuffer().then(toArray).then(callback);
    }
    else if (withNativeArrayBuffer$2 &&
        (packet.data instanceof ArrayBuffer || isView$1(packet.data))) {
        return callback(toArray(packet.data));
    }
    encodePacket(packet, false, (encoded) => {
        if (!TEXT_ENCODER) {
            TEXT_ENCODER = new TextEncoder();
        }
        callback(TEXT_ENCODER.encode(encoded));
    });
}

// imported from https://github.com/socketio/base64-arraybuffer
const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
// Use a lookup table to find the index.
const lookup$1 = typeof Uint8Array === 'undefined' ? [] : new Uint8Array(256);
for (let i = 0; i < chars.length; i++) {
    lookup$1[chars.charCodeAt(i)] = i;
}
const decode$1 = (base64) => {
    let bufferLength = base64.length * 0.75, len = base64.length, i, p = 0, encoded1, encoded2, encoded3, encoded4;
    if (base64[base64.length - 1] === '=') {
        bufferLength--;
        if (base64[base64.length - 2] === '=') {
            bufferLength--;
        }
    }
    const arraybuffer = new ArrayBuffer(bufferLength), bytes = new Uint8Array(arraybuffer);
    for (i = 0; i < len; i += 4) {
        encoded1 = lookup$1[base64.charCodeAt(i)];
        encoded2 = lookup$1[base64.charCodeAt(i + 1)];
        encoded3 = lookup$1[base64.charCodeAt(i + 2)];
        encoded4 = lookup$1[base64.charCodeAt(i + 3)];
        bytes[p++] = (encoded1 << 2) | (encoded2 >> 4);
        bytes[p++] = ((encoded2 & 15) << 4) | (encoded3 >> 2);
        bytes[p++] = ((encoded3 & 3) << 6) | (encoded4 & 63);
    }
    return arraybuffer;
};

const withNativeArrayBuffer$1 = typeof ArrayBuffer === "function";
const decodePacket = (encodedPacket, binaryType) => {
    if (typeof encodedPacket !== "string") {
        return {
            type: "message",
            data: mapBinary(encodedPacket, binaryType),
        };
    }
    const type = encodedPacket.charAt(0);
    if (type === "b") {
        return {
            type: "message",
            data: decodeBase64Packet(encodedPacket.substring(1), binaryType),
        };
    }
    const packetType = PACKET_TYPES_REVERSE[type];
    if (!packetType) {
        return ERROR_PACKET;
    }
    return encodedPacket.length > 1
        ? {
            type: PACKET_TYPES_REVERSE[type],
            data: encodedPacket.substring(1),
        }
        : {
            type: PACKET_TYPES_REVERSE[type],
        };
};
const decodeBase64Packet = (data, binaryType) => {
    if (withNativeArrayBuffer$1) {
        const decoded = decode$1(data);
        return mapBinary(decoded, binaryType);
    }
    else {
        return { base64: true, data }; // fallback for old browsers
    }
};
const mapBinary = (data, binaryType) => {
    switch (binaryType) {
        case "blob":
            if (data instanceof Blob) {
                // from WebSocket + binaryType "blob"
                return data;
            }
            else {
                // from HTTP long-polling or WebTransport
                return new Blob([data]);
            }
        case "arraybuffer":
        default:
            if (data instanceof ArrayBuffer) {
                // from HTTP long-polling (base64) or WebSocket + binaryType "arraybuffer"
                return data;
            }
            else {
                // from WebTransport (Uint8Array)
                return data.buffer;
            }
    }
};

const SEPARATOR = String.fromCharCode(30); // see https://en.wikipedia.org/wiki/Delimiter#ASCII_delimited_text
const encodePayload = (packets, callback) => {
    // some packets may be added to the array while encoding, so the initial length must be saved
    const length = packets.length;
    const encodedPackets = new Array(length);
    let count = 0;
    packets.forEach((packet, i) => {
        // force base64 encoding for binary packets
        encodePacket(packet, false, (encodedPacket) => {
            encodedPackets[i] = encodedPacket;
            if (++count === length) {
                callback(encodedPackets.join(SEPARATOR));
            }
        });
    });
};
const decodePayload = (encodedPayload, binaryType) => {
    const encodedPackets = encodedPayload.split(SEPARATOR);
    const packets = [];
    for (let i = 0; i < encodedPackets.length; i++) {
        const decodedPacket = decodePacket(encodedPackets[i], binaryType);
        packets.push(decodedPacket);
        if (decodedPacket.type === "error") {
            break;
        }
    }
    return packets;
};
function createPacketEncoderStream() {
    return new TransformStream({
        transform(packet, controller) {
            encodePacketToBinary(packet, (encodedPacket) => {
                const payloadLength = encodedPacket.length;
                let header;
                // inspired by the WebSocket format: https://developer.mozilla.org/en-US/docs/Web/API/WebSockets_API/Writing_WebSocket_servers#decoding_payload_length
                if (payloadLength < 126) {
                    header = new Uint8Array(1);
                    new DataView(header.buffer).setUint8(0, payloadLength);
                }
                else if (payloadLength < 65536) {
                    header = new Uint8Array(3);
                    const view = new DataView(header.buffer);
                    view.setUint8(0, 126);
                    view.setUint16(1, payloadLength);
                }
                else {
                    header = new Uint8Array(9);
                    const view = new DataView(header.buffer);
                    view.setUint8(0, 127);
                    view.setBigUint64(1, BigInt(payloadLength));
                }
                // first bit indicates whether the payload is plain text (0) or binary (1)
                if (packet.data && typeof packet.data !== "string") {
                    header[0] |= 0x80;
                }
                controller.enqueue(header);
                controller.enqueue(encodedPacket);
            });
        },
    });
}
let TEXT_DECODER;
function totalLength(chunks) {
    return chunks.reduce((acc, chunk) => acc + chunk.length, 0);
}
function concatChunks(chunks, size) {
    if (chunks[0].length === size) {
        return chunks.shift();
    }
    const buffer = new Uint8Array(size);
    let j = 0;
    for (let i = 0; i < size; i++) {
        buffer[i] = chunks[0][j++];
        if (j === chunks[0].length) {
            chunks.shift();
            j = 0;
        }
    }
    if (chunks.length && j < chunks[0].length) {
        chunks[0] = chunks[0].slice(j);
    }
    return buffer;
}
function createPacketDecoderStream(maxPayload, binaryType) {
    if (!TEXT_DECODER) {
        TEXT_DECODER = new TextDecoder();
    }
    const chunks = [];
    let state = 0 /* State.READ_HEADER */;
    let expectedLength = -1;
    let isBinary = false;
    return new TransformStream({
        transform(chunk, controller) {
            chunks.push(chunk);
            while (true) {
                if (state === 0 /* State.READ_HEADER */) {
                    if (totalLength(chunks) < 1) {
                        break;
                    }
                    const header = concatChunks(chunks, 1);
                    isBinary = (header[0] & 0x80) === 0x80;
                    expectedLength = header[0] & 0x7f;
                    if (expectedLength < 126) {
                        state = 3 /* State.READ_PAYLOAD */;
                    }
                    else if (expectedLength === 126) {
                        state = 1 /* State.READ_EXTENDED_LENGTH_16 */;
                    }
                    else {
                        state = 2 /* State.READ_EXTENDED_LENGTH_64 */;
                    }
                }
                else if (state === 1 /* State.READ_EXTENDED_LENGTH_16 */) {
                    if (totalLength(chunks) < 2) {
                        break;
                    }
                    const headerArray = concatChunks(chunks, 2);
                    expectedLength = new DataView(headerArray.buffer, headerArray.byteOffset, headerArray.length).getUint16(0);
                    state = 3 /* State.READ_PAYLOAD */;
                }
                else if (state === 2 /* State.READ_EXTENDED_LENGTH_64 */) {
                    if (totalLength(chunks) < 8) {
                        break;
                    }
                    const headerArray = concatChunks(chunks, 8);
                    const view = new DataView(headerArray.buffer, headerArray.byteOffset, headerArray.length);
                    const n = view.getUint32(0);
                    if (n > Math.pow(2, 53 - 32) - 1) {
                        // the maximum safe integer in JavaScript is 2^53 - 1
                        controller.enqueue(ERROR_PACKET);
                        break;
                    }
                    expectedLength = n * Math.pow(2, 32) + view.getUint32(4);
                    state = 3 /* State.READ_PAYLOAD */;
                }
                else {
                    if (totalLength(chunks) < expectedLength) {
                        break;
                    }
                    const data = concatChunks(chunks, expectedLength);
                    controller.enqueue(decodePacket(isBinary ? data : TEXT_DECODER.decode(data), binaryType));
                    state = 0 /* State.READ_HEADER */;
                }
                if (expectedLength === 0 || expectedLength > maxPayload) {
                    controller.enqueue(ERROR_PACKET);
                    break;
                }
            }
        },
    });
}
const protocol = 4;

/**
 * Initialize a new `Emitter`.
 *
 * @api public
 */

function Emitter(obj) {
  if (obj) return mixin(obj);
}

/**
 * Mixin the emitter properties.
 *
 * @param {Object} obj
 * @return {Object}
 * @api private
 */

function mixin(obj) {
  for (var key in Emitter.prototype) {
    obj[key] = Emitter.prototype[key];
  }
  return obj;
}

/**
 * Listen on the given `event` with `fn`.
 *
 * @param {String} event
 * @param {Function} fn
 * @return {Emitter}
 * @api public
 */

Emitter.prototype.on =
Emitter.prototype.addEventListener = function(event, fn){
  this._callbacks = this._callbacks || {};
  (this._callbacks['$' + event] = this._callbacks['$' + event] || [])
    .push(fn);
  return this;
};

/**
 * Adds an `event` listener that will be invoked a single
 * time then automatically removed.
 *
 * @param {String} event
 * @param {Function} fn
 * @return {Emitter}
 * @api public
 */

Emitter.prototype.once = function(event, fn){
  function on() {
    this.off(event, on);
    fn.apply(this, arguments);
  }

  on.fn = fn;
  this.on(event, on);
  return this;
};

/**
 * Remove the given callback for `event` or all
 * registered callbacks.
 *
 * @param {String} event
 * @param {Function} fn
 * @return {Emitter}
 * @api public
 */

Emitter.prototype.off =
Emitter.prototype.removeListener =
Emitter.prototype.removeAllListeners =
Emitter.prototype.removeEventListener = function(event, fn){
  this._callbacks = this._callbacks || {};

  // all
  if (0 == arguments.length) {
    this._callbacks = {};
    return this;
  }

  // specific event
  var callbacks = this._callbacks['$' + event];
  if (!callbacks) return this;

  // remove all handlers
  if (1 == arguments.length) {
    delete this._callbacks['$' + event];
    return this;
  }

  // remove specific handler
  var cb;
  for (var i = 0; i < callbacks.length; i++) {
    cb = callbacks[i];
    if (cb === fn || cb.fn === fn) {
      callbacks.splice(i, 1);
      break;
    }
  }

  // Remove event specific arrays for event types that no
  // one is subscribed for to avoid memory leak.
  if (callbacks.length === 0) {
    delete this._callbacks['$' + event];
  }

  return this;
};

/**
 * Emit `event` with the given args.
 *
 * @param {String} event
 * @param {Mixed} ...
 * @return {Emitter}
 */

Emitter.prototype.emit = function(event){
  this._callbacks = this._callbacks || {};

  var args = new Array(arguments.length - 1)
    , callbacks = this._callbacks['$' + event];

  for (var i = 1; i < arguments.length; i++) {
    args[i - 1] = arguments[i];
  }

  if (callbacks) {
    callbacks = callbacks.slice(0);
    for (var i = 0, len = callbacks.length; i < len; ++i) {
      callbacks[i].apply(this, args);
    }
  }

  return this;
};

// alias used for reserved events (protected method)
Emitter.prototype.emitReserved = Emitter.prototype.emit;

/**
 * Return array of callbacks for `event`.
 *
 * @param {String} event
 * @return {Array}
 * @api public
 */

Emitter.prototype.listeners = function(event){
  this._callbacks = this._callbacks || {};
  return this._callbacks['$' + event] || [];
};

/**
 * Check if this emitter has `event` handlers.
 *
 * @param {String} event
 * @return {Boolean}
 * @api public
 */

Emitter.prototype.hasListeners = function(event){
  return !! this.listeners(event).length;
};

const nextTick = (() => {
    const isPromiseAvailable = typeof Promise === "function" && typeof Promise.resolve === "function";
    if (isPromiseAvailable) {
        return (cb) => Promise.resolve().then(cb);
    }
    else {
        return (cb, setTimeoutFn) => setTimeoutFn(cb, 0);
    }
})();
const globalThisShim = (() => {
    if (typeof self !== "undefined") {
        return self;
    }
    else if (typeof window !== "undefined") {
        return window;
    }
    else {
        return Function("return this")();
    }
})();
const defaultBinaryType = "arraybuffer";
function createCookieJar() { }

function pick(obj, ...attr) {
    return attr.reduce((acc, k) => {
        if (obj.hasOwnProperty(k)) {
            acc[k] = obj[k];
        }
        return acc;
    }, {});
}
// Keep a reference to the real timeout functions so they can be used when overridden
const NATIVE_SET_TIMEOUT = globalThisShim.setTimeout;
const NATIVE_CLEAR_TIMEOUT = globalThisShim.clearTimeout;
function installTimerFunctions(obj, opts) {
    if (opts.useNativeTimers) {
        obj.setTimeoutFn = NATIVE_SET_TIMEOUT.bind(globalThisShim);
        obj.clearTimeoutFn = NATIVE_CLEAR_TIMEOUT.bind(globalThisShim);
    }
    else {
        obj.setTimeoutFn = globalThisShim.setTimeout.bind(globalThisShim);
        obj.clearTimeoutFn = globalThisShim.clearTimeout.bind(globalThisShim);
    }
}
// base64 encoded buffers are about 33% bigger (https://en.wikipedia.org/wiki/Base64)
const BASE64_OVERHEAD = 1.33;
// we could also have used `new Blob([obj]).size`, but it isn't supported in IE9
function byteLength(obj) {
    if (typeof obj === "string") {
        return utf8Length(obj);
    }
    // arraybuffer or blob
    return Math.ceil((obj.byteLength || obj.size) * BASE64_OVERHEAD);
}
function utf8Length(str) {
    let c = 0, length = 0;
    for (let i = 0, l = str.length; i < l; i++) {
        c = str.charCodeAt(i);
        if (c < 0x80) {
            length += 1;
        }
        else if (c < 0x800) {
            length += 2;
        }
        else if (c < 0xd800 || c >= 0xe000) {
            length += 3;
        }
        else {
            i++;
            length += 4;
        }
    }
    return length;
}
/**
 * Generates a random 8-characters string.
 */
function randomString() {
    return (Date.now().toString(36).substring(3) +
        Math.random().toString(36).substring(2, 5));
}

// imported from https://github.com/galkn/querystring
/**
 * Compiles a querystring
 * Returns string representation of the object
 *
 * @param {Object}
 * @api private
 */
function encode(obj) {
    let str = '';
    for (let i in obj) {
        if (obj.hasOwnProperty(i)) {
            if (str.length)
                str += '&';
            str += encodeURIComponent(i) + '=' + encodeURIComponent(obj[i]);
        }
    }
    return str;
}
/**
 * Parses a simple querystring into an object
 *
 * @param {String} qs
 * @api private
 */
function decode(qs) {
    let qry = {};
    let pairs = qs.split('&');
    for (let i = 0, l = pairs.length; i < l; i++) {
        let pair = pairs[i].split('=');
        qry[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
    }
    return qry;
}

class TransportError extends Error {
    constructor(reason, description, context) {
        super(reason);
        this.description = description;
        this.context = context;
        this.type = "TransportError";
    }
}
class Transport extends Emitter {
    /**
     * Transport abstract constructor.
     *
     * @param {Object} opts - options
     * @protected
     */
    constructor(opts) {
        super();
        this.writable = false;
        installTimerFunctions(this, opts);
        this.opts = opts;
        this.query = opts.query;
        this.socket = opts.socket;
        this.supportsBinary = !opts.forceBase64;
    }
    /**
     * Emits an error.
     *
     * @param {String} reason
     * @param description
     * @param context - the error context
     * @return {Transport} for chaining
     * @protected
     */
    onError(reason, description, context) {
        super.emitReserved("error", new TransportError(reason, description, context));
        return this;
    }
    /**
     * Opens the transport.
     */
    open() {
        this.readyState = "opening";
        this.doOpen();
        return this;
    }
    /**
     * Closes the transport.
     */
    close() {
        if (this.readyState === "opening" || this.readyState === "open") {
            this.doClose();
            this.onClose();
        }
        return this;
    }
    /**
     * Sends multiple packets.
     *
     * @param {Array} packets
     */
    send(packets) {
        if (this.readyState === "open") {
            this.write(packets);
        }
    }
    /**
     * Called upon open
     *
     * @protected
     */
    onOpen() {
        this.readyState = "open";
        this.writable = true;
        super.emitReserved("open");
    }
    /**
     * Called with data.
     *
     * @param {String} data
     * @protected
     */
    onData(data) {
        const packet = decodePacket(data, this.socket.binaryType);
        this.onPacket(packet);
    }
    /**
     * Called with a decoded packet.
     *
     * @protected
     */
    onPacket(packet) {
        super.emitReserved("packet", packet);
    }
    /**
     * Called upon close.
     *
     * @protected
     */
    onClose(details) {
        this.readyState = "closed";
        super.emitReserved("close", details);
    }
    /**
     * Pauses the transport, in order not to lose packets during an upgrade.
     *
     * @param onPause
     */
    pause(onPause) { }
    createUri(schema, query = {}) {
        return (schema +
            "://" +
            this._hostname() +
            this._port() +
            this.opts.path +
            this._query(query));
    }
    _hostname() {
        const hostname = this.opts.hostname;
        return hostname.indexOf(":") === -1 ? hostname : "[" + hostname + "]";
    }
    _port() {
        if (this.opts.port &&
            ((this.opts.secure && Number(this.opts.port !== 443)) ||
                (!this.opts.secure && Number(this.opts.port) !== 80))) {
            return ":" + this.opts.port;
        }
        else {
            return "";
        }
    }
    _query(query) {
        const encodedQuery = encode(query);
        return encodedQuery.length ? "?" + encodedQuery : "";
    }
}

class Polling extends Transport {
    constructor() {
        super(...arguments);
        this._polling = false;
    }
    get name() {
        return "polling";
    }
    /**
     * Opens the socket (triggers polling). We write a PING message to determine
     * when the transport is open.
     *
     * @protected
     */
    doOpen() {
        this._poll();
    }
    /**
     * Pauses polling.
     *
     * @param {Function} onPause - callback upon buffers are flushed and transport is paused
     * @package
     */
    pause(onPause) {
        this.readyState = "pausing";
        const pause = () => {
            this.readyState = "paused";
            onPause();
        };
        if (this._polling || !this.writable) {
            let total = 0;
            if (this._polling) {
                total++;
                this.once("pollComplete", function () {
                    --total || pause();
                });
            }
            if (!this.writable) {
                total++;
                this.once("drain", function () {
                    --total || pause();
                });
            }
        }
        else {
            pause();
        }
    }
    /**
     * Starts polling cycle.
     *
     * @private
     */
    _poll() {
        this._polling = true;
        this.doPoll();
        this.emitReserved("poll");
    }
    /**
     * Overloads onData to detect payloads.
     *
     * @protected
     */
    onData(data) {
        const callback = (packet) => {
            // if its the first message we consider the transport open
            if ("opening" === this.readyState && packet.type === "open") {
                this.onOpen();
            }
            // if its a close packet, we close the ongoing requests
            if ("close" === packet.type) {
                this.onClose({ description: "transport closed by the server" });
                return false;
            }
            // otherwise bypass onData and handle the message
            this.onPacket(packet);
        };
        // decode payload
        decodePayload(data, this.socket.binaryType).forEach(callback);
        // if an event did not trigger closing
        if ("closed" !== this.readyState) {
            // if we got data we're not polling
            this._polling = false;
            this.emitReserved("pollComplete");
            if ("open" === this.readyState) {
                this._poll();
            }
        }
    }
    /**
     * For polling, send a close packet.
     *
     * @protected
     */
    doClose() {
        const close = () => {
            this.write([{ type: "close" }]);
        };
        if ("open" === this.readyState) {
            close();
        }
        else {
            // in case we're trying to close while
            // handshaking is in progress (GH-164)
            this.once("open", close);
        }
    }
    /**
     * Writes a packets payload.
     *
     * @param {Array} packets - data packets
     * @protected
     */
    write(packets) {
        this.writable = false;
        encodePayload(packets, (data) => {
            this.doWrite(data, () => {
                this.writable = true;
                this.emitReserved("drain");
            });
        });
    }
    /**
     * Generates uri for connection.
     *
     * @private
     */
    uri() {
        const schema = this.opts.secure ? "https" : "http";
        const query = this.query || {};
        // cache busting is forced
        if (false !== this.opts.timestampRequests) {
            query[this.opts.timestampParam] = randomString();
        }
        if (!this.supportsBinary && !query.sid) {
            query.b64 = 1;
        }
        return this.createUri(schema, query);
    }
}

// imported from https://github.com/component/has-cors
let value = false;
try {
    value = typeof XMLHttpRequest !== 'undefined' &&
        'withCredentials' in new XMLHttpRequest();
}
catch (err) {
    // if XMLHttp support is disabled in IE then it will throw
    // when trying to create
}
const hasCORS = value;

function empty() { }
class BaseXHR extends Polling {
    /**
     * XHR Polling constructor.
     *
     * @param {Object} opts
     * @package
     */
    constructor(opts) {
        super(opts);
        if (typeof location !== "undefined") {
            const isSSL = "https:" === location.protocol;
            let port = location.port;
            // some user agents have empty `location.port`
            if (!port) {
                port = isSSL ? "443" : "80";
            }
            this.xd =
                (typeof location !== "undefined" &&
                    opts.hostname !== location.hostname) ||
                    port !== opts.port;
        }
    }
    /**
     * Sends data.
     *
     * @param {String} data to send.
     * @param {Function} called upon flush.
     * @private
     */
    doWrite(data, fn) {
        const req = this.request({
            method: "POST",
            data: data,
        });
        req.on("success", fn);
        req.on("error", (xhrStatus, context) => {
            this.onError("xhr post error", xhrStatus, context);
        });
    }
    /**
     * Starts a poll cycle.
     *
     * @private
     */
    doPoll() {
        const req = this.request();
        req.on("data", this.onData.bind(this));
        req.on("error", (xhrStatus, context) => {
            this.onError("xhr poll error", xhrStatus, context);
        });
        this.pollXhr = req;
    }
}
class Request extends Emitter {
    /**
     * Request constructor
     *
     * @param {Object} options
     * @package
     */
    constructor(createRequest, uri, opts) {
        super();
        this.createRequest = createRequest;
        installTimerFunctions(this, opts);
        this._opts = opts;
        this._method = opts.method || "GET";
        this._uri = uri;
        this._data = undefined !== opts.data ? opts.data : null;
        this._create();
    }
    /**
     * Creates the XHR object and sends the request.
     *
     * @private
     */
    _create() {
        var _a;
        const opts = pick(this._opts, "agent", "pfx", "key", "passphrase", "cert", "ca", "ciphers", "rejectUnauthorized", "autoUnref");
        opts.xdomain = !!this._opts.xd;
        const xhr = (this._xhr = this.createRequest(opts));
        try {
            xhr.open(this._method, this._uri, true);
            try {
                if (this._opts.extraHeaders) {
                    // @ts-ignore
                    xhr.setDisableHeaderCheck && xhr.setDisableHeaderCheck(true);
                    for (let i in this._opts.extraHeaders) {
                        if (this._opts.extraHeaders.hasOwnProperty(i)) {
                            xhr.setRequestHeader(i, this._opts.extraHeaders[i]);
                        }
                    }
                }
            }
            catch (e) { }
            if ("POST" === this._method) {
                try {
                    xhr.setRequestHeader("Content-type", "text/plain;charset=UTF-8");
                }
                catch (e) { }
            }
            try {
                xhr.setRequestHeader("Accept", "*/*");
            }
            catch (e) { }
            (_a = this._opts.cookieJar) === null || _a === void 0 ? void 0 : _a.addCookies(xhr);
            // ie6 check
            if ("withCredentials" in xhr) {
                xhr.withCredentials = this._opts.withCredentials;
            }
            if (this._opts.requestTimeout) {
                xhr.timeout = this._opts.requestTimeout;
            }
            xhr.onreadystatechange = () => {
                var _a;
                if (xhr.readyState === 3) {
                    (_a = this._opts.cookieJar) === null || _a === void 0 ? void 0 : _a.parseCookies(
                    // @ts-ignore
                    xhr.getResponseHeader("set-cookie"));
                }
                if (4 !== xhr.readyState)
                    return;
                if (200 === xhr.status || 1223 === xhr.status) {
                    this._onLoad();
                }
                else {
                    // make sure the `error` event handler that's user-set
                    // does not throw in the same tick and gets caught here
                    this.setTimeoutFn(() => {
                        this._onError(typeof xhr.status === "number" ? xhr.status : 0);
                    }, 0);
                }
            };
            xhr.send(this._data);
        }
        catch (e) {
            // Need to defer since .create() is called directly from the constructor
            // and thus the 'error' event can only be only bound *after* this exception
            // occurs.  Therefore, also, we cannot throw here at all.
            this.setTimeoutFn(() => {
                this._onError(e);
            }, 0);
            return;
        }
        if (typeof document !== "undefined") {
            this._index = Request.requestsCount++;
            Request.requests[this._index] = this;
        }
    }
    /**
     * Called upon error.
     *
     * @private
     */
    _onError(err) {
        this.emitReserved("error", err, this._xhr);
        this._cleanup(true);
    }
    /**
     * Cleans up house.
     *
     * @private
     */
    _cleanup(fromError) {
        if ("undefined" === typeof this._xhr || null === this._xhr) {
            return;
        }
        this._xhr.onreadystatechange = empty;
        if (fromError) {
            try {
                this._xhr.abort();
            }
            catch (e) { }
        }
        if (typeof document !== "undefined") {
            delete Request.requests[this._index];
        }
        this._xhr = null;
    }
    /**
     * Called upon load.
     *
     * @private
     */
    _onLoad() {
        const data = this._xhr.responseText;
        if (data !== null) {
            this.emitReserved("data", data);
            this.emitReserved("success");
            this._cleanup();
        }
    }
    /**
     * Aborts the request.
     *
     * @package
     */
    abort() {
        this._cleanup();
    }
}
Request.requestsCount = 0;
Request.requests = {};
/**
 * Aborts pending requests when unloading the window. This is needed to prevent
 * memory leaks (e.g. when using IE) and to ensure that no spurious error is
 * emitted.
 */
if (typeof document !== "undefined") {
    // @ts-ignore
    if (typeof attachEvent === "function") {
        // @ts-ignore
        attachEvent("onunload", unloadHandler);
    }
    else if (typeof addEventListener === "function") {
        const terminationEvent = "onpagehide" in globalThisShim ? "pagehide" : "unload";
        addEventListener(terminationEvent, unloadHandler, false);
    }
}
function unloadHandler() {
    for (let i in Request.requests) {
        if (Request.requests.hasOwnProperty(i)) {
            Request.requests[i].abort();
        }
    }
}
const hasXHR2 = (function () {
    const xhr = newRequest({
        xdomain: false,
    });
    return xhr && xhr.responseType !== null;
})();
/**
 * HTTP long-polling based on the built-in `XMLHttpRequest` object.
 *
 * Usage: browser
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest
 */
class XHR extends BaseXHR {
    constructor(opts) {
        super(opts);
        const forceBase64 = opts && opts.forceBase64;
        this.supportsBinary = hasXHR2 && !forceBase64;
    }
    request(opts = {}) {
        Object.assign(opts, { xd: this.xd }, this.opts);
        return new Request(newRequest, this.uri(), opts);
    }
}
function newRequest(opts) {
    const xdomain = opts.xdomain;
    // XMLHttpRequest can be disabled on IE
    try {
        if ("undefined" !== typeof XMLHttpRequest && (!xdomain || hasCORS)) {
            return new XMLHttpRequest();
        }
    }
    catch (e) { }
    if (!xdomain) {
        try {
            return new globalThisShim[["Active"].concat("Object").join("X")]("Microsoft.XMLHTTP");
        }
        catch (e) { }
    }
}

// detect ReactNative environment
const isReactNative = typeof navigator !== "undefined" &&
    typeof navigator.product === "string" &&
    navigator.product.toLowerCase() === "reactnative";
class BaseWS extends Transport {
    get name() {
        return "websocket";
    }
    doOpen() {
        const uri = this.uri();
        const protocols = this.opts.protocols;
        // React Native only supports the 'headers' option, and will print a warning if anything else is passed
        const opts = isReactNative
            ? {}
            : pick(this.opts, "agent", "perMessageDeflate", "pfx", "key", "passphrase", "cert", "ca", "ciphers", "rejectUnauthorized", "localAddress", "protocolVersion", "origin", "maxPayload", "family", "checkServerIdentity");
        if (this.opts.extraHeaders) {
            opts.headers = this.opts.extraHeaders;
        }
        try {
            this.ws = this.createSocket(uri, protocols, opts);
        }
        catch (err) {
            return this.emitReserved("error", err);
        }
        this.ws.binaryType = this.socket.binaryType;
        this.addEventListeners();
    }
    /**
     * Adds event listeners to the socket
     *
     * @private
     */
    addEventListeners() {
        this.ws.onopen = () => {
            if (this.opts.autoUnref) {
                this.ws._socket.unref();
            }
            this.onOpen();
        };
        this.ws.onclose = (closeEvent) => this.onClose({
            description: "websocket connection closed",
            context: closeEvent,
        });
        this.ws.onmessage = (ev) => this.onData(ev.data);
        this.ws.onerror = (e) => this.onError("websocket error", e);
    }
    write(packets) {
        this.writable = false;
        // encodePacket efficient as it uses WS framing
        // no need for encodePayload
        for (let i = 0; i < packets.length; i++) {
            const packet = packets[i];
            const lastPacket = i === packets.length - 1;
            encodePacket(packet, this.supportsBinary, (data) => {
                // Sometimes the websocket has already been closed but the browser didn't
                // have a chance of informing us about it yet, in that case send will
                // throw an error
                try {
                    this.doWrite(packet, data);
                }
                catch (e) {
                }
                if (lastPacket) {
                    // fake drain
                    // defer to next tick to allow Socket to clear writeBuffer
                    nextTick(() => {
                        this.writable = true;
                        this.emitReserved("drain");
                    }, this.setTimeoutFn);
                }
            });
        }
    }
    doClose() {
        if (typeof this.ws !== "undefined") {
            this.ws.onerror = () => { };
            this.ws.close();
            this.ws = null;
        }
    }
    /**
     * Generates uri for connection.
     *
     * @private
     */
    uri() {
        const schema = this.opts.secure ? "wss" : "ws";
        const query = this.query || {};
        // append timestamp to URI
        if (this.opts.timestampRequests) {
            query[this.opts.timestampParam] = randomString();
        }
        // communicate binary support capabilities
        if (!this.supportsBinary) {
            query.b64 = 1;
        }
        return this.createUri(schema, query);
    }
}
const WebSocketCtor = globalThisShim.WebSocket || globalThisShim.MozWebSocket;
/**
 * WebSocket transport based on the built-in `WebSocket` object.
 *
 * Usage: browser, Node.js (since v21), Deno, Bun
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/WebSocket
 * @see https://caniuse.com/mdn-api_websocket
 * @see https://nodejs.org/api/globals.html#websocket
 */
class WS extends BaseWS {
    createSocket(uri, protocols, opts) {
        return !isReactNative
            ? protocols
                ? new WebSocketCtor(uri, protocols)
                : new WebSocketCtor(uri)
            : new WebSocketCtor(uri, protocols, opts);
    }
    doWrite(_packet, data) {
        this.ws.send(data);
    }
}

/**
 * WebTransport transport based on the built-in `WebTransport` object.
 *
 * Usage: browser, Node.js (with the `@fails-components/webtransport` package)
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/WebTransport
 * @see https://caniuse.com/webtransport
 */
class WT extends Transport {
    get name() {
        return "webtransport";
    }
    doOpen() {
        try {
            // @ts-ignore
            this._transport = new WebTransport(this.createUri("https"), this.opts.transportOptions[this.name]);
        }
        catch (err) {
            return this.emitReserved("error", err);
        }
        this._transport.closed
            .then(() => {
            this.onClose();
        })
            .catch((err) => {
            this.onError("webtransport error", err);
        });
        // note: we could have used async/await, but that would require some additional polyfills
        this._transport.ready.then(() => {
            this._transport.createBidirectionalStream().then((stream) => {
                const decoderStream = createPacketDecoderStream(Number.MAX_SAFE_INTEGER, this.socket.binaryType);
                const reader = stream.readable.pipeThrough(decoderStream).getReader();
                const encoderStream = createPacketEncoderStream();
                encoderStream.readable.pipeTo(stream.writable);
                this._writer = encoderStream.writable.getWriter();
                const read = () => {
                    reader
                        .read()
                        .then(({ done, value }) => {
                        if (done) {
                            return;
                        }
                        this.onPacket(value);
                        read();
                    })
                        .catch((err) => {
                    });
                };
                read();
                const packet = { type: "open" };
                if (this.query.sid) {
                    packet.data = `{"sid":"${this.query.sid}"}`;
                }
                this._writer.write(packet).then(() => this.onOpen());
            });
        });
    }
    write(packets) {
        this.writable = false;
        for (let i = 0; i < packets.length; i++) {
            const packet = packets[i];
            const lastPacket = i === packets.length - 1;
            this._writer.write(packet).then(() => {
                if (lastPacket) {
                    nextTick(() => {
                        this.writable = true;
                        this.emitReserved("drain");
                    }, this.setTimeoutFn);
                }
            });
        }
    }
    doClose() {
        var _a;
        (_a = this._transport) === null || _a === void 0 ? void 0 : _a.close();
    }
}

const transports = {
    websocket: WS,
    webtransport: WT,
    polling: XHR,
};

// imported from https://github.com/galkn/parseuri
/**
 * Parses a URI
 *
 * Note: we could also have used the built-in URL object, but it isn't supported on all platforms.
 *
 * See:
 * - https://developer.mozilla.org/en-US/docs/Web/API/URL
 * - https://caniuse.com/url
 * - https://www.rfc-editor.org/rfc/rfc3986#appendix-B
 *
 * History of the parse() method:
 * - first commit: https://github.com/socketio/socket.io-client/commit/4ee1d5d94b3906a9c052b459f1a818b15f38f91c
 * - export into its own module: https://github.com/socketio/engine.io-client/commit/de2c561e4564efeb78f1bdb1ba39ef81b2822cb3
 * - reimport: https://github.com/socketio/engine.io-client/commit/df32277c3f6d622eec5ed09f493cae3f3391d242
 *
 * @author Steven Levithan <stevenlevithan.com> (MIT license)
 * @api private
 */
const re = /^(?:(?![^:@\/?#]+:[^:@\/]*@)(http|https|ws|wss):\/\/)?((?:(([^:@\/?#]*)(?::([^:@\/?#]*))?)?@)?((?:[a-f0-9]{0,4}:){2,7}[a-f0-9]{0,4}|[^:\/?#]*)(?::(\d*))?)(((\/(?:[^?#](?![^?#\/]*\.[^?#\/.]+(?:[?#]|$)))*\/?)?([^?#\/]*))(?:\?([^#]*))?(?:#(.*))?)/;
const parts = [
    'source', 'protocol', 'authority', 'userInfo', 'user', 'password', 'host', 'port', 'relative', 'path', 'directory', 'file', 'query', 'anchor'
];
function parse(str) {
    if (str.length > 8000) {
        throw "URI too long";
    }
    const src = str, b = str.indexOf('['), e = str.indexOf(']');
    if (b != -1 && e != -1) {
        str = str.substring(0, b) + str.substring(b, e).replace(/:/g, ';') + str.substring(e, str.length);
    }
    let m = re.exec(str || ''), uri = {}, i = 14;
    while (i--) {
        uri[parts[i]] = m[i] || '';
    }
    if (b != -1 && e != -1) {
        uri.source = src;
        uri.host = uri.host.substring(1, uri.host.length - 1).replace(/;/g, ':');
        uri.authority = uri.authority.replace('[', '').replace(']', '').replace(/;/g, ':');
        uri.ipv6uri = true;
    }
    uri.pathNames = pathNames(uri, uri['path']);
    uri.queryKey = queryKey(uri, uri['query']);
    return uri;
}
function pathNames(obj, path) {
    const regx = /\/{2,9}/g, names = path.replace(regx, "/").split("/");
    if (path.slice(0, 1) == '/' || path.length === 0) {
        names.splice(0, 1);
    }
    if (path.slice(-1) == '/') {
        names.splice(names.length - 1, 1);
    }
    return names;
}
function queryKey(uri, query) {
    const data = {};
    query.replace(/(?:^|&)([^&=]*)=?([^&]*)/g, function ($0, $1, $2) {
        if ($1) {
            data[$1] = $2;
        }
    });
    return data;
}

const withEventListeners = typeof addEventListener === "function" &&
    typeof removeEventListener === "function";
const OFFLINE_EVENT_LISTENERS = [];
if (withEventListeners) {
    // within a ServiceWorker, any event handler for the 'offline' event must be added on the initial evaluation of the
    // script, so we create one single event listener here which will forward the event to the socket instances
    addEventListener("offline", () => {
        OFFLINE_EVENT_LISTENERS.forEach((listener) => listener());
    }, false);
}
/**
 * This class provides a WebSocket-like interface to connect to an Engine.IO server. The connection will be established
 * with one of the available low-level transports, like HTTP long-polling, WebSocket or WebTransport.
 *
 * This class comes without upgrade mechanism, which means that it will keep the first low-level transport that
 * successfully establishes the connection.
 *
 * In order to allow tree-shaking, there are no transports included, that's why the `transports` option is mandatory.
 *
 * @example
 * import { SocketWithoutUpgrade, WebSocket } from "engine.io-client";
 *
 * const socket = new SocketWithoutUpgrade({
 *   transports: [WebSocket]
 * });
 *
 * socket.on("open", () => {
 *   socket.send("hello");
 * });
 *
 * @see SocketWithUpgrade
 * @see Socket
 */
class SocketWithoutUpgrade extends Emitter {
    /**
     * Socket constructor.
     *
     * @param {String|Object} uri - uri or options
     * @param {Object} opts - options
     */
    constructor(uri, opts) {
        super();
        this.binaryType = defaultBinaryType;
        this.writeBuffer = [];
        this._prevBufferLen = 0;
        this._pingInterval = -1;
        this._pingTimeout = -1;
        this._maxPayload = -1;
        /**
         * The expiration timestamp of the {@link _pingTimeoutTimer} object is tracked, in case the timer is throttled and the
         * callback is not fired on time. This can happen for example when a laptop is suspended or when a phone is locked.
         */
        this._pingTimeoutTime = Infinity;
        if (uri && "object" === typeof uri) {
            opts = uri;
            uri = null;
        }
        if (uri) {
            const parsedUri = parse(uri);
            opts.hostname = parsedUri.host;
            opts.secure =
                parsedUri.protocol === "https" || parsedUri.protocol === "wss";
            opts.port = parsedUri.port;
            if (parsedUri.query)
                opts.query = parsedUri.query;
        }
        else if (opts.host) {
            opts.hostname = parse(opts.host).host;
        }
        installTimerFunctions(this, opts);
        this.secure =
            null != opts.secure
                ? opts.secure
                : typeof location !== "undefined" && "https:" === location.protocol;
        if (opts.hostname && !opts.port) {
            // if no port is specified manually, use the protocol default
            opts.port = this.secure ? "443" : "80";
        }
        this.hostname =
            opts.hostname ||
                (typeof location !== "undefined" ? location.hostname : "localhost");
        this.port =
            opts.port ||
                (typeof location !== "undefined" && location.port
                    ? location.port
                    : this.secure
                        ? "443"
                        : "80");
        this.transports = [];
        this._transportsByName = {};
        opts.transports.forEach((t) => {
            const transportName = t.prototype.name;
            this.transports.push(transportName);
            this._transportsByName[transportName] = t;
        });
        this.opts = Object.assign({
            path: "/engine.io",
            agent: false,
            withCredentials: false,
            upgrade: true,
            timestampParam: "t",
            rememberUpgrade: false,
            addTrailingSlash: true,
            rejectUnauthorized: true,
            perMessageDeflate: {
                threshold: 1024,
            },
            transportOptions: {},
            closeOnBeforeunload: false,
        }, opts);
        this.opts.path =
            this.opts.path.replace(/\/$/, "") +
                (this.opts.addTrailingSlash ? "/" : "");
        if (typeof this.opts.query === "string") {
            this.opts.query = decode(this.opts.query);
        }
        if (withEventListeners) {
            if (this.opts.closeOnBeforeunload) {
                // Firefox closes the connection when the "beforeunload" event is emitted but not Chrome. This event listener
                // ensures every browser behaves the same (no "disconnect" event at the Socket.IO level when the page is
                // closed/reloaded)
                this._beforeunloadEventListener = () => {
                    if (this.transport) {
                        // silently close the transport
                        this.transport.removeAllListeners();
                        this.transport.close();
                    }
                };
                addEventListener("beforeunload", this._beforeunloadEventListener, false);
            }
            if (this.hostname !== "localhost") {
                this._offlineEventListener = () => {
                    this._onClose("transport close", {
                        description: "network connection lost",
                    });
                };
                OFFLINE_EVENT_LISTENERS.push(this._offlineEventListener);
            }
        }
        if (this.opts.withCredentials) {
            this._cookieJar = createCookieJar();
        }
        this._open();
    }
    /**
     * Creates transport of the given type.
     *
     * @param {String} name - transport name
     * @return {Transport}
     * @private
     */
    createTransport(name) {
        const query = Object.assign({}, this.opts.query);
        // append engine.io protocol identifier
        query.EIO = protocol;
        // transport name
        query.transport = name;
        // session id if we already have one
        if (this.id)
            query.sid = this.id;
        const opts = Object.assign({}, this.opts, {
            query,
            socket: this,
            hostname: this.hostname,
            secure: this.secure,
            port: this.port,
        }, this.opts.transportOptions[name]);
        return new this._transportsByName[name](opts);
    }
    /**
     * Initializes transport to use and starts probe.
     *
     * @private
     */
    _open() {
        if (this.transports.length === 0) {
            // Emit error on next tick so it can be listened to
            this.setTimeoutFn(() => {
                this.emitReserved("error", "No transports available");
            }, 0);
            return;
        }
        const transportName = this.opts.rememberUpgrade &&
            SocketWithoutUpgrade.priorWebsocketSuccess &&
            this.transports.indexOf("websocket") !== -1
            ? "websocket"
            : this.transports[0];
        this.readyState = "opening";
        const transport = this.createTransport(transportName);
        transport.open();
        this.setTransport(transport);
    }
    /**
     * Sets the current transport. Disables the existing one (if any).
     *
     * @private
     */
    setTransport(transport) {
        if (this.transport) {
            this.transport.removeAllListeners();
        }
        // set up transport
        this.transport = transport;
        // set up transport listeners
        transport
            .on("drain", this._onDrain.bind(this))
            .on("packet", this._onPacket.bind(this))
            .on("error", this._onError.bind(this))
            .on("close", (reason) => this._onClose("transport close", reason));
    }
    /**
     * Called when connection is deemed open.
     *
     * @private
     */
    onOpen() {
        this.readyState = "open";
        SocketWithoutUpgrade.priorWebsocketSuccess =
            "websocket" === this.transport.name;
        this.emitReserved("open");
        this.flush();
    }
    /**
     * Handles a packet.
     *
     * @private
     */
    _onPacket(packet) {
        if ("opening" === this.readyState ||
            "open" === this.readyState ||
            "closing" === this.readyState) {
            this.emitReserved("packet", packet);
            // Socket is live - any packet counts
            this.emitReserved("heartbeat");
            switch (packet.type) {
                case "open":
                    this.onHandshake(JSON.parse(packet.data));
                    break;
                case "ping":
                    this._sendPacket("pong");
                    this.emitReserved("ping");
                    this.emitReserved("pong");
                    this._resetPingTimeout();
                    break;
                case "error":
                    const err = new Error("server error");
                    // @ts-ignore
                    err.code = packet.data;
                    this._onError(err);
                    break;
                case "message":
                    this.emitReserved("data", packet.data);
                    this.emitReserved("message", packet.data);
                    break;
            }
        }
    }
    /**
     * Called upon handshake completion.
     *
     * @param {Object} data - handshake obj
     * @private
     */
    onHandshake(data) {
        this.emitReserved("handshake", data);
        this.id = data.sid;
        this.transport.query.sid = data.sid;
        this._pingInterval = data.pingInterval;
        this._pingTimeout = data.pingTimeout;
        this._maxPayload = data.maxPayload;
        this.onOpen();
        // In case open handler closes socket
        if ("closed" === this.readyState)
            return;
        this._resetPingTimeout();
    }
    /**
     * Sets and resets ping timeout timer based on server pings.
     *
     * @private
     */
    _resetPingTimeout() {
        this.clearTimeoutFn(this._pingTimeoutTimer);
        const delay = this._pingInterval + this._pingTimeout;
        this._pingTimeoutTime = Date.now() + delay;
        this._pingTimeoutTimer = this.setTimeoutFn(() => {
            this._onClose("ping timeout");
        }, delay);
        if (this.opts.autoUnref) {
            this._pingTimeoutTimer.unref();
        }
    }
    /**
     * Called on `drain` event
     *
     * @private
     */
    _onDrain() {
        this.writeBuffer.splice(0, this._prevBufferLen);
        // setting prevBufferLen = 0 is very important
        // for example, when upgrading, upgrade packet is sent over,
        // and a nonzero prevBufferLen could cause problems on `drain`
        this._prevBufferLen = 0;
        if (0 === this.writeBuffer.length) {
            this.emitReserved("drain");
        }
        else {
            this.flush();
        }
    }
    /**
     * Flush write buffers.
     *
     * @private
     */
    flush() {
        if ("closed" !== this.readyState &&
            this.transport.writable &&
            !this.upgrading &&
            this.writeBuffer.length) {
            const packets = this._getWritablePackets();
            this.transport.send(packets);
            // keep track of current length of writeBuffer
            // splice writeBuffer and callbackBuffer on `drain`
            this._prevBufferLen = packets.length;
            this.emitReserved("flush");
        }
    }
    /**
     * Ensure the encoded size of the writeBuffer is below the maxPayload value sent by the server (only for HTTP
     * long-polling)
     *
     * @private
     */
    _getWritablePackets() {
        const shouldCheckPayloadSize = this._maxPayload &&
            this.transport.name === "polling" &&
            this.writeBuffer.length > 1;
        if (!shouldCheckPayloadSize) {
            return this.writeBuffer;
        }
        let payloadSize = 1; // first packet type
        for (let i = 0; i < this.writeBuffer.length; i++) {
            const data = this.writeBuffer[i].data;
            if (data) {
                payloadSize += byteLength(data);
            }
            if (i > 0 && payloadSize > this._maxPayload) {
                return this.writeBuffer.slice(0, i);
            }
            payloadSize += 2; // separator + packet type
        }
        return this.writeBuffer;
    }
    /**
     * Checks whether the heartbeat timer has expired but the socket has not yet been notified.
     *
     * Note: this method is private for now because it does not really fit the WebSocket API, but if we put it in the
     * `write()` method then the message would not be buffered by the Socket.IO client.
     *
     * @return {boolean}
     * @private
     */
    /* private */ _hasPingExpired() {
        if (!this._pingTimeoutTime)
            return true;
        const hasExpired = Date.now() > this._pingTimeoutTime;
        if (hasExpired) {
            this._pingTimeoutTime = 0;
            nextTick(() => {
                this._onClose("ping timeout");
            }, this.setTimeoutFn);
        }
        return hasExpired;
    }
    /**
     * Sends a message.
     *
     * @param {String} msg - message.
     * @param {Object} options.
     * @param {Function} fn - callback function.
     * @return {Socket} for chaining.
     */
    write(msg, options, fn) {
        this._sendPacket("message", msg, options, fn);
        return this;
    }
    /**
     * Sends a message. Alias of {@link Socket#write}.
     *
     * @param {String} msg - message.
     * @param {Object} options.
     * @param {Function} fn - callback function.
     * @return {Socket} for chaining.
     */
    send(msg, options, fn) {
        this._sendPacket("message", msg, options, fn);
        return this;
    }
    /**
     * Sends a packet.
     *
     * @param {String} type: packet type.
     * @param {String} data.
     * @param {Object} options.
     * @param {Function} fn - callback function.
     * @private
     */
    _sendPacket(type, data, options, fn) {
        if ("function" === typeof data) {
            fn = data;
            data = undefined;
        }
        if ("function" === typeof options) {
            fn = options;
            options = null;
        }
        if ("closing" === this.readyState || "closed" === this.readyState) {
            return;
        }
        options = options || {};
        options.compress = false !== options.compress;
        const packet = {
            type: type,
            data: data,
            options: options,
        };
        this.emitReserved("packetCreate", packet);
        this.writeBuffer.push(packet);
        if (fn)
            this.once("flush", fn);
        this.flush();
    }
    /**
     * Closes the connection.
     */
    close() {
        const close = () => {
            this._onClose("forced close");
            this.transport.close();
        };
        const cleanupAndClose = () => {
            this.off("upgrade", cleanupAndClose);
            this.off("upgradeError", cleanupAndClose);
            close();
        };
        const waitForUpgrade = () => {
            // wait for upgrade to finish since we can't send packets while pausing a transport
            this.once("upgrade", cleanupAndClose);
            this.once("upgradeError", cleanupAndClose);
        };
        if ("opening" === this.readyState || "open" === this.readyState) {
            this.readyState = "closing";
            if (this.writeBuffer.length) {
                this.once("drain", () => {
                    if (this.upgrading) {
                        waitForUpgrade();
                    }
                    else {
                        close();
                    }
                });
            }
            else if (this.upgrading) {
                waitForUpgrade();
            }
            else {
                close();
            }
        }
        return this;
    }
    /**
     * Called upon transport error
     *
     * @private
     */
    _onError(err) {
        SocketWithoutUpgrade.priorWebsocketSuccess = false;
        if (this.opts.tryAllTransports &&
            this.transports.length > 1 &&
            this.readyState === "opening") {
            this.transports.shift();
            return this._open();
        }
        this.emitReserved("error", err);
        this._onClose("transport error", err);
    }
    /**
     * Called upon transport close.
     *
     * @private
     */
    _onClose(reason, description) {
        if ("opening" === this.readyState ||
            "open" === this.readyState ||
            "closing" === this.readyState) {
            // clear timers
            this.clearTimeoutFn(this._pingTimeoutTimer);
            // stop event from firing again for transport
            this.transport.removeAllListeners("close");
            // ensure transport won't stay open
            this.transport.close();
            // ignore further transport communication
            this.transport.removeAllListeners();
            if (withEventListeners) {
                if (this._beforeunloadEventListener) {
                    removeEventListener("beforeunload", this._beforeunloadEventListener, false);
                }
                if (this._offlineEventListener) {
                    const i = OFFLINE_EVENT_LISTENERS.indexOf(this._offlineEventListener);
                    if (i !== -1) {
                        OFFLINE_EVENT_LISTENERS.splice(i, 1);
                    }
                }
            }
            // set ready state
            this.readyState = "closed";
            // clear session id
            this.id = null;
            // emit close event
            this.emitReserved("close", reason, description);
            // clean buffers after, so users can still
            // grab the buffers on `close` event
            this.writeBuffer = [];
            this._prevBufferLen = 0;
        }
    }
}
SocketWithoutUpgrade.protocol = protocol;
/**
 * This class provides a WebSocket-like interface to connect to an Engine.IO server. The connection will be established
 * with one of the available low-level transports, like HTTP long-polling, WebSocket or WebTransport.
 *
 * This class comes with an upgrade mechanism, which means that once the connection is established with the first
 * low-level transport, it will try to upgrade to a better transport.
 *
 * In order to allow tree-shaking, there are no transports included, that's why the `transports` option is mandatory.
 *
 * @example
 * import { SocketWithUpgrade, WebSocket } from "engine.io-client";
 *
 * const socket = new SocketWithUpgrade({
 *   transports: [WebSocket]
 * });
 *
 * socket.on("open", () => {
 *   socket.send("hello");
 * });
 *
 * @see SocketWithoutUpgrade
 * @see Socket
 */
class SocketWithUpgrade extends SocketWithoutUpgrade {
    constructor() {
        super(...arguments);
        this._upgrades = [];
    }
    onOpen() {
        super.onOpen();
        if ("open" === this.readyState && this.opts.upgrade) {
            for (let i = 0; i < this._upgrades.length; i++) {
                this._probe(this._upgrades[i]);
            }
        }
    }
    /**
     * Probes a transport.
     *
     * @param {String} name - transport name
     * @private
     */
    _probe(name) {
        let transport = this.createTransport(name);
        let failed = false;
        SocketWithoutUpgrade.priorWebsocketSuccess = false;
        const onTransportOpen = () => {
            if (failed)
                return;
            transport.send([{ type: "ping", data: "probe" }]);
            transport.once("packet", (msg) => {
                if (failed)
                    return;
                if ("pong" === msg.type && "probe" === msg.data) {
                    this.upgrading = true;
                    this.emitReserved("upgrading", transport);
                    if (!transport)
                        return;
                    SocketWithoutUpgrade.priorWebsocketSuccess =
                        "websocket" === transport.name;
                    this.transport.pause(() => {
                        if (failed)
                            return;
                        if ("closed" === this.readyState)
                            return;
                        cleanup();
                        this.setTransport(transport);
                        transport.send([{ type: "upgrade" }]);
                        this.emitReserved("upgrade", transport);
                        transport = null;
                        this.upgrading = false;
                        this.flush();
                    });
                }
                else {
                    const err = new Error("probe error");
                    // @ts-ignore
                    err.transport = transport.name;
                    this.emitReserved("upgradeError", err);
                }
            });
        };
        function freezeTransport() {
            if (failed)
                return;
            // Any callback called by transport should be ignored since now
            failed = true;
            cleanup();
            transport.close();
            transport = null;
        }
        // Handle any error that happens while probing
        const onerror = (err) => {
            const error = new Error("probe error: " + err);
            // @ts-ignore
            error.transport = transport.name;
            freezeTransport();
            this.emitReserved("upgradeError", error);
        };
        function onTransportClose() {
            onerror("transport closed");
        }
        // When the socket is closed while we're probing
        function onclose() {
            onerror("socket closed");
        }
        // When the socket is upgraded while we're probing
        function onupgrade(to) {
            if (transport && to.name !== transport.name) {
                freezeTransport();
            }
        }
        // Remove all listeners on the transport and on self
        const cleanup = () => {
            transport.removeListener("open", onTransportOpen);
            transport.removeListener("error", onerror);
            transport.removeListener("close", onTransportClose);
            this.off("close", onclose);
            this.off("upgrading", onupgrade);
        };
        transport.once("open", onTransportOpen);
        transport.once("error", onerror);
        transport.once("close", onTransportClose);
        this.once("close", onclose);
        this.once("upgrading", onupgrade);
        if (this._upgrades.indexOf("webtransport") !== -1 &&
            name !== "webtransport") {
            // favor WebTransport
            this.setTimeoutFn(() => {
                if (!failed) {
                    transport.open();
                }
            }, 200);
        }
        else {
            transport.open();
        }
    }
    onHandshake(data) {
        this._upgrades = this._filterUpgrades(data.upgrades);
        super.onHandshake(data);
    }
    /**
     * Filters upgrades, returning only those matching client transports.
     *
     * @param {Array} upgrades - server upgrades
     * @private
     */
    _filterUpgrades(upgrades) {
        const filteredUpgrades = [];
        for (let i = 0; i < upgrades.length; i++) {
            if (~this.transports.indexOf(upgrades[i]))
                filteredUpgrades.push(upgrades[i]);
        }
        return filteredUpgrades;
    }
}
/**
 * This class provides a WebSocket-like interface to connect to an Engine.IO server. The connection will be established
 * with one of the available low-level transports, like HTTP long-polling, WebSocket or WebTransport.
 *
 * This class comes with an upgrade mechanism, which means that once the connection is established with the first
 * low-level transport, it will try to upgrade to a better transport.
 *
 * @example
 * import { Socket } from "engine.io-client";
 *
 * const socket = new Socket();
 *
 * socket.on("open", () => {
 *   socket.send("hello");
 * });
 *
 * @see SocketWithoutUpgrade
 * @see SocketWithUpgrade
 */
let Socket$1 = class Socket extends SocketWithUpgrade {
    constructor(uri, opts = {}) {
        const o = typeof uri === "object" ? uri : opts;
        if (!o.transports ||
            (o.transports && typeof o.transports[0] === "string")) {
            o.transports = (o.transports || ["polling", "websocket", "webtransport"])
                .map((transportName) => transports[transportName])
                .filter((t) => !!t);
        }
        super(uri, o);
    }
};

/**
 * URL parser.
 *
 * @param uri - url
 * @param path - the request path of the connection
 * @param loc - An object meant to mimic window.location.
 *        Defaults to window.location.
 * @public
 */
function url(uri, path = "", loc) {
    let obj = uri;
    // default to window.location
    loc = loc || (typeof location !== "undefined" && location);
    if (null == uri)
        uri = loc.protocol + "//" + loc.host;
    // relative path support
    if (typeof uri === "string") {
        if ("/" === uri.charAt(0)) {
            if ("/" === uri.charAt(1)) {
                uri = loc.protocol + uri;
            }
            else {
                uri = loc.host + uri;
            }
        }
        if (!/^(https?|wss?):\/\//.test(uri)) {
            if ("undefined" !== typeof loc) {
                uri = loc.protocol + "//" + uri;
            }
            else {
                uri = "https://" + uri;
            }
        }
        // parse
        obj = parse(uri);
    }
    // make sure we treat `localhost:80` and `localhost` equally
    if (!obj.port) {
        if (/^(http|ws)$/.test(obj.protocol)) {
            obj.port = "80";
        }
        else if (/^(http|ws)s$/.test(obj.protocol)) {
            obj.port = "443";
        }
    }
    obj.path = obj.path || "/";
    const ipv6 = obj.host.indexOf(":") !== -1;
    const host = ipv6 ? "[" + obj.host + "]" : obj.host;
    // define unique id
    obj.id = obj.protocol + "://" + host + ":" + obj.port + path;
    // define href
    obj.href =
        obj.protocol +
            "://" +
            host +
            (loc && loc.port === obj.port ? "" : ":" + obj.port);
    return obj;
}

const withNativeArrayBuffer = typeof ArrayBuffer === "function";
const isView = (obj) => {
    return typeof ArrayBuffer.isView === "function"
        ? ArrayBuffer.isView(obj)
        : obj.buffer instanceof ArrayBuffer;
};
const toString = Object.prototype.toString;
const withNativeBlob = typeof Blob === "function" ||
    (typeof Blob !== "undefined" &&
        toString.call(Blob) === "[object BlobConstructor]");
const withNativeFile = typeof File === "function" ||
    (typeof File !== "undefined" &&
        toString.call(File) === "[object FileConstructor]");
/**
 * Returns true if obj is a Buffer, an ArrayBuffer, a Blob or a File.
 *
 * @private
 */
function isBinary(obj) {
    return ((withNativeArrayBuffer && (obj instanceof ArrayBuffer || isView(obj))) ||
        (withNativeBlob && obj instanceof Blob) ||
        (withNativeFile && obj instanceof File));
}
function hasBinary(obj, toJSON) {
    if (!obj || typeof obj !== "object") {
        return false;
    }
    if (Array.isArray(obj)) {
        for (let i = 0, l = obj.length; i < l; i++) {
            if (hasBinary(obj[i])) {
                return true;
            }
        }
        return false;
    }
    if (isBinary(obj)) {
        return true;
    }
    if (obj.toJSON &&
        typeof obj.toJSON === "function" &&
        arguments.length === 1) {
        return hasBinary(obj.toJSON(), true);
    }
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key) && hasBinary(obj[key])) {
            return true;
        }
    }
    return false;
}

/**
 * Replaces every Buffer | ArrayBuffer | Blob | File in packet with a numbered placeholder.
 *
 * @param {Object} packet - socket.io event packet
 * @return {Object} with deconstructed packet and list of buffers
 * @public
 */
function deconstructPacket(packet) {
    const buffers = [];
    const packetData = packet.data;
    const pack = packet;
    pack.data = _deconstructPacket(packetData, buffers);
    pack.attachments = buffers.length; // number of binary 'attachments'
    return { packet: pack, buffers: buffers };
}
function _deconstructPacket(data, buffers) {
    if (!data)
        return data;
    if (isBinary(data)) {
        const placeholder = { _placeholder: true, num: buffers.length };
        buffers.push(data);
        return placeholder;
    }
    else if (Array.isArray(data)) {
        const newData = new Array(data.length);
        for (let i = 0; i < data.length; i++) {
            newData[i] = _deconstructPacket(data[i], buffers);
        }
        return newData;
    }
    else if (typeof data === "object" && !(data instanceof Date)) {
        const newData = {};
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                newData[key] = _deconstructPacket(data[key], buffers);
            }
        }
        return newData;
    }
    return data;
}
/**
 * Reconstructs a binary packet from its placeholder packet and buffers
 *
 * @param {Object} packet - event packet with placeholders
 * @param {Array} buffers - binary buffers to put in placeholder positions
 * @return {Object} reconstructed packet
 * @public
 */
function reconstructPacket(packet, buffers) {
    packet.data = _reconstructPacket(packet.data, buffers);
    delete packet.attachments; // no longer useful
    return packet;
}
function _reconstructPacket(data, buffers) {
    if (!data)
        return data;
    if (data && data._placeholder === true) {
        const isIndexValid = typeof data.num === "number" &&
            data.num >= 0 &&
            data.num < buffers.length;
        if (isIndexValid) {
            return buffers[data.num]; // appropriate buffer (should be natural order anyway)
        }
        else {
            throw new Error("illegal attachments");
        }
    }
    else if (Array.isArray(data)) {
        for (let i = 0; i < data.length; i++) {
            data[i] = _reconstructPacket(data[i], buffers);
        }
    }
    else if (typeof data === "object") {
        for (const key in data) {
            if (Object.prototype.hasOwnProperty.call(data, key)) {
                data[key] = _reconstructPacket(data[key], buffers);
            }
        }
    }
    return data;
}

/**
 * These strings must not be used as event names, as they have a special meaning.
 */
const RESERVED_EVENTS$1 = [
    "connect",
    "connect_error",
    "disconnect",
    "disconnecting",
    "newListener",
    "removeListener", // used by the Node.js EventEmitter
];
var PacketType;
(function (PacketType) {
    PacketType[PacketType["CONNECT"] = 0] = "CONNECT";
    PacketType[PacketType["DISCONNECT"] = 1] = "DISCONNECT";
    PacketType[PacketType["EVENT"] = 2] = "EVENT";
    PacketType[PacketType["ACK"] = 3] = "ACK";
    PacketType[PacketType["CONNECT_ERROR"] = 4] = "CONNECT_ERROR";
    PacketType[PacketType["BINARY_EVENT"] = 5] = "BINARY_EVENT";
    PacketType[PacketType["BINARY_ACK"] = 6] = "BINARY_ACK";
})(PacketType || (PacketType = {}));
/**
 * A socket.io Encoder instance
 */
class Encoder {
    /**
     * Encoder constructor
     *
     * @param {function} replacer - custom replacer to pass down to JSON.parse
     */
    constructor(replacer) {
        this.replacer = replacer;
    }
    /**
     * Encode a packet as a single string if non-binary, or as a
     * buffer sequence, depending on packet type.
     *
     * @param {Object} obj - packet object
     */
    encode(obj) {
        if (obj.type === PacketType.EVENT || obj.type === PacketType.ACK) {
            if (hasBinary(obj)) {
                return this.encodeAsBinary({
                    type: obj.type === PacketType.EVENT
                        ? PacketType.BINARY_EVENT
                        : PacketType.BINARY_ACK,
                    nsp: obj.nsp,
                    data: obj.data,
                    id: obj.id,
                });
            }
        }
        return [this.encodeAsString(obj)];
    }
    /**
     * Encode packet as string.
     */
    encodeAsString(obj) {
        // first is type
        let str = "" + obj.type;
        // attachments if we have them
        if (obj.type === PacketType.BINARY_EVENT ||
            obj.type === PacketType.BINARY_ACK) {
            str += obj.attachments + "-";
        }
        // if we have a namespace other than `/`
        // we append it followed by a comma `,`
        if (obj.nsp && "/" !== obj.nsp) {
            str += obj.nsp + ",";
        }
        // immediately followed by the id
        if (null != obj.id) {
            str += obj.id;
        }
        // json data
        if (null != obj.data) {
            str += JSON.stringify(obj.data, this.replacer);
        }
        return str;
    }
    /**
     * Encode packet as 'buffer sequence' by removing blobs, and
     * deconstructing packet into object with placeholders and
     * a list of buffers.
     */
    encodeAsBinary(obj) {
        const deconstruction = deconstructPacket(obj);
        const pack = this.encodeAsString(deconstruction.packet);
        const buffers = deconstruction.buffers;
        buffers.unshift(pack); // add packet info to beginning of data list
        return buffers; // write all the buffers
    }
}
// see https://stackoverflow.com/questions/8511281/check-if-a-value-is-an-object-in-javascript
function isObject(value) {
    return Object.prototype.toString.call(value) === "[object Object]";
}
/**
 * A socket.io Decoder instance
 *
 * @return {Object} decoder
 */
class Decoder extends Emitter {
    /**
     * Decoder constructor
     *
     * @param {function} reviver - custom reviver to pass down to JSON.stringify
     */
    constructor(reviver) {
        super();
        this.reviver = reviver;
    }
    /**
     * Decodes an encoded packet string into packet JSON.
     *
     * @param {String} obj - encoded packet
     */
    add(obj) {
        let packet;
        if (typeof obj === "string") {
            if (this.reconstructor) {
                throw new Error("got plaintext data when reconstructing a packet");
            }
            packet = this.decodeString(obj);
            const isBinaryEvent = packet.type === PacketType.BINARY_EVENT;
            if (isBinaryEvent || packet.type === PacketType.BINARY_ACK) {
                packet.type = isBinaryEvent ? PacketType.EVENT : PacketType.ACK;
                // binary packet's json
                this.reconstructor = new BinaryReconstructor(packet);
                // no attachments, labeled binary but no binary data to follow
                if (packet.attachments === 0) {
                    super.emitReserved("decoded", packet);
                }
            }
            else {
                // non-binary full packet
                super.emitReserved("decoded", packet);
            }
        }
        else if (isBinary(obj) || obj.base64) {
            // raw binary data
            if (!this.reconstructor) {
                throw new Error("got binary data when not reconstructing a packet");
            }
            else {
                packet = this.reconstructor.takeBinaryData(obj);
                if (packet) {
                    // received final buffer
                    this.reconstructor = null;
                    super.emitReserved("decoded", packet);
                }
            }
        }
        else {
            throw new Error("Unknown type: " + obj);
        }
    }
    /**
     * Decode a packet String (JSON data)
     *
     * @param {String} str
     * @return {Object} packet
     */
    decodeString(str) {
        let i = 0;
        // look up type
        const p = {
            type: Number(str.charAt(0)),
        };
        if (PacketType[p.type] === undefined) {
            throw new Error("unknown packet type " + p.type);
        }
        // look up attachments if type binary
        if (p.type === PacketType.BINARY_EVENT ||
            p.type === PacketType.BINARY_ACK) {
            const start = i + 1;
            while (str.charAt(++i) !== "-" && i != str.length) { }
            const buf = str.substring(start, i);
            if (buf != Number(buf) || str.charAt(i) !== "-") {
                throw new Error("Illegal attachments");
            }
            p.attachments = Number(buf);
        }
        // look up namespace (if any)
        if ("/" === str.charAt(i + 1)) {
            const start = i + 1;
            while (++i) {
                const c = str.charAt(i);
                if ("," === c)
                    break;
                if (i === str.length)
                    break;
            }
            p.nsp = str.substring(start, i);
        }
        else {
            p.nsp = "/";
        }
        // look up id
        const next = str.charAt(i + 1);
        if ("" !== next && Number(next) == next) {
            const start = i + 1;
            while (++i) {
                const c = str.charAt(i);
                if (null == c || Number(c) != c) {
                    --i;
                    break;
                }
                if (i === str.length)
                    break;
            }
            p.id = Number(str.substring(start, i + 1));
        }
        // look up json data
        if (str.charAt(++i)) {
            const payload = this.tryParse(str.substr(i));
            if (Decoder.isPayloadValid(p.type, payload)) {
                p.data = payload;
            }
            else {
                throw new Error("invalid payload");
            }
        }
        return p;
    }
    tryParse(str) {
        try {
            return JSON.parse(str, this.reviver);
        }
        catch (e) {
            return false;
        }
    }
    static isPayloadValid(type, payload) {
        switch (type) {
            case PacketType.CONNECT:
                return isObject(payload);
            case PacketType.DISCONNECT:
                return payload === undefined;
            case PacketType.CONNECT_ERROR:
                return typeof payload === "string" || isObject(payload);
            case PacketType.EVENT:
            case PacketType.BINARY_EVENT:
                return (Array.isArray(payload) &&
                    (typeof payload[0] === "number" ||
                        (typeof payload[0] === "string" &&
                            RESERVED_EVENTS$1.indexOf(payload[0]) === -1)));
            case PacketType.ACK:
            case PacketType.BINARY_ACK:
                return Array.isArray(payload);
        }
    }
    /**
     * Deallocates a parser's resources
     */
    destroy() {
        if (this.reconstructor) {
            this.reconstructor.finishedReconstruction();
            this.reconstructor = null;
        }
    }
}
/**
 * A manager of a binary event's 'buffer sequence'. Should
 * be constructed whenever a packet of type BINARY_EVENT is
 * decoded.
 *
 * @param {Object} packet
 * @return {BinaryReconstructor} initialized reconstructor
 */
class BinaryReconstructor {
    constructor(packet) {
        this.packet = packet;
        this.buffers = [];
        this.reconPack = packet;
    }
    /**
     * Method to be called when binary data received from connection
     * after a BINARY_EVENT packet.
     *
     * @param {Buffer | ArrayBuffer} binData - the raw binary data received
     * @return {null | Object} returns null if more binary data is expected or
     *   a reconstructed packet object if all buffers have been received.
     */
    takeBinaryData(binData) {
        this.buffers.push(binData);
        if (this.buffers.length === this.reconPack.attachments) {
            // done with buffer list
            const packet = reconstructPacket(this.reconPack, this.buffers);
            this.finishedReconstruction();
            return packet;
        }
        return null;
    }
    /**
     * Cleans up binary packet reconstruction variables.
     */
    finishedReconstruction() {
        this.reconPack = null;
        this.buffers = [];
    }
}

const parser = /*#__PURE__*/Object.freeze(/*#__PURE__*/Object.defineProperty({
  __proto__: null,
  Decoder,
  Encoder,
  get PacketType () { return PacketType; }
}, Symbol.toStringTag, { value: 'Module' }));

function on(obj, ev, fn) {
    obj.on(ev, fn);
    return function subDestroy() {
        obj.off(ev, fn);
    };
}

/**
 * Internal events.
 * These events can't be emitted by the user.
 */
const RESERVED_EVENTS = Object.freeze({
    connect: 1,
    connect_error: 1,
    disconnect: 1,
    disconnecting: 1,
    // EventEmitter reserved events: https://nodejs.org/api/events.html#events_event_newlistener
    newListener: 1,
    removeListener: 1,
});
/**
 * A Socket is the fundamental class for interacting with the server.
 *
 * A Socket belongs to a certain Namespace (by default /) and uses an underlying {@link Manager} to communicate.
 *
 * @example
 * const socket = io();
 *
 * socket.on("connect", () => {
 *   console.log("connected");
 * });
 *
 * // send an event to the server
 * socket.emit("foo", "bar");
 *
 * socket.on("foobar", () => {
 *   // an event was received from the server
 * });
 *
 * // upon disconnection
 * socket.on("disconnect", (reason) => {
 *   console.log(`disconnected due to ${reason}`);
 * });
 */
class Socket extends Emitter {
    /**
     * `Socket` constructor.
     */
    constructor(io, nsp, opts) {
        super();
        /**
         * Whether the socket is currently connected to the server.
         *
         * @example
         * const socket = io();
         *
         * socket.on("connect", () => {
         *   console.log(socket.connected); // true
         * });
         *
         * socket.on("disconnect", () => {
         *   console.log(socket.connected); // false
         * });
         */
        this.connected = false;
        /**
         * Whether the connection state was recovered after a temporary disconnection. In that case, any missed packets will
         * be transmitted by the server.
         */
        this.recovered = false;
        /**
         * Buffer for packets received before the CONNECT packet
         */
        this.receiveBuffer = [];
        /**
         * Buffer for packets that will be sent once the socket is connected
         */
        this.sendBuffer = [];
        /**
         * The queue of packets to be sent with retry in case of failure.
         *
         * Packets are sent one by one, each waiting for the server acknowledgement, in order to guarantee the delivery order.
         * @private
         */
        this._queue = [];
        /**
         * A sequence to generate the ID of the {@link QueuedPacket}.
         * @private
         */
        this._queueSeq = 0;
        this.ids = 0;
        /**
         * A map containing acknowledgement handlers.
         *
         * The `withError` attribute is used to differentiate handlers that accept an error as first argument:
         *
         * - `socket.emit("test", (err, value) => { ... })` with `ackTimeout` option
         * - `socket.timeout(5000).emit("test", (err, value) => { ... })`
         * - `const value = await socket.emitWithAck("test")`
         *
         * From those that don't:
         *
         * - `socket.emit("test", (value) => { ... });`
         *
         * In the first case, the handlers will be called with an error when:
         *
         * - the timeout is reached
         * - the socket gets disconnected
         *
         * In the second case, the handlers will be simply discarded upon disconnection, since the client will never receive
         * an acknowledgement from the server.
         *
         * @private
         */
        this.acks = {};
        this.flags = {};
        this.io = io;
        this.nsp = nsp;
        if (opts && opts.auth) {
            this.auth = opts.auth;
        }
        this._opts = Object.assign({}, opts);
        if (this.io._autoConnect)
            this.open();
    }
    /**
     * Whether the socket is currently disconnected
     *
     * @example
     * const socket = io();
     *
     * socket.on("connect", () => {
     *   console.log(socket.disconnected); // false
     * });
     *
     * socket.on("disconnect", () => {
     *   console.log(socket.disconnected); // true
     * });
     */
    get disconnected() {
        return !this.connected;
    }
    /**
     * Subscribe to open, close and packet events
     *
     * @private
     */
    subEvents() {
        if (this.subs)
            return;
        const io = this.io;
        this.subs = [
            on(io, "open", this.onopen.bind(this)),
            on(io, "packet", this.onpacket.bind(this)),
            on(io, "error", this.onerror.bind(this)),
            on(io, "close", this.onclose.bind(this)),
        ];
    }
    /**
     * Whether the Socket will try to reconnect when its Manager connects or reconnects.
     *
     * @example
     * const socket = io();
     *
     * console.log(socket.active); // true
     *
     * socket.on("disconnect", (reason) => {
     *   if (reason === "io server disconnect") {
     *     // the disconnection was initiated by the server, you need to manually reconnect
     *     console.log(socket.active); // false
     *   }
     *   // else the socket will automatically try to reconnect
     *   console.log(socket.active); // true
     * });
     */
    get active() {
        return !!this.subs;
    }
    /**
     * "Opens" the socket.
     *
     * @example
     * const socket = io({
     *   autoConnect: false
     * });
     *
     * socket.connect();
     */
    connect() {
        if (this.connected)
            return this;
        this.subEvents();
        if (!this.io["_reconnecting"])
            this.io.open(); // ensure open
        if ("open" === this.io._readyState)
            this.onopen();
        return this;
    }
    /**
     * Alias for {@link connect()}.
     */
    open() {
        return this.connect();
    }
    /**
     * Sends a `message` event.
     *
     * This method mimics the WebSocket.send() method.
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/WebSocket/send
     *
     * @example
     * socket.send("hello");
     *
     * // this is equivalent to
     * socket.emit("message", "hello");
     *
     * @return self
     */
    send(...args) {
        args.unshift("message");
        this.emit.apply(this, args);
        return this;
    }
    /**
     * Override `emit`.
     * If the event is in `events`, it's emitted normally.
     *
     * @example
     * socket.emit("hello", "world");
     *
     * // all serializable datastructures are supported (no need to call JSON.stringify)
     * socket.emit("hello", 1, "2", { 3: ["4"], 5: Uint8Array.from([6]) });
     *
     * // with an acknowledgement from the server
     * socket.emit("hello", "world", (val) => {
     *   // ...
     * });
     *
     * @return self
     */
    emit(ev, ...args) {
        var _a, _b, _c;
        if (RESERVED_EVENTS.hasOwnProperty(ev)) {
            throw new Error('"' + ev.toString() + '" is a reserved event name');
        }
        args.unshift(ev);
        if (this._opts.retries && !this.flags.fromQueue && !this.flags.volatile) {
            this._addToQueue(args);
            return this;
        }
        const packet = {
            type: PacketType.EVENT,
            data: args,
        };
        packet.options = {};
        packet.options.compress = this.flags.compress !== false;
        // event ack callback
        if ("function" === typeof args[args.length - 1]) {
            const id = this.ids++;
            const ack = args.pop();
            this._registerAckCallback(id, ack);
            packet.id = id;
        }
        const isTransportWritable = (_b = (_a = this.io.engine) === null || _a === void 0 ? void 0 : _a.transport) === null || _b === void 0 ? void 0 : _b.writable;
        const isConnected = this.connected && !((_c = this.io.engine) === null || _c === void 0 ? void 0 : _c._hasPingExpired());
        const discardPacket = this.flags.volatile && !isTransportWritable;
        if (discardPacket) ;
        else if (isConnected) {
            this.notifyOutgoingListeners(packet);
            this.packet(packet);
        }
        else {
            this.sendBuffer.push(packet);
        }
        this.flags = {};
        return this;
    }
    /**
     * @private
     */
    _registerAckCallback(id, ack) {
        var _a;
        const timeout = (_a = this.flags.timeout) !== null && _a !== void 0 ? _a : this._opts.ackTimeout;
        if (timeout === undefined) {
            this.acks[id] = ack;
            return;
        }
        // @ts-ignore
        const timer = this.io.setTimeoutFn(() => {
            delete this.acks[id];
            for (let i = 0; i < this.sendBuffer.length; i++) {
                if (this.sendBuffer[i].id === id) {
                    this.sendBuffer.splice(i, 1);
                }
            }
            ack.call(this, new Error("operation has timed out"));
        }, timeout);
        const fn = (...args) => {
            // @ts-ignore
            this.io.clearTimeoutFn(timer);
            ack.apply(this, args);
        };
        fn.withError = true;
        this.acks[id] = fn;
    }
    /**
     * Emits an event and waits for an acknowledgement
     *
     * @example
     * // without timeout
     * const response = await socket.emitWithAck("hello", "world");
     *
     * // with a specific timeout
     * try {
     *   const response = await socket.timeout(1000).emitWithAck("hello", "world");
     * } catch (err) {
     *   // the server did not acknowledge the event in the given delay
     * }
     *
     * @return a Promise that will be fulfilled when the server acknowledges the event
     */
    emitWithAck(ev, ...args) {
        return new Promise((resolve, reject) => {
            const fn = (arg1, arg2) => {
                return arg1 ? reject(arg1) : resolve(arg2);
            };
            fn.withError = true;
            args.push(fn);
            this.emit(ev, ...args);
        });
    }
    /**
     * Add the packet to the queue.
     * @param args
     * @private
     */
    _addToQueue(args) {
        let ack;
        if (typeof args[args.length - 1] === "function") {
            ack = args.pop();
        }
        const packet = {
            id: this._queueSeq++,
            tryCount: 0,
            pending: false,
            args,
            flags: Object.assign({ fromQueue: true }, this.flags),
        };
        args.push((err, ...responseArgs) => {
            if (packet !== this._queue[0]) {
                // the packet has already been acknowledged
                return;
            }
            const hasError = err !== null;
            if (hasError) {
                if (packet.tryCount > this._opts.retries) {
                    this._queue.shift();
                    if (ack) {
                        ack(err);
                    }
                }
            }
            else {
                this._queue.shift();
                if (ack) {
                    ack(null, ...responseArgs);
                }
            }
            packet.pending = false;
            return this._drainQueue();
        });
        this._queue.push(packet);
        this._drainQueue();
    }
    /**
     * Send the first packet of the queue, and wait for an acknowledgement from the server.
     * @param force - whether to resend a packet that has not been acknowledged yet
     *
     * @private
     */
    _drainQueue(force = false) {
        if (!this.connected || this._queue.length === 0) {
            return;
        }
        const packet = this._queue[0];
        if (packet.pending && !force) {
            return;
        }
        packet.pending = true;
        packet.tryCount++;
        this.flags = packet.flags;
        this.emit.apply(this, packet.args);
    }
    /**
     * Sends a packet.
     *
     * @param packet
     * @private
     */
    packet(packet) {
        packet.nsp = this.nsp;
        this.io._packet(packet);
    }
    /**
     * Called upon engine `open`.
     *
     * @private
     */
    onopen() {
        if (typeof this.auth == "function") {
            this.auth((data) => {
                this._sendConnectPacket(data);
            });
        }
        else {
            this._sendConnectPacket(this.auth);
        }
    }
    /**
     * Sends a CONNECT packet to initiate the Socket.IO session.
     *
     * @param data
     * @private
     */
    _sendConnectPacket(data) {
        this.packet({
            type: PacketType.CONNECT,
            data: this._pid
                ? Object.assign({ pid: this._pid, offset: this._lastOffset }, data)
                : data,
        });
    }
    /**
     * Called upon engine or manager `error`.
     *
     * @param err
     * @private
     */
    onerror(err) {
        if (!this.connected) {
            this.emitReserved("connect_error", err);
        }
    }
    /**
     * Called upon engine `close`.
     *
     * @param reason
     * @param description
     * @private
     */
    onclose(reason, description) {
        this.connected = false;
        delete this.id;
        this.emitReserved("disconnect", reason, description);
        this._clearAcks();
    }
    /**
     * Clears the acknowledgement handlers upon disconnection, since the client will never receive an acknowledgement from
     * the server.
     *
     * @private
     */
    _clearAcks() {
        Object.keys(this.acks).forEach((id) => {
            const isBuffered = this.sendBuffer.some((packet) => String(packet.id) === id);
            if (!isBuffered) {
                // note: handlers that do not accept an error as first argument are ignored here
                const ack = this.acks[id];
                delete this.acks[id];
                if (ack.withError) {
                    ack.call(this, new Error("socket has been disconnected"));
                }
            }
        });
    }
    /**
     * Called with socket packet.
     *
     * @param packet
     * @private
     */
    onpacket(packet) {
        const sameNamespace = packet.nsp === this.nsp;
        if (!sameNamespace)
            return;
        switch (packet.type) {
            case PacketType.CONNECT:
                if (packet.data && packet.data.sid) {
                    this.onconnect(packet.data.sid, packet.data.pid);
                }
                else {
                    this.emitReserved("connect_error", new Error("It seems you are trying to reach a Socket.IO server in v2.x with a v3.x client, but they are not compatible (more information here: https://socket.io/docs/v3/migrating-from-2-x-to-3-0/)"));
                }
                break;
            case PacketType.EVENT:
            case PacketType.BINARY_EVENT:
                this.onevent(packet);
                break;
            case PacketType.ACK:
            case PacketType.BINARY_ACK:
                this.onack(packet);
                break;
            case PacketType.DISCONNECT:
                this.ondisconnect();
                break;
            case PacketType.CONNECT_ERROR:
                this.destroy();
                const err = new Error(packet.data.message);
                // @ts-ignore
                err.data = packet.data.data;
                this.emitReserved("connect_error", err);
                break;
        }
    }
    /**
     * Called upon a server event.
     *
     * @param packet
     * @private
     */
    onevent(packet) {
        const args = packet.data || [];
        if (null != packet.id) {
            args.push(this.ack(packet.id));
        }
        if (this.connected) {
            this.emitEvent(args);
        }
        else {
            this.receiveBuffer.push(Object.freeze(args));
        }
    }
    emitEvent(args) {
        if (this._anyListeners && this._anyListeners.length) {
            const listeners = this._anyListeners.slice();
            for (const listener of listeners) {
                listener.apply(this, args);
            }
        }
        super.emit.apply(this, args);
        if (this._pid && args.length && typeof args[args.length - 1] === "string") {
            this._lastOffset = args[args.length - 1];
        }
    }
    /**
     * Produces an ack callback to emit with an event.
     *
     * @private
     */
    ack(id) {
        const self = this;
        let sent = false;
        return function (...args) {
            // prevent double callbacks
            if (sent)
                return;
            sent = true;
            self.packet({
                type: PacketType.ACK,
                id: id,
                data: args,
            });
        };
    }
    /**
     * Called upon a server acknowledgement.
     *
     * @param packet
     * @private
     */
    onack(packet) {
        const ack = this.acks[packet.id];
        if (typeof ack !== "function") {
            return;
        }
        delete this.acks[packet.id];
        // @ts-ignore FIXME ack is incorrectly inferred as 'never'
        if (ack.withError) {
            packet.data.unshift(null);
        }
        // @ts-ignore
        ack.apply(this, packet.data);
    }
    /**
     * Called upon server connect.
     *
     * @private
     */
    onconnect(id, pid) {
        this.id = id;
        this.recovered = pid && this._pid === pid;
        this._pid = pid; // defined only if connection state recovery is enabled
        this.connected = true;
        this.emitBuffered();
        this.emitReserved("connect");
        this._drainQueue(true);
    }
    /**
     * Emit buffered events (received and emitted).
     *
     * @private
     */
    emitBuffered() {
        this.receiveBuffer.forEach((args) => this.emitEvent(args));
        this.receiveBuffer = [];
        this.sendBuffer.forEach((packet) => {
            this.notifyOutgoingListeners(packet);
            this.packet(packet);
        });
        this.sendBuffer = [];
    }
    /**
     * Called upon server disconnect.
     *
     * @private
     */
    ondisconnect() {
        this.destroy();
        this.onclose("io server disconnect");
    }
    /**
     * Called upon forced client/server side disconnections,
     * this method ensures the manager stops tracking us and
     * that reconnections don't get triggered for this.
     *
     * @private
     */
    destroy() {
        if (this.subs) {
            // clean subscriptions to avoid reconnections
            this.subs.forEach((subDestroy) => subDestroy());
            this.subs = undefined;
        }
        this.io["_destroy"](this);
    }
    /**
     * Disconnects the socket manually. In that case, the socket will not try to reconnect.
     *
     * If this is the last active Socket instance of the {@link Manager}, the low-level connection will be closed.
     *
     * @example
     * const socket = io();
     *
     * socket.on("disconnect", (reason) => {
     *   // console.log(reason); prints "io client disconnect"
     * });
     *
     * socket.disconnect();
     *
     * @return self
     */
    disconnect() {
        if (this.connected) {
            this.packet({ type: PacketType.DISCONNECT });
        }
        // remove socket from pool
        this.destroy();
        if (this.connected) {
            // fire events
            this.onclose("io client disconnect");
        }
        return this;
    }
    /**
     * Alias for {@link disconnect()}.
     *
     * @return self
     */
    close() {
        return this.disconnect();
    }
    /**
     * Sets the compress flag.
     *
     * @example
     * socket.compress(false).emit("hello");
     *
     * @param compress - if `true`, compresses the sending data
     * @return self
     */
    compress(compress) {
        this.flags.compress = compress;
        return this;
    }
    /**
     * Sets a modifier for a subsequent event emission that the event message will be dropped when this socket is not
     * ready to send messages.
     *
     * @example
     * socket.volatile.emit("hello"); // the server may or may not receive it
     *
     * @returns self
     */
    get volatile() {
        this.flags.volatile = true;
        return this;
    }
    /**
     * Sets a modifier for a subsequent event emission that the callback will be called with an error when the
     * given number of milliseconds have elapsed without an acknowledgement from the server:
     *
     * @example
     * socket.timeout(5000).emit("my-event", (err) => {
     *   if (err) {
     *     // the server did not acknowledge the event in the given delay
     *   }
     * });
     *
     * @returns self
     */
    timeout(timeout) {
        this.flags.timeout = timeout;
        return this;
    }
    /**
     * Adds a listener that will be fired when any event is emitted. The event name is passed as the first argument to the
     * callback.
     *
     * @example
     * socket.onAny((event, ...args) => {
     *   console.log(`got ${event}`);
     * });
     *
     * @param listener
     */
    onAny(listener) {
        this._anyListeners = this._anyListeners || [];
        this._anyListeners.push(listener);
        return this;
    }
    /**
     * Adds a listener that will be fired when any event is emitted. The event name is passed as the first argument to the
     * callback. The listener is added to the beginning of the listeners array.
     *
     * @example
     * socket.prependAny((event, ...args) => {
     *   console.log(`got event ${event}`);
     * });
     *
     * @param listener
     */
    prependAny(listener) {
        this._anyListeners = this._anyListeners || [];
        this._anyListeners.unshift(listener);
        return this;
    }
    /**
     * Removes the listener that will be fired when any event is emitted.
     *
     * @example
     * const catchAllListener = (event, ...args) => {
     *   console.log(`got event ${event}`);
     * }
     *
     * socket.onAny(catchAllListener);
     *
     * // remove a specific listener
     * socket.offAny(catchAllListener);
     *
     * // or remove all listeners
     * socket.offAny();
     *
     * @param listener
     */
    offAny(listener) {
        if (!this._anyListeners) {
            return this;
        }
        if (listener) {
            const listeners = this._anyListeners;
            for (let i = 0; i < listeners.length; i++) {
                if (listener === listeners[i]) {
                    listeners.splice(i, 1);
                    return this;
                }
            }
        }
        else {
            this._anyListeners = [];
        }
        return this;
    }
    /**
     * Returns an array of listeners that are listening for any event that is specified. This array can be manipulated,
     * e.g. to remove listeners.
     */
    listenersAny() {
        return this._anyListeners || [];
    }
    /**
     * Adds a listener that will be fired when any event is emitted. The event name is passed as the first argument to the
     * callback.
     *
     * Note: acknowledgements sent to the server are not included.
     *
     * @example
     * socket.onAnyOutgoing((event, ...args) => {
     *   console.log(`sent event ${event}`);
     * });
     *
     * @param listener
     */
    onAnyOutgoing(listener) {
        this._anyOutgoingListeners = this._anyOutgoingListeners || [];
        this._anyOutgoingListeners.push(listener);
        return this;
    }
    /**
     * Adds a listener that will be fired when any event is emitted. The event name is passed as the first argument to the
     * callback. The listener is added to the beginning of the listeners array.
     *
     * Note: acknowledgements sent to the server are not included.
     *
     * @example
     * socket.prependAnyOutgoing((event, ...args) => {
     *   console.log(`sent event ${event}`);
     * });
     *
     * @param listener
     */
    prependAnyOutgoing(listener) {
        this._anyOutgoingListeners = this._anyOutgoingListeners || [];
        this._anyOutgoingListeners.unshift(listener);
        return this;
    }
    /**
     * Removes the listener that will be fired when any event is emitted.
     *
     * @example
     * const catchAllListener = (event, ...args) => {
     *   console.log(`sent event ${event}`);
     * }
     *
     * socket.onAnyOutgoing(catchAllListener);
     *
     * // remove a specific listener
     * socket.offAnyOutgoing(catchAllListener);
     *
     * // or remove all listeners
     * socket.offAnyOutgoing();
     *
     * @param [listener] - the catch-all listener (optional)
     */
    offAnyOutgoing(listener) {
        if (!this._anyOutgoingListeners) {
            return this;
        }
        if (listener) {
            const listeners = this._anyOutgoingListeners;
            for (let i = 0; i < listeners.length; i++) {
                if (listener === listeners[i]) {
                    listeners.splice(i, 1);
                    return this;
                }
            }
        }
        else {
            this._anyOutgoingListeners = [];
        }
        return this;
    }
    /**
     * Returns an array of listeners that are listening for any event that is specified. This array can be manipulated,
     * e.g. to remove listeners.
     */
    listenersAnyOutgoing() {
        return this._anyOutgoingListeners || [];
    }
    /**
     * Notify the listeners for each packet sent
     *
     * @param packet
     *
     * @private
     */
    notifyOutgoingListeners(packet) {
        if (this._anyOutgoingListeners && this._anyOutgoingListeners.length) {
            const listeners = this._anyOutgoingListeners.slice();
            for (const listener of listeners) {
                listener.apply(this, packet.data);
            }
        }
    }
}

/**
 * Initialize backoff timer with `opts`.
 *
 * - `min` initial timeout in milliseconds [100]
 * - `max` max timeout [10000]
 * - `jitter` [0]
 * - `factor` [2]
 *
 * @param {Object} opts
 * @api public
 */
function Backoff(opts) {
    opts = opts || {};
    this.ms = opts.min || 100;
    this.max = opts.max || 10000;
    this.factor = opts.factor || 2;
    this.jitter = opts.jitter > 0 && opts.jitter <= 1 ? opts.jitter : 0;
    this.attempts = 0;
}
/**
 * Return the backoff duration.
 *
 * @return {Number}
 * @api public
 */
Backoff.prototype.duration = function () {
    var ms = this.ms * Math.pow(this.factor, this.attempts++);
    if (this.jitter) {
        var rand = Math.random();
        var deviation = Math.floor(rand * this.jitter * ms);
        ms = (Math.floor(rand * 10) & 1) == 0 ? ms - deviation : ms + deviation;
    }
    return Math.min(ms, this.max) | 0;
};
/**
 * Reset the number of attempts.
 *
 * @api public
 */
Backoff.prototype.reset = function () {
    this.attempts = 0;
};
/**
 * Set the minimum duration
 *
 * @api public
 */
Backoff.prototype.setMin = function (min) {
    this.ms = min;
};
/**
 * Set the maximum duration
 *
 * @api public
 */
Backoff.prototype.setMax = function (max) {
    this.max = max;
};
/**
 * Set the jitter
 *
 * @api public
 */
Backoff.prototype.setJitter = function (jitter) {
    this.jitter = jitter;
};

class Manager extends Emitter {
    constructor(uri, opts) {
        var _a;
        super();
        this.nsps = {};
        this.subs = [];
        if (uri && "object" === typeof uri) {
            opts = uri;
            uri = undefined;
        }
        opts = opts || {};
        opts.path = opts.path || "/socket.io";
        this.opts = opts;
        installTimerFunctions(this, opts);
        this.reconnection(opts.reconnection !== false);
        this.reconnectionAttempts(opts.reconnectionAttempts || Infinity);
        this.reconnectionDelay(opts.reconnectionDelay || 1000);
        this.reconnectionDelayMax(opts.reconnectionDelayMax || 5000);
        this.randomizationFactor((_a = opts.randomizationFactor) !== null && _a !== void 0 ? _a : 0.5);
        this.backoff = new Backoff({
            min: this.reconnectionDelay(),
            max: this.reconnectionDelayMax(),
            jitter: this.randomizationFactor(),
        });
        this.timeout(null == opts.timeout ? 20000 : opts.timeout);
        this._readyState = "closed";
        this.uri = uri;
        const _parser = opts.parser || parser;
        this.encoder = new _parser.Encoder();
        this.decoder = new _parser.Decoder();
        this._autoConnect = opts.autoConnect !== false;
        if (this._autoConnect)
            this.open();
    }
    reconnection(v) {
        if (!arguments.length)
            return this._reconnection;
        this._reconnection = !!v;
        if (!v) {
            this.skipReconnect = true;
        }
        return this;
    }
    reconnectionAttempts(v) {
        if (v === undefined)
            return this._reconnectionAttempts;
        this._reconnectionAttempts = v;
        return this;
    }
    reconnectionDelay(v) {
        var _a;
        if (v === undefined)
            return this._reconnectionDelay;
        this._reconnectionDelay = v;
        (_a = this.backoff) === null || _a === void 0 ? void 0 : _a.setMin(v);
        return this;
    }
    randomizationFactor(v) {
        var _a;
        if (v === undefined)
            return this._randomizationFactor;
        this._randomizationFactor = v;
        (_a = this.backoff) === null || _a === void 0 ? void 0 : _a.setJitter(v);
        return this;
    }
    reconnectionDelayMax(v) {
        var _a;
        if (v === undefined)
            return this._reconnectionDelayMax;
        this._reconnectionDelayMax = v;
        (_a = this.backoff) === null || _a === void 0 ? void 0 : _a.setMax(v);
        return this;
    }
    timeout(v) {
        if (!arguments.length)
            return this._timeout;
        this._timeout = v;
        return this;
    }
    /**
     * Starts trying to reconnect if reconnection is enabled and we have not
     * started reconnecting yet
     *
     * @private
     */
    maybeReconnectOnOpen() {
        // Only try to reconnect if it's the first time we're connecting
        if (!this._reconnecting &&
            this._reconnection &&
            this.backoff.attempts === 0) {
            // keeps reconnection from firing twice for the same reconnection loop
            this.reconnect();
        }
    }
    /**
     * Sets the current transport `socket`.
     *
     * @param {Function} fn - optional, callback
     * @return self
     * @public
     */
    open(fn) {
        if (~this._readyState.indexOf("open"))
            return this;
        this.engine = new Socket$1(this.uri, this.opts);
        const socket = this.engine;
        const self = this;
        this._readyState = "opening";
        this.skipReconnect = false;
        // emit `open`
        const openSubDestroy = on(socket, "open", function () {
            self.onopen();
            fn && fn();
        });
        const onError = (err) => {
            this.cleanup();
            this._readyState = "closed";
            this.emitReserved("error", err);
            if (fn) {
                fn(err);
            }
            else {
                // Only do this if there is no fn to handle the error
                this.maybeReconnectOnOpen();
            }
        };
        // emit `error`
        const errorSub = on(socket, "error", onError);
        if (false !== this._timeout) {
            const timeout = this._timeout;
            // set timer
            const timer = this.setTimeoutFn(() => {
                openSubDestroy();
                onError(new Error("timeout"));
                socket.close();
            }, timeout);
            if (this.opts.autoUnref) {
                timer.unref();
            }
            this.subs.push(() => {
                this.clearTimeoutFn(timer);
            });
        }
        this.subs.push(openSubDestroy);
        this.subs.push(errorSub);
        return this;
    }
    /**
     * Alias for open()
     *
     * @return self
     * @public
     */
    connect(fn) {
        return this.open(fn);
    }
    /**
     * Called upon transport open.
     *
     * @private
     */
    onopen() {
        // clear old subs
        this.cleanup();
        // mark as open
        this._readyState = "open";
        this.emitReserved("open");
        // add new subs
        const socket = this.engine;
        this.subs.push(on(socket, "ping", this.onping.bind(this)), on(socket, "data", this.ondata.bind(this)), on(socket, "error", this.onerror.bind(this)), on(socket, "close", this.onclose.bind(this)), 
        // @ts-ignore
        on(this.decoder, "decoded", this.ondecoded.bind(this)));
    }
    /**
     * Called upon a ping.
     *
     * @private
     */
    onping() {
        this.emitReserved("ping");
    }
    /**
     * Called with data.
     *
     * @private
     */
    ondata(data) {
        try {
            this.decoder.add(data);
        }
        catch (e) {
            this.onclose("parse error", e);
        }
    }
    /**
     * Called when parser fully decodes a packet.
     *
     * @private
     */
    ondecoded(packet) {
        // the nextTick call prevents an exception in a user-provided event listener from triggering a disconnection due to a "parse error"
        nextTick(() => {
            this.emitReserved("packet", packet);
        }, this.setTimeoutFn);
    }
    /**
     * Called upon socket error.
     *
     * @private
     */
    onerror(err) {
        this.emitReserved("error", err);
    }
    /**
     * Creates a new socket for the given `nsp`.
     *
     * @return {Socket}
     * @public
     */
    socket(nsp, opts) {
        let socket = this.nsps[nsp];
        if (!socket) {
            socket = new Socket(this, nsp, opts);
            this.nsps[nsp] = socket;
        }
        else if (this._autoConnect && !socket.active) {
            socket.connect();
        }
        return socket;
    }
    /**
     * Called upon a socket close.
     *
     * @param socket
     * @private
     */
    _destroy(socket) {
        const nsps = Object.keys(this.nsps);
        for (const nsp of nsps) {
            const socket = this.nsps[nsp];
            if (socket.active) {
                return;
            }
        }
        this._close();
    }
    /**
     * Writes a packet.
     *
     * @param packet
     * @private
     */
    _packet(packet) {
        const encodedPackets = this.encoder.encode(packet);
        for (let i = 0; i < encodedPackets.length; i++) {
            this.engine.write(encodedPackets[i], packet.options);
        }
    }
    /**
     * Clean up transport subscriptions and packet buffer.
     *
     * @private
     */
    cleanup() {
        this.subs.forEach((subDestroy) => subDestroy());
        this.subs.length = 0;
        this.decoder.destroy();
    }
    /**
     * Close the current socket.
     *
     * @private
     */
    _close() {
        this.skipReconnect = true;
        this._reconnecting = false;
        this.onclose("forced close");
    }
    /**
     * Alias for close()
     *
     * @private
     */
    disconnect() {
        return this._close();
    }
    /**
     * Called when:
     *
     * - the low-level engine is closed
     * - the parser encountered a badly formatted packet
     * - all sockets are disconnected
     *
     * @private
     */
    onclose(reason, description) {
        var _a;
        this.cleanup();
        (_a = this.engine) === null || _a === void 0 ? void 0 : _a.close();
        this.backoff.reset();
        this._readyState = "closed";
        this.emitReserved("close", reason, description);
        if (this._reconnection && !this.skipReconnect) {
            this.reconnect();
        }
    }
    /**
     * Attempt a reconnection.
     *
     * @private
     */
    reconnect() {
        if (this._reconnecting || this.skipReconnect)
            return this;
        const self = this;
        if (this.backoff.attempts >= this._reconnectionAttempts) {
            this.backoff.reset();
            this.emitReserved("reconnect_failed");
            this._reconnecting = false;
        }
        else {
            const delay = this.backoff.duration();
            this._reconnecting = true;
            const timer = this.setTimeoutFn(() => {
                if (self.skipReconnect)
                    return;
                this.emitReserved("reconnect_attempt", self.backoff.attempts);
                // check again for the case socket closed in above events
                if (self.skipReconnect)
                    return;
                self.open((err) => {
                    if (err) {
                        self._reconnecting = false;
                        self.reconnect();
                        this.emitReserved("reconnect_error", err);
                    }
                    else {
                        self.onreconnect();
                    }
                });
            }, delay);
            if (this.opts.autoUnref) {
                timer.unref();
            }
            this.subs.push(() => {
                this.clearTimeoutFn(timer);
            });
        }
    }
    /**
     * Called upon successful reconnect.
     *
     * @private
     */
    onreconnect() {
        const attempt = this.backoff.attempts;
        this._reconnecting = false;
        this.backoff.reset();
        this.emitReserved("reconnect", attempt);
    }
}

/**
 * Managers cache.
 */
const cache = {};
function lookup(uri, opts) {
    if (typeof uri === "object") {
        opts = uri;
        uri = undefined;
    }
    opts = opts || {};
    const parsed = url(uri, opts.path || "/socket.io");
    const source = parsed.source;
    const id = parsed.id;
    const path = parsed.path;
    const sameNamespace = cache[id] && path in cache[id]["nsps"];
    const newConnection = opts.forceNew ||
        opts["force new connection"] ||
        false === opts.multiplex ||
        sameNamespace;
    let io;
    if (newConnection) {
        io = new Manager(source, opts);
    }
    else {
        if (!cache[id]) {
            cache[id] = new Manager(source, opts);
        }
        io = cache[id];
    }
    if (parsed.query && !opts.query) {
        opts.query = parsed.queryKey;
    }
    return io.socket(parsed.path, opts);
}
// so that "lookup" can be used both as a function (e.g. `io(...)`) and as a
// namespace (e.g. `io.connect(...)`), for backward compatibility
Object.assign(lookup, {
    Manager,
    Socket,
    io: lookup,
    connect: lookup,
});

// Set up global message listener for iframe requests (only once)
if (typeof window !== 'undefined') {
    window.addEventListener('message', (event) => {
        const trustedOrigin = 'https://agent.brainfi.sh';
        if (event.origin !== trustedOrigin) {
            return; // Silently ignore messages from untrusted origins
        }
        // Handle REQUEST_USER_IDENTITY from agent-widget
        if (event.data?.type === 'REQUEST_USER_IDENTITY') ;
    });
}
var NudgeCTABlockActionType;
(function (NudgeCTABlockActionType) {
    NudgeCTABlockActionType["OpenUrl"] = "openUrl";
    NudgeCTABlockActionType["CallPhone"] = "callPhone";
    NudgeCTABlockActionType["SendEmail"] = "sendEmail";
    NudgeCTABlockActionType["TriggerEvent"] = "triggerEvent";
})(NudgeCTABlockActionType || (NudgeCTABlockActionType = {}));
var NudgeCTABlockType;
(function (NudgeCTABlockType) {
    NudgeCTABlockType["Button"] = "button";
    NudgeCTABlockType["Link"] = "link";
})(NudgeCTABlockType || (NudgeCTABlockType = {}));

var _tmpl$ = /* @__PURE__ */ template(`<a target=_blank rel="noopener noreferrer">`), _tmpl$2 = /* @__PURE__ */ template(`<a>`), _tmpl$3 = /* @__PURE__ */ template(`<div>`), _tmpl$4 = /* @__PURE__ */ template(`<div><div><div><h3></h3><div></div></div><button aria-label=Close>`);
const NotificationToast = (props) => {
  props = mergeProps({
    nudgeId: "",
    title: "",
    message: "",
    icon: "",
    iconColor: "",
    attachments: null,
    theme: null,
    position: "",
    onDismiss: () => {
    },
    class: "",
    style: {},
    ctaBlocks: null,
    onCTAClick: () => {
    }
  }, props);
  const [local, rest] = splitProps(props, ["nudgeId", "title", "message", "icon", "iconColor", "attachments", "theme", "position", "onDismiss", "class", "style", "ctaBlocks", "onCTAClick"]);
  const filledStyle = local.theme?.filled;
  let bgColor = local.iconColor;
  if (!filledStyle) {
    bgColor = mix$1(0.9, "#FFFFFF", local.iconColor);
  }
  const fgColor = getReadableColor(bgColor, filledStyle ? "#FFF" : "#171717");
  const sanitizeHTML = (html) => ({
    __html: DOMPurify.sanitize(html)
  });
  const CTAButton = (ctaBlock) => {
    const {
      action,
      title
    } = ctaBlock;
    const handleClick = () => {
      if (local.onCTAClick) {
        local.onCTAClick(ctaBlock);
      }
    };
    const commonProps = {
      class: styles.ctaBlock,
      style: {
        color: fgColor,
        "--hover-bg-color": filledStyle ? fgColor : local.iconColor,
        "--hover-fg-color": bgColor
      },
      onClick: handleClick
    };
    if (action.type === NudgeCTABlockActionType.OpenUrl) {
      return (() => {
        var _el$ = _tmpl$();
        spread(_el$, mergeProps({
          get href() {
            return action.url;
          }
        }, commonProps), false, true);
        insert(_el$, title);
        return _el$;
      })();
    } else if (action.type === NudgeCTABlockActionType.CallPhone) {
      return (() => {
        var _el$2 = _tmpl$2();
        spread(_el$2, mergeProps({
          get href() {
            return `tel:${action.phone}`;
          }
        }, commonProps), false, true);
        insert(_el$2, title);
        return _el$2;
      })();
    } else if (action.type === NudgeCTABlockActionType.SendEmail) {
      return (() => {
        var _el$3 = _tmpl$2();
        spread(_el$3, mergeProps({
          get href() {
            return `mailto:${action.to}`;
          }
        }, commonProps), false, true);
        insert(_el$3, title);
        return _el$3;
      })();
    }
  };
  return (() => {
    var _el$4 = _tmpl$4(), _el$5 = _el$4.firstChild, _el$6 = _el$5.firstChild, _el$7 = _el$6.firstChild, _el$8 = _el$7.nextSibling, _el$0 = _el$6.nextSibling;
    spread(_el$4, mergeProps({
      get ["class"]() {
        return classNames(styles.toast, local.class);
      },
      get style() {
        return {
          background: bgColor,
          color: fgColor,
          border: `1px solid ${local.iconColor}`
        };
      }
    }, rest), false, true);
    insert(_el$5, createComponent(Icon, {
      get classes() {
        return styles.headerIcon;
      },
      get name() {
        return local.icon;
      },
      get fill() {
        return filledStyle ? fgColor : local.iconColor;
      },
      weight: "fill"
    }), _el$6);
    insert(_el$7, () => local.title);
    insert(_el$6, createComponent(Show, {
      get when() {
        return !!local.ctaBlocks?.length;
      },
      get children() {
        var _el$9 = _tmpl$3();
        insert(_el$9, () => createComponent(For, {
          get each() {
            return local.ctaBlocks;
          },
          children: (ctaBlock) => createComponent(CTAButton, ctaBlock)
        }));
        createRenderEffect(() => className(_el$9, styles.ctaBlocks));
        return _el$9;
      }
    }), null);
    addEventListener$1(_el$0, "click", local.onDismiss, true);
    insert(_el$0, createComponent(Icon, {
      name: "X",
      classes: "",
      fill: fgColor
    }));
    createRenderEffect((_p$) => {
      var _v$ = styles.container, _v$2 = styles.content, _v$3 = styles.title, _v$4 = sanitizeHTML(local.message).__html, _v$5 = styles.closeButton;
      _v$ !== _p$.e && className(_el$5, _p$.e = _v$);
      _v$2 !== _p$.t && className(_el$6, _p$.t = _v$2);
      _v$3 !== _p$.a && className(_el$7, _p$.a = _v$3);
      _v$4 !== _p$.o && (_el$8.innerHTML = _p$.o = _v$4);
      _v$5 !== _p$.i && className(_el$0, _p$.i = _v$5);
      return _p$;
    }, {
      e: void 0,
      t: void 0,
      a: void 0,
      o: void 0,
      i: void 0
    });
    return _el$4;
  })();
};
delegateEvents(["click"]);

export { ActionButtons as A, Button as B, Icon as I, NotificationToast as N, AutocompleteSearchInput as a, insert as i };
